import React, { useState, useEffect } from 'react'; // Added useRef and useEffect
import SalesManagementView from './SalesManagementView';
import OrderManagement from '../components/admin/OrderManagement';
import InformationManagement from '../components/admin/InformationManagement';
import { AISHA_API_BASE } from '../constants';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import {
  CSS,
} from '@dnd-kit/utilities';
import {
  AdminGenerationMenuItem,
  SupportedGenerationEngine,
  AdminChargeOptionItem,
  AdminGenerationMenuCategoryItem,
  AdminSection,
} from '../types';
import {
  CogIcon,
  ViewGridIcon,
  CubeIcon,
  UsersIcon,
  PlusCircleIcon,
  PencilAltIcon,
  TrashIcon,
  LinkIcon,
  KeyIcon,
  CreditCardIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  FolderOpenIcon,
  InformationCircleIcon,
  CheckIcon,
  XMarkIcon,
  PhotoIcon,
  CodeBracketIcon,
  EyeIcon,
  EyeSlashIcon,
} from '../components/icons/HeroIcons';
import { ImageUpload } from '@/components/ImageUpload';
import {
  createCategory,
  updateCategory,
  deleteCategory,
  updateCategoryOrder,
} from '../services/api/categories';
import { createMenu, updateMenu, deleteMenu, updateMenuOrder } from '../services/api/menus';
import {
  useChargeOptions,
  useChargeOptionsActions,
} from '@/contexts/ChargeOptionsContext';
import {
  createChargeOption,
  deleteChargeOption,
  updateChargeOption,
} from '@/services/api/charge-options';
import { useToast } from '@/contexts/ToastContext';
import {
  useCategories,
  useCategoriesActions,
} from '@/contexts/CategoriesContext';
import { useMenus, useMenusActions } from '@/contexts/MenusContext';
import { getUserCredits, addCreditsToUser, getAllUsers, UserCreditInfo, UserStatistics, getGenerationHistoryStats, getGenerationHistoryList, GenerationHistoryStats, GenerationHistoryItem } from '@/services/api/admin-credits';
import { 
  getGoodsManagementList, 
  updateGoodsManagement, 
  syncSuzuriItems,
  GoodsManagementItem 
} from '@/services/api/goods-management';
import {
  getPrintfulCatalogProducts,
  syncAllPrintfulProducts,
  deletePrintfulSyncProduct
} from '@/services/api/printful';
import AdminGoodsManagementForm from '@/components/AdminGoodsManagementForm';
import { uploadMenuImage, registerRenoiseAsset, fetchRenoiseAssets, RenoiseAsset } from '@/services/api/menu-image-upload';
import GenerationMenuAssistantModal from '@/components/modals/GenerationMenuAssistantModal';
import PrintfulProductEditModal from '@/components/PrintfulProductEditModal';
import PrintfulPurchaseModal from '@/components/modals/PrintfulPurchaseModal';
import { useMasterReferences } from '@/contexts/MasterReferencesContext';
import {
  getArtisanProducts,
  createArtisanProduct,
  updateArtisanProduct as updateArtisanProductApi,
  deleteArtisanProduct as deleteArtisanProductApi,
  ArtisanProduct,
} from '@/services/api/artisan-product';
import ArtisanProductForm from '@/components/ArtisanProductForm';
import OrderStatusProgress from '@/components/OrderStatusProgress';
import {
  getArtisanOrders,
  updateArtisanOrderStatus,
  ArtisanOrder,
} from '@/services/api/artisan-orders';

// 2960側で停止中のエンジン（2026-10-31版エンジンリファレンス）。新規メニューの選択肢には出さない
const STOPPED_ENGINES: string[] = ['imagen3', 'ideogram', 'midjourney', 'black_forest_labs', 'nanobanana', 'grok_imagen', 'seedream4', 'seedream45', 'seedream5', 'gpt_image'];

const AdminView: React.FC = () => {
  const { showToast } = useToast();
  const categories = useCategories();
  const { refreshCategories } = useCategoriesActions();
  const [editingAdminGenMenuCategory, setEditingAdminGenMenuCategory] =
    useState<AdminGenerationMenuCategoryItem | null>(null);
  const menus = useMenus();
  const { refreshMenus } = useMenusActions();
  const [editingGenMenu, setEditingGenMenu] =
    useState<AdminGenerationMenuItem | null>(null);
  const chargeOptions = useChargeOptions();
  const { refreshChargeOptions } = useChargeOptionsActions();
  const [editingChargeOption, setEditingChargeOption] =
    useState<AdminChargeOptionItem | null>(null);
  const [activeSection, setActiveSection] =
    useState<AdminSection>('genMenuCategories'); // Default to first item in new order
  const [goodsItems, setGoodsItems] = useState<GoodsManagementItem[]>([]);
  const [editingGoodsItem, setEditingGoodsItem] =
    useState<GoodsManagementItem | null>(null);
  const [isLoadingGoods, setIsLoadingGoods] = useState(false);
  const [isSyncingGoods, setIsSyncingGoods] = useState(false);
  const [showPublicOnly, setShowPublicOnly] = useState(false);
  const [showSubMaterialsOnly, setShowSubMaterialsOnly] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<'suzuri' | 'printful' | 'prodigi' | 'artisan'>('suzuri');
  // Artisan関連のstate
  const [artisanProducts, setArtisanProducts] = useState<ArtisanProduct[]>([]);
  const [isLoadingArtisan, setIsLoadingArtisan] = useState(false);
  const [showArtisanForm, setShowArtisanForm] = useState(false);
  const [editingArtisanProduct, setEditingArtisanProduct] = useState<ArtisanProduct | null>(null);
  // Artisan注文管理関連のstate
  const [orderTab, setOrderTab] = useState<'printful' | 'artisan'>('printful');
  const [artisanOrders, setArtisanOrders] = useState<ArtisanOrder[]>([]);
  const [isLoadingArtisanOrders, setIsLoadingArtisanOrders] = useState(false);
  const [artisanOrderStatusFilter, setArtisanOrderStatusFilter] = useState<string>('new');
  const [editingArtisanOrderNotes, setEditingArtisanOrderNotes] = useState<Record<number, string>>({});
  const [editingArtisanDeliveryDate, setEditingArtisanDeliveryDate] = useState<Record<number, string>>({});
  const [showDeliveryDatePicker, setShowDeliveryDatePicker] = useState<Record<number, boolean>>({});
  const [deliveryYear, setDeliveryYear] = useState<Record<number, number>>({});
  const [deliveryMonth, setDeliveryMonth] = useState<Record<number, number>>({});
  const [deliveryDay, setDeliveryDay] = useState<Record<number, number>>({});
  const [editingArtisanCarrier, setEditingArtisanCarrier] = useState<Record<number, string>>({});
  const [editingArtisanTracking, setEditingArtisanTracking] = useState<Record<number, string>>({});
  const [editingArtisanShippedDate, setEditingArtisanShippedDate] = useState<Record<number, string>>({});
  const [showShippingForm, setShowShippingForm] = useState<Record<number, boolean>>({});
  const [shippedYear, setShippedYear] = useState<Record<number, number>>({});
  const [shippedMonth, setShippedMonth] = useState<Record<number, number>>({});
  const [shippedDay, setShippedDay] = useState<Record<number, number>>({});
  const [savingArtisanOrder, setSavingArtisanOrder] = useState<number | null>(null);
  const [confirmBoardOrderId, setConfirmBoardOrderId] = useState<number | null>(null);
  const [reviewDetailOrder, setReviewDetailOrder] = useState<ArtisanOrder | null>(null);
  // Printful関連のstate
  // @ts-ignore: setter is used, variable kept for state
  const [_printfulProducts, setPrintfulProducts] = useState<any[]>([]);
  const [printfulGoods, setPrintfulGoods] = useState<any[]>([]);
  const [isLoadingPrintful, setIsLoadingPrintful] = useState(false);
  const [isSyncingPrintful, setIsSyncingPrintful] = useState(false);
  const [isCheckingJapan, setIsCheckingJapan] = useState(false);
  // @ts-ignore: setter is used, variable kept for state
  const [_editingPrintfulPrice, setEditingPrintfulPrice] = useState<number | null>(null);
  const [newPrintfulPrice, setNewPrintfulPrice] = useState<{base_price?: number, profit_margin?: number}>({});
  const [showJapanShippable, setShowJapanShippable] = useState(true);
  const [showOverseas, setShowOverseas] = useState(false);
  const [showNonJapanShippable, setShowNonJapanShippable] = useState(false);
  const [showDiscontinued, setShowDiscontinued] = useState(false);
  // 画像アップロード関連のstate
  const [uploadingImageForProduct, setUploadingImageForProduct] = useState<number | null>(null);
  const [productImages, setProductImages] = useState<{[key: number]: string}>({});
  // Printful編集モーダル関連のstate
  const [editingPrintfulProduct, setEditingPrintfulProduct] = useState<any>(null);
  const [isPrintfulEditModalOpen, setIsPrintfulEditModalOpen] = useState(false);
  const [generatingMockup, setGeneratingMockup] = useState<number | null>(null);
  const [productMockups, setProductMockups] = useState<{[key: number]: string}>({});
  const [creatingProduct, setCreatingProduct] = useState<number | null>(null);
  const [showVariantModal, setShowVariantModal] = useState<number | null>(null);
  const [productVariants, setProductVariants] = useState<any>(null);
  const [selectedVariant, setSelectedVariant] = useState<number | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [loadingVariants, setLoadingVariants] = useState(false);
  // バリアント選択モーダル用
  const [showVariantSelectModal, setShowVariantSelectModal] = useState(false);
  const [selectedGoodsForMockup, setSelectedGoodsForMockup] = useState<any>(null);
  const [catalogVariants, setCatalogVariants] = useState<any[]>([]);
  const [selectedVariantForMockup, setSelectedVariantForMockup] = useState<any>(null);
  const [loadingCatalogVariants, setLoadingCatalogVariants] = useState(false);
  // モックアップ位置調整用
  const [mockupPositionParams, setMockupPositionParams] = useState({
    width: 1600,
    height: 1600,
    top: 0,
    left: 0
  });
  // プレビュー用の位置パラメータ（実際に適用される値）
  const [previewParams, setPreviewParams] = useState({
    width: 1600,
    height: 1600,
    top: 0,
    left: 0
  });
  // 比率維持チェックボックスの状態
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const imageAspectRatio = 640 / 480; // 4:3 = 1.333...
  // Printfulモックアップ生成関連のステート
  const [creatingMockup, setCreatingMockup] = useState(false);
  const [generatedMockupUrl, setGeneratedMockupUrl] = useState<string | null>(null);
  const [mockupError, setMockupError] = useState<string | null>(null);
  // @ts-ignore: setter is used, variable kept for state
  const [_mockupTaskKey, setMockupTaskKey] = useState<string | null>(null);
  // Printfiles and Templates data for mockup
  const [printfilesData, setPrintfilesData] = useState<any>(null);
  const [templatesData, setTemplatesData] = useState<any>(null);
  const [selectedTemplateUrl, setSelectedTemplateUrl] = useState<string | null>(null);
  const [fullTemplateData, setFullTemplateData] = useState<any>(null);
  // 購入モーダル用
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseModalData, setPurchaseModalData] = useState<{
    userId: string;
    imageUrl: string;
    catalogId: string;
    goodsName?: string;
    mockupUrl?: string;
  } | null>(null);
  // クレジット管理用のstate
  const [searchUsername, setSearchUsername] = useState('');
  const [foundUsers, setFoundUsers] = useState<UserCreditInfo[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserCreditInfo | null>(null);
  const [creditAmount, setCreditAmount] = useState(100);
  const [isSearching, setIsSearching] = useState(false);
  const [isAddingCredits, setIsAddingCredits] = useState(false);
  // ユーザー管理用のstate
  const [allUsers, setAllUsers] = useState<UserCreditInfo[]>([]);
  const [userStatistics, setUserStatistics] = useState<UserStatistics | null>(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchLimit, setUserSearchLimit] = useState(20);
  // 生成履歴管理用のstate
  const [generationHistoryStats, setGenerationHistoryStats] = useState<GenerationHistoryStats | null>(null);
  const [generationHistoryList, setGenerationHistoryList] = useState<GenerationHistoryItem[]>([]);
  const [isLoadingGenerationHistory, setIsLoadingGenerationHistory] = useState(false);
  const [generationHistorySearch, setGenerationHistorySearch] = useState('');
  const [generationHistoryCategory, setGenerationHistoryCategory] = useState('');
  const [generationHistoryUser, setGenerationHistoryUser] = useState('');
  const [generationHistoryRating, setGenerationHistoryRating] = useState('');
  // 生成履歴詳細モーダル用のstate
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<GenerationHistoryItem | null>(null);
  const [isHistoryDetailModalOpen, setIsHistoryDetailModalOpen] = useState(false);
  // 画像拡大モーダル用のstate
  const [isImageExpandModalOpen, setIsImageExpandModalOpen] = useState(false);
  const [expandedImageUrl, setExpandedImageUrl] = useState('');
  // 生成メニュー管理用のカテゴリ絞り込み状態
  const [selectedMenuCategory, setSelectedMenuCategory] = useState<string>('');
  // グッズ作成許可フィルター
  const [filterSuzuriEnabled, setFilterSuzuriEnabled] = useState(false);
  const [filterPrintfulEnabled, setFilterPrintfulEnabled] = useState(false);
  const [filterArtisanEnabled, setFilterArtisanEnabled] = useState(false);
  // 非表示フィルター（OFF: 表示中のみ / ON: 非表示のみ）
  const [filterHiddenOnly, setFilterHiddenOnly] = useState(false);
  // ドラッグ&ドロップ用の状態
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  // コンポーネントマウント時にグッズ一覧を自動読み込み
  useEffect(() => {
    if (activeSection === 'goods') {
      if (selectedVendor === 'suzuri') {
        loadGoodsItems();
      } else if (selectedVendor === 'printful') {
        // loadPrintfulProducts();  // カタログAPIは使用しない
        loadPrintfulGoods();  // GoodsManagementからのみ読み込み
      } else if (selectedVendor === 'artisan') {
        loadArtisanProducts();
      }
    }
  }, [activeSection, selectedVendor, showJapanShippable, showOverseas, showNonJapanShippable, showDiscontinued, showPublicOnly]);
  // ユーザー管理セクションがアクティブになった時にユーザー一覧を読み込み
  useEffect(() => {
    if (activeSection === 'users') {
      loadAllUsers('', 20);
    }
  }, [activeSection]);
  // 生成履歴セクションがアクティブになった時にデータを読み込み
  useEffect(() => {
    if (activeSection === 'generationHistory') {
      loadGenerationHistoryStats();
      loadGenerationHistoryList();
    }
  }, [activeSection]);
  const renderAdminNavItem = (
    sectionId: AdminSection,
    label: string,
    icon: React.ReactNode,
  ) => (
    <button
      key={sectionId}
      onClick={() => {
        setActiveSection(sectionId);
      }}
      className={`flex items-center space-x-3 p-3 rounded-md w-full text-left transition-colors duration-150
                  ${activeSection === sectionId ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-indigo-400'}`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
  // クレジット管理の関数
  const handleSearchUser = async () => {
    if (!searchUsername.trim()) {
      showToast('error', 'ユーザー名を入力してください');
      return;
    }
    setIsSearching(true);
    try {
      const userInfo = await getUserCredits(searchUsername.trim());
      if (Array.isArray(userInfo)) {
        setFoundUsers(userInfo);
        setSelectedUser(null);
        showToast('success', `${userInfo.length}人のユーザーが見つかりました`);
      } else {
        setFoundUsers([userInfo]);
        setSelectedUser(userInfo);
        showToast('success', 'ユーザーが見つかりました');
      }
    } catch (error) {
      setFoundUsers([]);
      setSelectedUser(null);
      showToast('error', 
        error instanceof Error ? error.message : 'ユーザーの検索に失敗しました');
    } finally {
      setIsSearching(false);
    }
  };
  const handleAddCredits = async () => {
    if (!selectedUser) {
      showToast('error', '先にユーザーを選択してください');
      return;
    }
    if (creditAmount <= 0) {
      showToast('error', 'クレジット数は1以上を入力してください');
      return;
    }
    setIsAddingCredits(true);
    try {
      const result = await addCreditsToUser({
        userIdentifier: selectedUser.firebase_uid,
        amount: creditAmount,
        description: `管理者による手動追加: ${creditAmount}クレジット`,
      });
      if (result.success) {
        showToast('success', `${selectedUser.nickname}に${creditAmount}クレジットを追加しました！`);
        const updatedUserInfo = await getUserCredits(selectedUser.firebase_uid);
        if (Array.isArray(updatedUserInfo)) {
          const updated = updatedUserInfo.find(u => u.firebase_uid === selectedUser.firebase_uid);
          if (updated) {
            setSelectedUser(updated);
            setFoundUsers(foundUsers.map(u => u.firebase_uid === selectedUser.firebase_uid ? updated : u));
          }
        } else {
          setSelectedUser(updatedUserInfo);
          setFoundUsers([updatedUserInfo]);
        }
      } else {
        showToast('error', result.message || 'クレジットの追加に失敗しました');
      }
    } catch (error) {
      console.error('クレジット追加エラー:', error);
      showToast('error', 
        `${selectedUser.nickname}へのクレジット追加に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
    } finally {
      setIsAddingCredits(false);
    }
  };
  // APIサンプルコードをクリップボードにコピー
  const copyApiSampleCode = (menu: AdminGenerationMenuItem) => {
    const promptVariables = menu.promptVariables || [];
    const useEditImage = menu.useEditImage || menu.use_edit_image || false;
    const refImageLabels = menu.refImageLabels || menu.ref_image_labels || [];

    // サンプルコード生成
    let sampleCode = `# AISHA API サンプルコード - ${menu.name}
# メニューID: ${menu.id}
# エンジン: ${menu.engine}
# クレジットコスト: ${menu.credit}

import requests

API_URL = "https://django-api-asia-756710178273.asia-northeast1.run.app/api/simple-generate/"
API_KEY = "your_api_key_here"

headers = {
    "X-API-Key": API_KEY
}

# FormDataでリクエストを送信
data = {
    "menu_id": ${menu.id},
    # "user_id": "your_user_id",  # ユーザーIDを指定すると、そのユーザーのタイムラインに保存されます
    # "aspect_ratio": "1:1",  # 画像比率（オプション）: "1:1", "16:9", "9:16", "4:3", "3:4"（未指定時は元画像のまま）
`;

    // プロンプト変数がある場合
    if (promptVariables.length > 0) {
      sampleCode += `    # プロンプト変数 (prompt_variables)\n`;
      promptVariables.forEach((pv, index) => {
        const multilineNote = pv.multiline ? ' (複数行対応)' : '';
        sampleCode += `    "prompt_variables[${index}][key]": "${pv.key}",\n`;
        sampleCode += `    "prompt_variables[${index}][value]": "ここに${pv.label}を入力${multilineNote}",\n`;
      });
    }

    sampleCode += `}

files = {
    "image": open("your_car_image.jpg", "rb"),  # メイン画像
`;

    // 編集画像が必要な場合
    if (useEditImage) {
      sampleCode += `    "edit_image": open("your_edit_image.jpg", "rb"),  # 編集画像\n`;
    }

    // 参照画像がある場合
    if (refImageLabels.length > 0) {
      refImageLabels.forEach((label, index) => {
        sampleCode += `    # reference_images[${index}]: ${label}\n`;
      });
      sampleCode += `    # 参照画像は "reference_images" キーで複数送信可能\n`;
    }

    sampleCode += `}

response = requests.post(API_URL, headers=headers, data=data, files=files)

if response.status_code == 200:
    result = response.json()
    print("生成成功!")
    print(f"画像URL: {result['image_url']}")
    print(f"プロンプト: {result['prompt_formatted']}")
else:
    print(f"エラー: {response.status_code}")
    print(response.json())
`;

    // クリップボードにコピー
    navigator.clipboard.writeText(sampleCode).then(() => {
      showToast('success', 'APIサンプルコードをコピーしました');
    }).catch((err) => {
      console.error('クリップボードへのコピーに失敗:', err);
      showToast('error', 'コピーに失敗しました');
    });
  };

  // グッズ管理の関数
  // Artisanサービス品一覧を読み込み
  const loadArtisanProducts = async () => {
    setIsLoadingArtisan(true);
    try {
      const products = await getArtisanProducts();
      setArtisanProducts(products);
    } catch (error) {
      showToast('error',
        error instanceof Error ? error.message : 'Artisanサービス品一覧の取得に失敗しました');
    } finally {
      setIsLoadingArtisan(false);
    }
  };

  // Artisanサービス品を削除
  const handleDeleteArtisanProduct = async (id: number, productName: string) => {
    if (!window.confirm(`「${productName}」を削除しますか？この操作は取り消せません。`)) {
      return;
    }
    try {
      await deleteArtisanProductApi(id);
      showToast('success', `「${productName}」を削除しました`);
      await loadArtisanProducts();
    } catch (error) {
      showToast('error',
        error instanceof Error ? error.message : 'Artisanサービス品の削除に失敗しました');
    }
  };

  const loadArtisanOrders = async () => {
    setIsLoadingArtisanOrders(true);
    try {
      const orders = await getArtisanOrders();
      setArtisanOrders(orders);
    } catch (error) {
      console.error('Artisan注文取得エラー:', error);
    } finally {
      setIsLoadingArtisanOrders(false);
    }
  };


  const loadGoodsItems = async () => {
    setIsLoadingGoods(true);
    try {
      const params = new URLSearchParams();
      if (showPublicOnly) {
        params.append('public_only', 'true');
      }
      if (showSubMaterialsOnly) {
        params.append('needs_sub_materials', 'true');
      }
      const items = await getGoodsManagementList(params.toString());
      setGoodsItems(items);
    } catch (error) {
      showToast('error', 
        error instanceof Error ? error.message : 'グッズ管理一覧の取得に失敗しました');
    } finally {
      setIsLoadingGoods(false);
    }
  };
  const handleSyncSuzuriItems = async () => {
    setIsSyncingGoods(true);
    try {
      const result = await syncSuzuriItems();
      showToast('success', result.message);
      // 同期後に一覧を再取得
      await loadGoodsItems();
    } catch (error) {
      console.error('SUZURIアイテム同期エラー:', error);
      showToast('error', 
        error instanceof Error ? error.message : 'SUZURIアイテムの同期に失敗しました');
    } finally {
      setIsSyncingGoods(false);
    }
  };
  // Printful関連の関数
  const loadPrintfulProducts = async () => {
    setIsLoadingPrintful(true);
    try {
      // Store IDがないため、カタログ商品を取得
      const response = await getPrintfulCatalogProducts();
      if (response.success) {
        setPrintfulProducts(response.products || []);
      }
    } catch (error) {
      console.error('Printful商品取得エラー:', error);
      // エラーメッセージを表示しない（初回読み込み時のエラーは無視）
    } finally {
      setIsLoadingPrintful(false);
    }
  };
  const handleSyncPrintfulProducts = async () => {
    setIsSyncingPrintful(true);
    try {
      const result = await syncAllPrintfulProducts();
      if (result.success) {
        showToast('success', result.message || '同期が完了しました');
        // Printful商品を再読み込み
        await loadPrintfulGoods();
        // カタログ商品を再読み込み
        await loadPrintfulProducts();
      }
    } catch (error) {
      console.error('Printful同期エラー:', error);
      showToast('error', 
        error instanceof Error ? error.message : 'Printful商品の同期に失敗しました');
    } finally {
      setIsSyncingPrintful(false);
    }
  };
  const handleJapanShippingCheck = async () => {
    setIsCheckingJapan(true);
    try {
      // 日本発送チェックをバックグラウンドで開始
      const response = await fetch(`${AISHA_API_BASE}/printful/japan-check/start/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();
      if (result.success) {
        showToast('success', '日本発送チェックを開始しました。処理には5-10分かかります。');
        // 10秒後に商品リストを再読み込み
        setTimeout(async () => {
          await loadPrintfulGoods();
          showToast('info', '商品リストを更新しました');
        }, 10000);
      } else {
        if (result.message?.includes('既に実行中')) {
          showToast('info', '処理が既に実行中です');
        } else {
          showToast('error', 'チェックの開始に失敗しました');
        }
      }
    } catch (error) {
      console.error('Japan check error:', error);
      showToast('error', 'エラーが発生しました');
    } finally {
      setIsCheckingJapan(false);
    }
  };
  // Printful商品の読み込み（GoodsManagementから）
  const loadPrintfulGoods = async () => {
    try {
      // パラメータを構築
      const params = new URLSearchParams();
      if (showPublicOnly) {
        params.append('public_only', 'true');
      }
      const url = params.toString() 
        ? `${AISHA_API_BASE}/printful/goods/?${params.toString()}`
        : `${AISHA_API_BASE}/printful/goods/`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        // チェックボックスの状態に応じてフィルタリング
        const filteredGoods = data.goods.filter((goods: any) => {
          if (goods.supplier !== 'printful') return false;
          // 公開のみフィルター
          if (showPublicOnly && !goods.is_public) return false;
          // 廃版品の表示チェック
          if (goods.is_discontinued && !showDiscontinued) return false;
          if (!goods.is_discontinued && showDiscontinued && !showJapanShippable && !showOverseas && !showNonJapanShippable) return false;
          // 日本配送区分の表示チェック（local: 日本発送品 / overseas: 海外直送品 / none: 日本発送不可）
          if (!goods.is_discontinued) {
            const shippingType = goods.japan_shipping_type || (goods.is_japan_shippable ? 'local' : 'none');
            if (shippingType === 'local' && !showJapanShippable) return false;
            if (shippingType === 'overseas' && !showOverseas) return false;
            if (shippingType === 'none' && !showNonJapanShippable) return false;
          }
          return true;
        });
        setPrintfulGoods(filteredGoods);
        // 保存済みの画像URLをセット
        const savedImages: {[key: number]: string} = {};
        const savedMockups: {[key: number]: string} = {};
        filteredGoods.forEach((goods: any) => {
          if (goods.mockup_image_url) {
            savedImages[goods.id] = goods.mockup_image_url;
          }
          if (goods.mockup_url) {
            savedMockups[goods.id] = goods.mockup_url;
          }
        });
        setProductImages(savedImages);
        setProductMockups(savedMockups);
        // カタログAPIを使用しないため、空配列を設定
        setPrintfulProducts([]);
      }
    } catch (error) {
      console.error('Printful商品読み込みエラー:', error);
    }
  };
  // Printful商品の公開/非公開切り替え
  const handleTogglePrintfulVisibility = async (goodsId: number, isPublic: boolean) => {
    try {
      const response = await fetch(`${AISHA_API_BASE}/printful/goods/${goodsId}/visibility/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_public: !isPublic })
      });
      const data = await response.json();
      if (data.success) {
        showToast('success', data.message);
        await loadPrintfulGoods();
      }
    } catch (error) {
      console.error('公開設定エラー:', error);
      showToast('error', '公開設定の変更に失敗しました');
    }
  };
  // Printful商品の編集モーダルを開く
  const handleOpenPrintfulEditModal = (goods: any) => {
    // goodsオブジェクトをPrintfulProductEditModalが期待する形式に変換
    const productForModal = {
      id: goods.id,
      external_id: goods.vendor_item_id || '',
      name: goods.item_name || goods.display_name,
      thumbnail_url: goods.icon_url,
      mockup_urls: goods.mockup_url ? [goods.mockup_url] : [],
      is_ignored: !goods.is_public,
      retail_price: goods.final_price?.toString(),
      currency: 'JPY',
      vendor_item_id: goods.vendor_item_id,
      sync_product_id: goods.printful_sync_product_id || goods.api_config?.sync_product_id || null,
      display_name: goods.display_name,
      display_order: goods.display_order || 0,
      additional_profit: goods.profit_margin || 0,
      shipping_surcharge: goods.shipping_surcharge || 0,
      base_price: goods.base_price
    };
    setEditingPrintfulProduct(productForModal);
    setIsPrintfulEditModalOpen(true);
  };
  // Printful商品の編集保存
  const handleSavePrintfulEdit = async (updatedProduct: any) => {
    try {
      // バックエンドに保存
      const response = await fetch(`${AISHA_API_BASE}/printful/goods/${updatedProduct.id}/update/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_public: !updatedProduct.is_ignored,
          display_name: updatedProduct.display_name,
          display_order: updatedProduct.display_order,
          base_price: updatedProduct.base_price || 0,
          profit_margin: updatedProduct.additional_profit || 0,
          shipping_surcharge: updatedProduct.shipping_surcharge || 0,
          final_price: parseInt(updatedProduct.retail_price || '0')
        })
      });
      if (response.ok) {
        showToast('success', '商品情報を更新しました');
        await loadPrintfulGoods();
      }
    } catch (error) {
      console.error('保存エラー:', error);
      showToast('error', '保存に失敗しました');
    }
  };
  // Printful商品の価格更新
  // @ts-ignore: kept for future use
  const _handleUpdatePrintfulPrice = async (goodsId: number) => {
    try {
      const response = await fetch(`${AISHA_API_BASE}/printful/goods/${goodsId}/price/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPrintfulPrice)
      });
      const data = await response.json();
      if (data.success) {
        showToast('success', data.message);
        setEditingPrintfulPrice(null);
        setNewPrintfulPrice({});
        await loadPrintfulGoods();
      }
    } catch (error) {
      console.error('価格更新エラー:', error);
      showToast('error', '価格の更新に失敗しました');
    }
  };
  // 画像アップロード処理
  const handleImageUploadForProduct = async (file: File, goodsId: number) => {
    setUploadingImageForProduct(goodsId);
    try {
      // 既存のuploadMenuImageを使用
      const response = await uploadMenuImage(file, 'sample_input');
      const imageUrl = response.public_url;
      if (imageUrl) {
        // URLが公開アクセス可能であることをログに記録
        console.log('✅ Image uploaded successfully:', imageUrl);
        console.log('Is Firebase URL:', imageUrl.includes('firebasestorage.googleapis.com'));
        console.log('Has alt=media:', imageUrl.includes('alt=media'));
        // アップロード成功したら、画像URLを保存
        setProductImages(prev => ({
          ...prev,
          [goodsId]: imageUrl
        }));
        // DBに画像URLを保存
        try {
          const updateResponse = await fetch(`${AISHA_API_BASE}/printful/goods/${goodsId}/update-mockup-image/`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mockup_image_url: imageUrl })
          });
          // レスポンスのContent-Typeを確認
          const contentType = updateResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const updateData = await updateResponse.json();
            if (updateData.success) {
              showToast('success', '画像をアップロードしました');
            }
          } else {
            // JSONでない場合もアップロード自体は成功とみなす
            showToast('success', '画像をアップロードしました');
          }
        } catch (dbError) {
          // DB保存エラーでも画像自体はアップロード済みなので表示は更新
          console.warn('DB保存エラー（画像は表示可能）:', dbError);
          showToast('success', '画像をアップロードしました');
        }
      } else {
        showToast('error', '画像のアップロードに失敗しました');
      }
    } catch (error) {
      console.error('画像アップロードエラー:', error);
      showToast('error', '画像のアップロードに失敗しました');
    } finally {
      setUploadingImageForProduct(null);
    }
  };
  // デフォルトの画像位置パラメータを計算
  const calculateDefaultParams = (imageUrl: string, templateData: any) => {
    if (!imageUrl || !templateData) return null;
    const printAreaWidth = templateData.print_area_width;
    const printAreaHeight = templateData.print_area_height;
    // サンプル画像のサイズ（実際には640x480）
    // Printful APIでは画像の縦横比を保持するため、印刷エリアに合わせて計算
    const imageWidth = 640;
    const imageHeight = 480;
    const imageAspectRatio = imageWidth / imageHeight; // 4:3 = 1.333...
    const printAreaAspectRatio = printAreaWidth / printAreaHeight; // 671:261 = 2.571...
    let finalWidth, finalHeight, finalTop, finalLeft;
    if (imageAspectRatio < printAreaAspectRatio) {
      // 画像の方が縦長 -> 高さに合わせる
      finalHeight = printAreaHeight;
      finalWidth = Math.round(printAreaHeight * imageAspectRatio);
      finalTop = 0;
      finalLeft = Math.round((printAreaWidth - finalWidth) / 2); // 中央寄せ
    } else {
      // 画像の方が横長 -> 幅に合わせる
      finalWidth = printAreaWidth;
      finalHeight = Math.round(printAreaWidth / imageAspectRatio);
      finalTop = Math.round((printAreaHeight - finalHeight) / 2); // 中央寄せ
      finalLeft = 0;
    }
    console.log('Calculated default params:', {
      printArea: { width: printAreaWidth, height: printAreaHeight },
      image: { width: imageWidth, height: imageHeight, aspectRatio: imageAspectRatio },
      result: { width: finalWidth, height: finalHeight, top: finalTop, left: finalLeft }
    });
    return {
      width: finalWidth,
      height: finalHeight,
      top: finalTop,
      left: finalLeft
    };
  };
  // バリアント選択モーダルを開く
  const handleOpenVariantSelectForMockup = async (goodsId: number) => {
    const imageUrl = productImages[goodsId];
    const goods = printfulGoods.find(g => g.id === goodsId);
    if (!imageUrl || !goods) {
      showToast('error', '画像または商品情報が見つかりません');
      return;
    }
    setSelectedGoodsForMockup(goods);
    setShowVariantSelectModal(true);
    setLoadingCatalogVariants(true);
    try {
      // 保存された位置データを取得
      const positionResponse = await fetch(`${AISHA_API_BASE}/printful/goods/${goodsId}/position/get/`);
      const positionData = await positionResponse.json();
      // カタログ商品のバリアント一覧を取得
      const response = await fetch(`${AISHA_API_BASE}/printful/catalog/${goods.vendor_item_id}/variants/`);
      const data = await response.json();
      if (data.success) {
        setCatalogVariants(data.variants);
        // デフォルトバリアントを選択
        if (data.default_variant) {
          setSelectedVariantForMockup(data.default_variant);
        }
      } else {
        showToast('error', 'バリアント情報の取得に失敗しました');
        setShowVariantSelectModal(false);
      }
      // Printfiles と Templates データを取得
      console.log('Fetching data for vendor_item_id:', goods.vendor_item_id);
      const [printfilesRes, templatesRes] = await Promise.all([
        fetch(`${AISHA_API_BASE}/printful/printfiles/${goods.vendor_item_id}/`),
        fetch(`${AISHA_API_BASE}/printful/templates/${goods.vendor_item_id}/`)
      ]);
      const printfilesData = await printfilesRes.json();
      const templatesData = await templatesRes.json();
      console.log('Printfiles data:', printfilesData);
      console.log('Templates data:', templatesData);
      if (printfilesData.success) {
        setPrintfilesData(printfilesData.printfiles);
      }
      if (templatesData.success) {
        setTemplatesData(templatesData.templates);
        // デフォルトバリアントのテンプレート画像をセット
        if (data.default_variant && templatesData.templates?.variant_mapping) {
          const variantId = parseInt(data.default_variant.variant_id);
          const variantTemplate = templatesData.templates.variant_mapping.find((v: any) => 
            parseInt(v.variant_id) === variantId
          );
          if (variantTemplate?.templates?.[0]) {
            // templatesリストから実際のテンプレートを取得
            const fullTemplate = templatesData.templates.templates?.find((t: any) => 
              t.template_id === variantTemplate.templates[0].template_id
            );
            if (fullTemplate?.image_url) {
              setSelectedTemplateUrl(fullTemplate.image_url);
              setFullTemplateData(fullTemplate);
              // 保存された位置データがある場合はそれを使用、なければデフォルトパラメータを設定
              if (positionData.success && positionData.position_data && 
                  Object.keys(positionData.position_data).length > 0) {
                console.log('Loading saved position data:', positionData.position_data);
                const savedParams = {
                  width: positionData.position_data.width,
                  height: positionData.position_data.height,
                  top: positionData.position_data.top,
                  left: positionData.position_data.left
                };
                setMockupPositionParams(savedParams);
                setPreviewParams(savedParams);
              } else {
                // デフォルトパラメータを設定
                const defaultParams = calculateDefaultParams(imageUrl, fullTemplate);
                if (defaultParams) {
                  setMockupPositionParams(defaultParams);
                  setPreviewParams(defaultParams);
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('バリアント取得エラー:', error);
      showToast('error', 'バリアント情報の取得に失敗しました');
      setShowVariantSelectModal(false);
    } finally {
      setLoadingCatalogVariants(false);
    }
  };
  // Printful APIでモックアップを実際に生成する関数
  const handleCreatePrintfulMockup = async () => {
    if (!selectedVariantForMockup || !selectedGoodsForMockup || !fullTemplateData) {
      showToast('error', '必要な情報が不足しています');
      return;
    }
    const imageUrl = productImages[selectedGoodsForMockup.id];
    if (!imageUrl) {
      showToast('error', '画像URLが見つかりません');
      return;
    }
    setCreatingMockup(true);
    setMockupError(null);
    setGeneratedMockupUrl(null);
    try {
      // バリアントのplacement情報を取得
      const variantPrintfile = printfilesData?.variant_printfiles?.find(
        (vp: any) => vp.variant_id === selectedVariantForMockup.variant_id
      );
      const placement = Object.keys(variantPrintfile?.placements || {})[0] || 'default';
      // position パラメータを構築（ユーザーが調整した値を使用）
      const position = {
        area_width: fullTemplateData.print_area_width,
        area_height: fullTemplateData.print_area_height,
        width: mockupPositionParams.width,  // previewParamsではなくmockupPositionParamsを使用
        height: mockupPositionParams.height,
        top: mockupPositionParams.top,
        left: mockupPositionParams.left
      };
      console.log('selectedVariantForMockup:', selectedVariantForMockup);
      const productId = selectedVariantForMockup.product_id || selectedVariantForMockup.catalog_product_id || selectedVariantForMockup.catalog_id || 19;
      console.log('Creating mockup with params:', {
        product_id: productId,
        variant_id: selectedVariantForMockup.variant_id,
        placement,
        position,
        image_url: imageUrl
      });
      // Printful API呼び出し
      const response = await fetch(`${AISHA_API_BASE}/printful/create-mockup/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_id: productId,
          variant_id: selectedVariantForMockup.variant_id,
          placement: placement,
          image_url: imageUrl,
          position: position
        })
      });
      // レスポンスのテキストを先に取得
      const responseText = await response.text();
      console.log('Response text:', responseText);
      // 空のレスポンスチェック
      if (!responseText) {
        throw new Error('サーバーから空のレスポンスが返されました');
      }
      // JSONパース
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        console.error('Response was:', responseText);
        throw new Error('サーバーレスポンスのパースに失敗しました');
      }
      if (data.success && data.data) {
        const taskKey = data.data.task_key;
        setMockupTaskKey(taskKey);
        // タスクの状態をポーリング
        await pollMockupTask(taskKey);
      } else {
        throw new Error(data.error || 'モックアップ生成に失敗しました');
      }
    } catch (error) {
      console.error('Mockup creation error:', error);
      setMockupError(error instanceof Error ? error.message : 'モックアップ生成エラー');
      showToast('error', 'モックアップ生成に失敗しました');
    } finally {
      setCreatingMockup(false);
    }
  };
  // モックアップタスクの状態をポーリング
  const pollMockupTask = async (taskKey: string) => {
    const maxAttempts = 30; // 最大30回試行（約30秒）
    let attempts = 0;
    const checkStatus = async (): Promise<void> => {
      try {
        const response = await fetch(`${AISHA_API_BASE}/printful/mockup-task/${taskKey}/`);
        const data = await response.json();
        if (data.success && data.data) {
          const status = data.data.status;
          if (status === 'completed') {
            // モックアップ生成完了
            const mockupUrl = data.data.mockups?.[0]?.mockup_url;
            if (mockupUrl) {
              setGeneratedMockupUrl(mockupUrl);
              showToast('success', 'モックアップが生成されました');
            } else {
              throw new Error('モックアップURLが取得できませんでした');
            }
          } else if (status === 'failed') {
            throw new Error(data.data.error || 'モックアップ生成に失敗しました');
          } else if (status === 'pending' && attempts < maxAttempts) {
            // まだ処理中、1秒後に再試行
            attempts++;
            setTimeout(() => checkStatus(), 1000);
          } else {
            throw new Error('タイムアウト: モックアップ生成に時間がかかりすぎています');
          }
        } else {
          throw new Error('タスク状態の取得に失敗しました');
        }
      } catch (error) {
        console.error('Polling error:', error);
        setMockupError(error instanceof Error ? error.message : 'タスク状態確認エラー');
        showToast('error', 'モックアップ生成状態の確認に失敗しました');
      }
    };
    await checkStatus();
  };
  // モックアップに反映処理（位置データ保存とモックアップURL更新）
  const handleGenerateMockup = async (goodsId: number, variantInfo?: any) => {
    const goods = printfulGoods.find(g => g.id === goodsId);
    if (!goods) {
      showToast('error', '商品情報が見つかりません');
      return;
    }
    // バリアント情報が指定されていない場合は、バリアント選択モーダルを開く
    if (!variantInfo) {
      handleOpenVariantSelectForMockup(goodsId);
      return;
    }
    setGeneratingMockup(goodsId);
    setShowVariantSelectModal(false);
    try {
      // 生成されたモックアップURLが存在する場合のみ処理を実行
      if (!generatedMockupUrl) {
        showToast('error', '先にモックアップを作成してください');
        return;
      }
      // 位置データを保存
      const positionResponse = await fetch(`${AISHA_API_BASE}/printful/goods/${goodsId}/position/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          position_data: {
            width: mockupPositionParams.width,
            height: mockupPositionParams.height,
            top: mockupPositionParams.top,
            left: mockupPositionParams.left,
            area_width: fullTemplateData?.print_area_width || 0,
            area_height: fullTemplateData?.print_area_height || 0
          }
        })
      });
      const positionData = await positionResponse.json();
      if (!positionData.success) {
        showToast('error', '位置データの保存に失敗しました');
        return;
      }
      // mockup_urlに生成されたモックアップURLをセット
      const updateResponse = await fetch(`${AISHA_API_BASE}/printful/goods/${goodsId}/update-mockup/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mockup_url: generatedMockupUrl
        })
      });
      const updateData = await updateResponse.json();
      if (updateData.success) {
        showToast('success', 'モックアップ設定を保存しました');
        // 商品リストを再読み込み
        await loadPrintfulGoods();
        // モーダルを閉じる
        setShowVariantSelectModal(false);
      } else {
        showToast('error', 'モックアップURLの更新に失敗しました');
      }
    } catch (error) {
      console.error('モックアップ反映エラー:', error);
      showToast('error', 'モックアップ反映中にエラーが発生しました');
    } finally {
      setGeneratingMockup(null);
    }
  };
  // @ts-ignore: kept for future use
  const _handleOpenVariantSelection = async (goodsId: number) => {
    const goods = printfulGoods.find(g => g.id === goodsId);
    if (!goods || !goods.mockup_image_url) {
      showToast('error', '商品情報またはモックアップ画像が見つかりません');
      return;
    }
    setLoadingVariants(true);
    setShowVariantModal(goodsId);
    try {
      // バリエーション情報を取得
      const response = await fetch(`${AISHA_API_BASE}/printful/goods/${goodsId}/variants/`);
      const data = await response.json();
      if (data.success) {
        setProductVariants(data.data);
        // サイズと色の選択状態をリセット
        setSelectedSize(null);
        setSelectedColor(null);
        setSelectedVariant(null);
      } else {
        showToast('error', 'バリエーション情報の取得に失敗しました');
        setShowVariantModal(null);
      }
    } catch (error) {
      console.error('バリエーション取得エラー:', error);
      showToast('error', 'バリエーション情報の取得中にエラーが発生しました');
      setShowVariantModal(null);
    } finally {
      setLoadingVariants(false);
    }
  };
  const handleCreateSampleProduct = async (goodsId: number) => {
    if (!selectedVariant) {
      showToast('error', 'バリエーションを選択してください');
      return;
    }
    setCreatingProduct(goodsId);
    try {
      // Printful商品作成API呼び出し（選択したバリエーションIDを含む）
      const response = await fetch(`${AISHA_API_BASE}/printful/goods/${goodsId}/create-product/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variant_id: selectedVariant })
      });
      const data = await response.json();
      if (data.success) {
        showToast('success', 'Printful商品を作成しました');
        // Printful管理画面のURLを表示（あれば）
        if (data.data?.url) {
          console.log('Printful商品URL:', data.data.url);
          // 配送地域の注意事項を表示
          if (data.data?.shipping_note) {
            showToast('info', data.data.shipping_note);
          }
          // URLを新しいタブで開く
          window.open(data.data.url, '_blank');
        }
        // モーダルを閉じる
        setShowVariantModal(null);
        setProductVariants(null);
        setSelectedVariant(null);
        setSelectedSize(null);
        setSelectedColor(null);
        // 商品リストを再読み込み
        await loadPrintfulGoods();
      } else {
        showToast('error', data.error || 'Printful商品作成に失敗しました');
      }
    } catch (error) {
      console.error('Printful商品作成エラー:', error);
      showToast('error', 'Printful商品作成中にエラーが発生しました');
    } finally {
      setCreatingProduct(null);
    }
  };
  // @ts-ignore: kept for future use
  const _handleDeletePrintfulProduct = async (productId: string) => {
    if (!confirm('この商品を削除してもよろしいですか？')) return;
    try {
      const result = await deletePrintfulSyncProduct(productId);
      if (result.success) {
        showToast('success', '商品を削除しました');
        await loadPrintfulProducts();
      }
    } catch (error) {
      console.error('商品削除エラー:', error);
      showToast('error', 
        error instanceof Error ? error.message : '商品の削除に失敗しました');
    }
  };
  // ユーザー管理の関数
  const loadAllUsers = async (searchQuery?: string, limit?: number) => {
    setIsLoadingUsers(true);
    try {
      const result = await getAllUsers(searchQuery, limit);
      setAllUsers(result.users);
      setUserStatistics(result.statistics);
    } catch (error) {
      console.error('ユーザー一覧取得エラー:', error);
      showToast('error', 
        error instanceof Error ? error.message : 'ユーザー一覧の取得に失敗しました');
    } finally {
      setIsLoadingUsers(false);
    }
  };
  const handleUserSearch = () => {
    loadAllUsers(userSearchQuery, userSearchLimit);
  };
  const handleUserSearchReset = () => {
    setUserSearchQuery('');
    setUserSearchLimit(20);
    loadAllUsers('', 20);
  };
  // 生成履歴管理の関数
  const loadGenerationHistoryStats = async () => {
    try {
      const stats = await getGenerationHistoryStats();
      if (stats) {
        setGenerationHistoryStats(stats);
      }
    } catch (error) {
      console.error('生成履歴統計取得エラー:', error);
      showToast('error', '生成履歴統計の取得に失敗しました');
    }
  };
  const loadGenerationHistoryList = async () => {
    setIsLoadingGenerationHistory(true);
    try {
      const params = {
        limit: 50,
        search: generationHistorySearch,
        category: generationHistoryCategory,
        user: generationHistoryUser,
        rating: generationHistoryRating
      };
      const result = await getGenerationHistoryList(params);
      if (result) {
        setGenerationHistoryList(result.history);
      }
    } catch (error) {
      console.error('生成履歴一覧取得エラー:', error);
      showToast('error', '生成履歴一覧の取得に失敗しました');
    } finally {
      setIsLoadingGenerationHistory(false);
    }
  };
  const handleGenerationHistorySearch = () => {
    loadGenerationHistoryList();
  };
  const handleGenerationHistoryReset = () => {
    setGenerationHistorySearch('');
    setGenerationHistoryCategory('');
    setGenerationHistoryUser('');
    setGenerationHistoryRating('');
    loadGenerationHistoryList();
  };
  // 生成履歴詳細モーダル関連の関数
  const handleHistoryItemClick = (item: GenerationHistoryItem) => {
    setSelectedHistoryItem(item);
    setIsHistoryDetailModalOpen(true);
  };
  const handleCloseHistoryDetailModal = () => {
    setIsHistoryDetailModalOpen(false);
    setSelectedHistoryItem(null);
  };
  // 画像拡大モーダル用のハンドラー
  const handleImageExpand = (imageUrl: string) => {
    setExpandedImageUrl(imageUrl);
    setIsImageExpandModalOpen(true);
  };
  const handleCloseImageExpandModal = () => {
    setIsImageExpandModalOpen(false);
    setExpandedImageUrl('');
  };
  const handleUpdateGoodsItem = async (goodsId: number, data: any) => {
    try {
      const updatedItem = await updateGoodsManagement(goodsId, data);
      setGoodsItems(prev => 
        prev.map(item => item.id === goodsId ? updatedItem : item)
      );
      showToast('success', 'グッズ情報を更新しました');
      setEditingGoodsItem(null);
    } catch (error) {
      console.error('グッズ更新エラー:', error);
      showToast('error', 
        error instanceof Error ? error.message : 'グッズ情報の更新に失敗しました');
    }
  };
  // ドラッグ&ドロップのハンドラー
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setIsDragging(true);
  };
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setIsDragging(false);
    if (!over || active.id === over.id) {
      return;
    }
    // カテゴリのドラッグ＆ドロップ処理
    if (categories) {
      const oldIndex = categories.findIndex(cat => cat.id.toString() === active.id);
      const newIndex = categories.findIndex(cat => cat.id.toString() === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newCategories = arrayMove(categories, oldIndex, newIndex);
        // ローカル状態を先に更新（ユーザー体験をスムーズに）
        refreshCategories();
        // サーバーに順番更新を送信
        const orderUpdates = newCategories.map((cat, index) => ({
          id: cat.id,
          orderIndex: index
        }));
        const success = await updateCategoryOrder(orderUpdates, () => {
          showToast('error', 'カテゴリ順番の更新に失敗しました');
        });
        if (success) {
          showToast('success', 'カテゴリ順番を更新しました');
          refreshCategories();
        }
        return;
      }
    }
    // メニューのドラッグ＆ドロップ処理
    if (menus) {
      const oldIndex = menus.findIndex(menu => menu.id.toString() === active.id);
      const newIndex = menus.findIndex(menu => menu.id.toString() === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newMenus = arrayMove(menus, oldIndex, newIndex);
        // ローカル状態を先に更新（ユーザー体験をスムーズに）
        refreshMenus();
        // サーバーに順番更新を送信
        const orderUpdates = newMenus.map((menu, index) => ({
          id: menu.id,
          display_order: index * 10
        }));
        const success = await updateMenuOrder(orderUpdates, () => {
          showToast('error', 'メニュー順番の更新に失敗しました');
        });
        if (success) {
          showToast('success', 'メニュー順番を更新しました');
          refreshMenus();
        }
      }
    }
  };
  const renderSectionContent = () => {
    switch (activeSection) {
      case 'sales':
        return <SalesManagementView />;
      case 'orderManagement':
        return (
          <div>
            {/* Tab bar */}
            <div className="flex mb-6 border-b border-gray-700">
              <button
                onClick={() => setOrderTab('printful')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  orderTab === 'printful'
                    ? 'border-indigo-500 text-indigo-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                Printful注文
              </button>
              <button
                onClick={() => {
                  setOrderTab('artisan');
                  if (artisanOrders.length === 0) {
                    loadArtisanOrders();
                  }
                }}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  orderTab === 'artisan'
                    ? 'border-indigo-500 text-indigo-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                Artisan注文
              </button>
            </div>

            {orderTab === 'printful' ? (
              <OrderManagement />
            ) : (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-indigo-300">Artisan注文管理</h3>
                  <button
                    onClick={loadArtisanOrders}
                    disabled={isLoadingArtisanOrders}
                    className="bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white font-semibold py-2 px-4 rounded-lg text-sm"
                  >
                    {isLoadingArtisanOrders ? '読み込み中...' : '更新'}
                  </button>
                </div>

                {/* ステータスタブ（Printful注文と同じデザイン） */}
                <div className="flex space-x-2 border-b border-gray-700 mb-4">
                  {[
                    { key: '', label: '全て', color: 'gray' },
                    { key: 'new', label: '新規注文', color: 'blue' },
                    { key: 'confirming', label: 'お客様確認', color: 'yellow' },
                    { key: 'manufacturing', label: '製造中', color: 'yellow' },
                    { key: 'shipped', label: '発送済', color: 'green' },
                    { key: 'reviewed', label: '評価済', color: 'green' },
                    { key: 'cancelled', label: 'キャンセル', color: 'orange' },
                  ].map((tab) => {
                    const count = tab.key === ''
                      ? artisanOrders.length
                      : artisanOrders.filter(o => o.status === tab.key).length;
                    return (
                      <button
                        key={tab.key}
                        onClick={() => setArtisanOrderStatusFilter(tab.key)}
                        className={`flex items-center px-4 py-3 font-medium transition-colors border-b-2 ${
                          artisanOrderStatusFilter === tab.key
                            ? `text-${tab.color}-400 border-${tab.color}-400`
                            : 'text-gray-400 border-transparent hover:text-gray-300'
                        }`}
                      >
                        {tab.label}
                        <span className="ml-2 px-2 py-0.5 bg-gray-700 rounded-full text-xs">
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {(() => {
                  const filtered = artisanOrderStatusFilter
                    ? artisanOrders.filter(o => o.status === artisanOrderStatusFilter)
                    : artisanOrders;
                  return isLoadingArtisanOrders ? (
                  <div className="text-center text-gray-400 py-12">読み込み中...</div>
                ) : filtered.length === 0 ? (
                  <div className="text-center text-gray-400 py-12">注文がありません</div>
                ) : (
                  <div className="space-y-4">
                    {filtered.map((order) => (
                      <div key={order.id} className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
                        {/* ヘッダー: 注文日 + 注文番号 + 金額 */}
                        <div className="flex items-center justify-between px-4 py-2 bg-gray-900/60 border-b border-gray-700/50">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-white">{new Date(order.created_at).toLocaleDateString('ja-JP')}</span>
                            <span className="text-sm text-gray-400">注文 #{order.id}</span>
                            {order.with_installation && (
                              <span className="text-xs text-blue-400 bg-blue-900/30 px-1.5 py-0.5 rounded">施工込</span>
                            )}
                          </div>
                          <span className="text-lg font-bold text-orange-400">¥{order.total_amount.toLocaleString()}</span>
                        </div>

                        <div className="p-4">
                          {/* 商品 + 画像 */}
                          <div className="flex gap-4 mb-4">
                            {order.image_url && (
                              <img src={order.image_url} alt="" className="w-20 h-20 object-cover rounded-lg border border-gray-600 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-base font-bold text-white">{order.product_name}{order.variation_name ? ` / ${order.variation_name}` : ''}</p>
                            </div>
                            <div className="flex flex-col gap-1.5 flex-shrink-0">
                              <button
                                onClick={() => setConfirmBoardOrderId(order.id)}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs px-3 py-1.5 rounded-lg transition-colors"
                              >
                                確認ボード
                              </button>
                              {order.review_rating && (
                                <button
                                  onClick={() => setReviewDetailOrder(order)}
                                  className="text-yellow-400 text-sm cursor-pointer hover:opacity-80"
                                >
                                  {'★'.repeat(order.review_rating)}{'☆'.repeat(5 - order.review_rating)}
                                </button>
                              )}
                              {(order.status === 'new' || order.status === 'confirming') && (
                                <button
                                  onClick={async (e) => {
                                    if (!confirm(`注文 #${order.id} をキャンセルしますか？\nStripe決済も返金されます。`)) return;
                                    const btn = e.currentTarget;
                                    const orig = btn.innerHTML;
                                    btn.disabled = true;
                                    btn.innerHTML = '<svg class="animate-spin h-4 w-4 mx-auto" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>';
                                    try {
                                      const res = await fetch(`${AISHA_API_BASE}/artisan-orders/${order.id}/cancel/`, { method: 'PUT', headers: { 'Content-Type': 'application/json' } });
                                      const data = await res.json();
                                      if (data.success) { alert('キャンセル・返金完了'); loadArtisanOrders(); } else { alert(`失敗: ${data.error}`); }
                                    } catch (e) { alert('キャンセル処理エラー'); }
                                    finally { btn.disabled = false; btn.innerHTML = orig; }
                                  }}
                                  className="bg-red-600/80 hover:bg-red-700 text-white text-xs px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                                >
                                  キャンセル
                                </button>
                              )}
                            </div>
                          </div>

                          {/* お客様情報 */}
                          <div className="bg-gray-900/50 rounded-lg p-3 mb-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-base font-bold text-white">{order.shipping_name}</span>
                              <span className="text-base text-gray-200">{order.shipping_phone}</span>
                            </div>
                            <p className="text-sm text-gray-300">〒{order.shipping_zip} {order.shipping_state}{order.shipping_city}{order.shipping_address1}{order.shipping_address2 ? ` ${order.shipping_address2}` : ''}</p>
                            <p className="text-sm text-gray-400 mt-0.5">{order.shipping_email}</p>
                          </div>

                          {/* 納期・発送・メモ */}
                          <div className="mt-3 pt-3 border-t border-gray-700/50 space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            {/* 納期 */}
                            <div>
                              <label className="text-xs text-gray-400 block mb-1">納期</label>
                              {(editingArtisanDeliveryDate[order.id] ?? order.estimated_delivery_date) && !showDeliveryDatePicker[order.id] ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-orange-400">{editingArtisanDeliveryDate[order.id] ?? order.estimated_delivery_date}</span>
                                  <button onClick={() => { const d = new Date(editingArtisanDeliveryDate[order.id] ?? order.estimated_delivery_date ?? ''); setDeliveryYear(p => ({ ...p, [order.id]: d.getFullYear() })); setDeliveryMonth(p => ({ ...p, [order.id]: d.getMonth() + 1 })); setDeliveryDay(p => ({ ...p, [order.id]: d.getDate() })); setShowDeliveryDatePicker(p => ({ ...p, [order.id]: true })); }} className="text-xs text-indigo-400 hover:text-indigo-300">変更</button>
                                </div>
                              ) : showDeliveryDatePicker[order.id] ? (
                                <div className="flex items-center gap-1 flex-wrap">
                                  <select value={deliveryYear[order.id] || new Date().getFullYear()} onChange={(e) => setDeliveryYear(p => ({ ...p, [order.id]: Number(e.target.value) }))} className="bg-gray-700 text-gray-300 border border-gray-600 rounded px-1 py-1 text-xs">{[...Array(5)].map((_, i) => { const y = new Date().getFullYear() + i; return <option key={y} value={y}>{y}</option>; })}</select>
                                  <select value={deliveryMonth[order.id] || new Date().getMonth() + 1} onChange={(e) => setDeliveryMonth(p => ({ ...p, [order.id]: Number(e.target.value) }))} className="bg-gray-700 text-gray-300 border border-gray-600 rounded px-1 py-1 text-xs">{[...Array(12)].map((_, i) => <option key={i+1} value={i+1}>{i+1}月</option>)}</select>
                                  <select value={deliveryDay[order.id] || new Date().getDate()} onChange={(e) => setDeliveryDay(p => ({ ...p, [order.id]: Number(e.target.value) }))} className="bg-gray-700 text-gray-300 border border-gray-600 rounded px-1 py-1 text-xs">{[...Array(31)].map((_, i) => <option key={i+1} value={i+1}>{i+1}日</option>)}</select>
                                  <button onClick={async () => { const y = deliveryYear[order.id] || new Date().getFullYear(); const m = String(deliveryMonth[order.id] || new Date().getMonth() + 1).padStart(2, '0'); const d = String(deliveryDay[order.id] || new Date().getDate()).padStart(2, '0'); const dateStr = `${y}-${m}-${d}`; if (!window.confirm(`納期を ${dateStr} に設定しますか？`)) return; setEditingArtisanDeliveryDate(p => ({ ...p, [order.id]: dateStr })); setShowDeliveryDatePicker(p => ({ ...p, [order.id]: false })); setSavingArtisanOrder(order.id); try { await updateArtisanOrderStatus(order.id, order.status, undefined, { estimated_delivery_date: dateStr }); showToast('success', '納期を更新しました'); await loadArtisanOrders(); } catch { showToast('error', '更新に失敗しました'); } setSavingArtisanOrder(null); }} className="bg-indigo-500 hover:bg-indigo-600 text-white text-xs px-2 py-1 rounded">OK</button>
                                  <button onClick={() => { setEditingArtisanDeliveryDate(p => ({ ...p, [order.id]: '' })); setShowDeliveryDatePicker(p => ({ ...p, [order.id]: false })); }} className="bg-gray-600 hover:bg-gray-500 text-gray-300 text-xs px-2 py-1 rounded">クリア</button>
                                </div>
                              ) : (
                                <button onClick={() => { const now = new Date(); setDeliveryYear(p => ({ ...p, [order.id]: now.getFullYear() })); setDeliveryMonth(p => ({ ...p, [order.id]: now.getMonth() + 1 })); setDeliveryDay(p => ({ ...p, [order.id]: now.getDate() })); setShowDeliveryDatePicker(p => ({ ...p, [order.id]: true })); }} className="w-full bg-gray-600 hover:bg-gray-500 text-gray-300 text-sm px-3 py-1.5 rounded-lg transition-colors">
                                  納期を入力
                                </button>
                              )}
                            </div>
                            {/* 発送情報 */}
                            <div>
                              <label className="text-xs text-gray-400 block mb-1">{order.with_installation ? '取付情報' : '発送情報'}</label>
                              {(editingArtisanShippedDate[order.id] ?? order.shipped_date) && !showShippingForm[order.id] ? (
                                <div className="flex items-center gap-2 text-sm flex-wrap">
                                  <span className="text-green-400">{editingArtisanShippedDate[order.id] ?? order.shipped_date}</span>
                                  <span className="text-gray-300">{editingArtisanCarrier[order.id] ?? order.shipping_carrier}</span>
                                  <span className="text-gray-400">{editingArtisanTracking[order.id] ?? order.tracking_number}</span>
                                  <button onClick={() => { const d = new Date(editingArtisanShippedDate[order.id] ?? order.shipped_date ?? ''); setShippedYear(p => ({ ...p, [order.id]: d.getFullYear() })); setShippedMonth(p => ({ ...p, [order.id]: d.getMonth() + 1 })); setShippedDay(p => ({ ...p, [order.id]: d.getDate() })); setShowShippingForm(p => ({ ...p, [order.id]: true })); }} className="text-xs text-indigo-400 hover:text-indigo-300">変更</button>
                                </div>
                              ) : showShippingForm[order.id] ? (
                                <div className="space-y-2 bg-gray-900/50 rounded-lg p-2 border border-gray-600">
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-gray-400 w-12 flex-shrink-0">{order.with_installation ? '取付日' : '発送日'}</span>
                                    <select value={shippedYear[order.id] || new Date().getFullYear()} onChange={(e) => setShippedYear(p => ({ ...p, [order.id]: Number(e.target.value) }))} className="bg-gray-700 text-gray-300 border border-gray-600 rounded px-1 py-1 text-xs">{[...Array(5)].map((_, i) => { const y = new Date().getFullYear() + i; return <option key={y} value={y}>{y}</option>; })}</select>
                                    <select value={shippedMonth[order.id] || new Date().getMonth() + 1} onChange={(e) => setShippedMonth(p => ({ ...p, [order.id]: Number(e.target.value) }))} className="bg-gray-700 text-gray-300 border border-gray-600 rounded px-1 py-1 text-xs">{[...Array(12)].map((_, i) => <option key={i+1} value={i+1}>{i+1}月</option>)}</select>
                                    <select value={shippedDay[order.id] || new Date().getDate()} onChange={(e) => setShippedDay(p => ({ ...p, [order.id]: Number(e.target.value) }))} className="bg-gray-700 text-gray-300 border border-gray-600 rounded px-1 py-1 text-xs">{[...Array(31)].map((_, i) => <option key={i+1} value={i+1}>{i+1}日</option>)}</select>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-gray-400 w-12 flex-shrink-0">{order.with_installation ? '取付店' : '運送会社'}</span>
                                    {order.with_installation ? (
                                      <select value={editingArtisanCarrier[order.id] ?? order.shipping_carrier ?? ''} onChange={(e) => setEditingArtisanCarrier(p => ({ ...p, [order.id]: e.target.value }))} className="flex-1 bg-gray-700 text-gray-300 border border-gray-600 rounded px-2 py-1 text-xs"><option value="">未選択</option><option value="アルティザン">アルティザン</option></select>
                                    ) : (
                                      <select value={editingArtisanCarrier[order.id] ?? order.shipping_carrier ?? ''} onChange={(e) => setEditingArtisanCarrier(p => ({ ...p, [order.id]: e.target.value }))} className="flex-1 bg-gray-700 text-gray-300 border border-gray-600 rounded px-2 py-1 text-xs"><option value="">未選択</option><option value="佐川急便">佐川急便</option><option value="ヤマト運輸">ヤマト運輸</option><option value="郵便局">郵便局</option><option value="その他">その他</option></select>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-gray-400 w-12 flex-shrink-0">{order.with_installation ? '予定時間' : '送り状'}</span>
                                    <input type={order.with_installation ? 'time' : 'text'} value={editingArtisanTracking[order.id] ?? order.tracking_number ?? ''} onChange={(e) => setEditingArtisanTracking(p => ({ ...p, [order.id]: e.target.value }))} className="flex-1 bg-gray-700 text-gray-300 border border-gray-600 rounded px-2 py-1 text-xs" placeholder={order.with_installation ? '' : '伝票番号'} />
                                  </div>
                                  <div className="flex gap-1 justify-end">
                                    <button onClick={async () => { const y = shippedYear[order.id] || new Date().getFullYear(); const m = String(shippedMonth[order.id] || new Date().getMonth() + 1).padStart(2, '0'); const d = String(shippedDay[order.id] || new Date().getDate()).padStart(2, '0'); const dateStr = `${y}-${m}-${d}`; const carrier = editingArtisanCarrier[order.id] ?? order.shipping_carrier ?? ''; const tracking = editingArtisanTracking[order.id] ?? order.tracking_number ?? ''; if (!window.confirm(`${order.with_installation ? '取付情報' : '発送情報'}を更新しますか？\n${order.with_installation ? '取付日' : '発送日'}: ${dateStr}\n${carrier} ${tracking}`)) return; setEditingArtisanShippedDate(p => ({ ...p, [order.id]: dateStr })); setShowShippingForm(p => ({ ...p, [order.id]: false })); setSavingArtisanOrder(order.id); try { await updateArtisanOrderStatus(order.id, order.status, undefined, { shipped_date: dateStr, shipping_carrier: carrier, tracking_number: tracking }); showToast('success', '発送情報を更新しました'); await loadArtisanOrders(); } catch { showToast('error', '更新に失敗しました'); } setSavingArtisanOrder(null); }} className="bg-indigo-500 hover:bg-indigo-600 text-white text-xs px-3 py-1 rounded">OK</button>
                                    <button onClick={() => setShowShippingForm(p => ({ ...p, [order.id]: false }))} className="bg-gray-600 hover:bg-gray-500 text-gray-300 text-xs px-3 py-1 rounded">閉じる</button>
                                  </div>
                                </div>
                              ) : (
                                <button onClick={() => { const now = new Date(); setShippedYear(p => ({ ...p, [order.id]: now.getFullYear() })); setShippedMonth(p => ({ ...p, [order.id]: now.getMonth() + 1 })); setShippedDay(p => ({ ...p, [order.id]: now.getDate() })); setShowShippingForm(p => ({ ...p, [order.id]: true })); }} className="w-full bg-gray-600 hover:bg-gray-500 text-gray-300 text-sm px-3 py-1.5 rounded-lg transition-colors">
                                  {order.with_installation ? '取付情報を入力' : '発送情報を入力'}
                                </button>
                              )}
                            </div>
                          </div>
                          {/* 管理メモ + 保存ボタン横並び */}
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editingArtisanOrderNotes[order.id] ?? order.admin_notes ?? ''}
                              onChange={(e) => setEditingArtisanOrderNotes(prev => ({ ...prev, [order.id]: e.target.value }))}
                              className="flex-1 bg-gray-700 text-gray-300 border border-gray-600 rounded-lg px-3 py-1.5 text-sm"
                              placeholder="管理メモ..."
                            />
                            <button
                              onClick={async () => { setSavingArtisanOrder(order.id); try { await updateArtisanOrderStatus(order.id, order.status, editingArtisanOrderNotes[order.id] ?? order.admin_notes ?? ''); showToast('success', 'メモを保存しました'); await loadArtisanOrders(); } catch { showToast('error', '保存に失敗しました'); } setSavingArtisanOrder(null); }}
                              disabled={savingArtisanOrder === order.id}
                              className="bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white font-semibold py-1.5 px-4 rounded-lg text-sm flex-shrink-0"
                            >
                              {savingArtisanOrder === order.id ? '...' : '保存'}
                            </button>
                          </div>
                        </div>
                        </div>

                        {/* ステータスバー（最下部） */}
                        <div className="border-t border-gray-700/40 bg-gray-900/30 px-4">
                          <OrderStatusProgress status={order.status} compact withInstallation={order.with_installation} />
                        </div>
                      </div>
                    ))}
                  </div>
                );
                })()}
              </div>
            )}

            {/* 評価詳細モーダル */}
            {reviewDetailOrder && (
              <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-4" onClick={() => setReviewDetailOrder(null)}>
                <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-bold text-white">到着評価 - 注文 #{reviewDetailOrder.id}</span>
                    <button onClick={() => setReviewDetailOrder(null)} className="text-gray-400 hover:text-white text-xl">✕</button>
                  </div>
                  <div className="text-yellow-400 text-2xl text-center mb-3">
                    {'★'.repeat(reviewDetailOrder.review_rating || 0)}{'☆'.repeat(5 - (reviewDetailOrder.review_rating || 0))}
                  </div>
                  {reviewDetailOrder.review_comment && (
                    <p className="text-sm text-gray-300 mb-3">{reviewDetailOrder.review_comment}</p>
                  )}
                  {reviewDetailOrder.review_image_url && (
                    <img src={reviewDetailOrder.review_image_url} alt="レビュー画像" className="w-full max-h-64 object-contain rounded-lg border border-gray-600 mb-3" />
                  )}
                  {reviewDetailOrder.reviewed_at && (
                    <p className="text-xs text-gray-500 text-right">{new Date(reviewDetailOrder.reviewed_at).toLocaleString('ja-JP')}</p>
                  )}
                </div>
              </div>
            )}

            {/* 確認ボードモーダル */}
            {confirmBoardOrderId && (
              <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-4">
                <div className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-2xl h-[85vh] flex flex-col overflow-hidden shadow-2xl">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 flex-shrink-0">
                    <span className="font-bold text-white">確認ボード - 注文 #{confirmBoardOrderId}</span>
                    <button
                      onClick={() => { setConfirmBoardOrderId(null); loadArtisanOrders(); }}
                      className="text-gray-400 hover:text-white text-xl px-2"
                    >
                      ✕
                    </button>
                  </div>
                  <iframe
                    src={`/aineko/order/${confirmBoardOrderId}?admin=1`}
                    className="flex-1 w-full border-0"
                  />
                </div>
              </div>
            )}
          </div>
        );
      case 'information':
        return <InformationManagement />;
      case 'genMenuCategories':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-indigo-300">
                生成メニューカテゴリ管理
              </h3>
              <button
                onClick={() =>
                  setEditingAdminGenMenuCategory({
                    id: 0,
                    name: '',
                    description: '',
                    orderIndex: 0,
                  })
                }
                className="bg-indigo-500 hover:bg-indigo-600 text-white font-semibold py-2 px-4 rounded-lg flex items-center"
              >
                <PlusCircleIcon className="w-5 h-5 mr-2" /> 新規カテゴリ追加
              </button>
            </div>
            {editingAdminGenMenuCategory && (
              <AdminGenMenuCategoryForm
                item={editingAdminGenMenuCategory}
                onSave={async (newItem) => {
                  const onError = () =>
                    showToast('error', 'カテゴリ保存に失敗しました');
                  let savedItem =
                    newItem.id === 0
                      ? await createCategory(newItem, onError)
                      : await updateCategory(newItem, onError);
                  if (savedItem) {
                    showToast('success', 'カテゴリを保存しました');
                    refreshCategories();
                    setEditingAdminGenMenuCategory(null);
                  }
                }}
                onCancel={() => setEditingAdminGenMenuCategory(null)}
              />
            )}
            <div className="mt-4">
              <p className="text-sm text-gray-400 mb-2">
                ドラッグ&ドロップでカテゴリ順番を変更できます
              </p>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={categories?.map(cat => cat.id.toString()) || []}
                  strategy={verticalListSortingStrategy}
                >
                  <ul className="space-y-3">
                    {categories?.map((cat) => (
                      <SortableCategoryItem
                        key={cat.id}
                        category={cat}
                        onEdit={() => setEditingAdminGenMenuCategory(cat)}
                        onDelete={async () => {
                          if (window.confirm(`${cat.name}を削除しますか？`)) {
                            const success = await deleteCategory(cat.id, () => {
                              showToast('error', 'カテゴリ削除に失敗しました');
                            });
                            if (success) {
                              showToast('success', 'カテゴリを削除しました');
                              refreshCategories();
                            }
                          }
                        }}
                        isDragging={isDragging}
                      />
                    ))}
                  </ul>
                </SortableContext>
                <DragOverlay>
                  {activeId ? (
                    <div className="bg-gray-600 p-4 rounded-md shadow-lg opacity-80">
                      <p className="font-semibold text-indigo-400">
                        {categories?.find(cat => cat.id.toString() === activeId)?.name}
                      </p>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>
          </div>
        );
      case 'engines':
        return (
          <div>
            <h3 className="text-xl font-semibold text-indigo-300 mb-4">
              利用エンジン設定
            </h3>
            <p className="text-gray-400 mb-6">
              アプリケーションで使用するAIエンジンのAPIキーは、サーバー側の環境変数で設定する必要があります。以下は、各エンジンに必要な代表的な環境変数名です。
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-700 p-4 rounded-md">
                <h4 className="text-lg font-medium text-indigo-400 mb-3">
                  画像生成モデル
                </h4>
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-gray-200">
                      Nanobanana Pro (Gemini 3 Pro Image Preview)
                    </p>
                    <p className="text-sm text-gray-300">
                      環境変数:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        GEMINI_API_KEY
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      高品質、2K/4K対応
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      Nanobanana 2 (Gemini 3.1 Flash Image)
                    </p>
                    <p className="text-sm text-gray-300">
                      環境変数:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        GEMINI_API_KEY
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Pro品質+Flash速度 ($0.067/枚)、最大4K対応、編集1枚+参照14枚
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      Imagen3 (Vertex AI)
                    </p>
                    <p className="text-sm text-gray-300">
                      環境変数:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        GOOGLE_SERVICE_ACCOUNT_JSON
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Google最新モデル、高品質
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      Black Forest Labs (FLUX AI)
                    </p>
                    <p className="text-sm text-gray-300">
                      環境変数:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        FLUX_API_KEY
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      高速・高品質
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      FLUX.2 Pro (画像編集)
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        black_forest_labs2_pro
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      高品質編集、編集画像1枚+参照画像最大7枚
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      FLUX.2 Flex (画像編集)
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        black_forest_labs2_flex
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      高速編集、編集画像1枚+参照画像最大9枚
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      Grok Imagen (xAI)
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        grok_imagen
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      マスク不要、編集画像or参照画像最大10枚
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      Seedream 4.5 (BytePlus)
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        seedream45
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      マスク不要、編集画像or参照画像対応（生成・編集両対応）
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      Seedream 5.0 Lite (BytePlus)
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        seedream5
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      マスク不要、編集画像or参照画像対応
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      GPT Image 2 (OpenAI)
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        gpt_image2
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      編集画像or参照画像対応（マスク不要）
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      GPT Image 2.5 Sunburst (OpenAI)
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        gpt_image25_sunburst
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      精度優先。編集画像or参照画像対応（マスク不要・参照最大16枚）
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      GPT Image 2.5 Flare (OpenAI)
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        gpt_image25_flare
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      速度優先。編集画像or参照画像対応（マスク不要・参照最大16枚）
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">HotGen Image v4 Pro</p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        hotgen_image_v4_pro
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      高度編集対応（編集画像+参照画像）
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">Ideogram</p>
                    <p className="text-sm text-gray-300">
                      環境変数:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        IDEOGRAM_API_KEY
                      </code>
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">Midjourney</p>
                    <p className="text-sm text-gray-400">
                      (通常、公式APIは提供されておらず、Discord
                      Bot経由での利用となります。)
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-700 p-4 rounded-md">
                <h4 className="text-lg font-medium text-indigo-400 mb-3">
                  言語モデル
                </h4>
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-gray-200">
                      Google Gemini (Text)
                    </p>
                    <p className="text-sm text-gray-300">
                      環境変数:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        API_KEY
                      </code>
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      OpenAI ChatGPT (GPT-3.5/4)
                    </p>
                    <p className="text-sm text-gray-300">
                      環境変数:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        OPENAI_API_KEY
                      </code>
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      Ideogram (Text-to-Imageも)
                    </p>
                    <p className="text-sm text-gray-300">
                      環境変数:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        IDEOGRAM_API_KEY
                      </code>{' '}
                      (仮)
                    </p>
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-indigo-300 mt-6 mb-3">
                  動画生成モデル
                </h4>
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-gray-200">
                      Seedance 2 (BytePlus)
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        seedance2
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      動画生成（高速版:{' '}
                      <code className="bg-gray-600 px-1 py-0.5 rounded text-indigo-300">
                        seedance2_fast
                      </code>
                      ）
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      Kling 3 Omni
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        kling3_omni
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      動画生成（Renoise・人物はアセット参照）
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      Seedance 2 refs (Sousaku AI)
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        seedance2_sousaku_refs
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      動画生成・参照画像最大9枚（人物は画像参照）
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      Seedance 2 i2v (Sousaku AI)
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        seedance2_sousaku_i2v
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      動画生成・スタートフレーム必須/エンドフレーム任意
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      Seedance 2.5 (Renoise)
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        seedance25
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      動画生成（Renoise・人物はアセット参照）
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      Seedance 2.5 refs (Sousaku AI)
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        seedance25_sousaku_refs
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      動画生成・参照画像最大9枚（人物は画像参照）
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      Seedance 2.5 i2v (Sousaku AI)
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        seedance25_sousaku_i2v
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      動画生成・スタートフレーム必須/エンドフレーム任意（人物は画像参照）
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      HotGen Video v5 (Sousaku AI)
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        hotgen_video_v5
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      動画生成・参照画像最大9枚（人物は画像参照）
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      HotGen Video v4 Pro (Sousaku AI)
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        hotgen_video_v4_pro
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      動画生成・スタートフレーム必須/エンドフレーム任意
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-200">
                      MiniMax H3 Max Turbo (fal)
                    </p>
                    <p className="text-sm text-gray-300">
                      エンジン名:{' '}
                      <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                        minimax_h3_max_turbo
                      </code>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      動画生成・スタート/エンドフレームは任意（画像なしはtext-to-video）。人物参照なし・5〜15秒
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-8">
              注意:
              APIキーはUI上では設定・表示できません。サーバー環境で安全に管理してください。上記は代表的な例であり、使用するSDKやライブラリによって異なる場合があります。
            </p>
          </div>
        );
      case 'genMenus':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-indigo-300">
                生成メニュー管理
              </h3>
              <button
                onClick={() =>
                  setEditingGenMenu({
                    id: 0,
                    name: '',
                    engine: 'nanobanana2',
                    prompt: '',
                    credit: 10,
                    promptVariables: [],
                    sampleInputImageUrl: '',
                    sampleResultImageUrl: '',
                  } as AdminGenerationMenuItem)
                }
                className="bg-indigo-500 hover:bg-indigo-600 text-white font-semibold py-2 px-4 rounded-lg flex items-center"
              >
                <PlusCircleIcon className="w-5 h-5 mr-2" /> 新規メニュー追加
              </button>
            </div>
            {/* カテゴリ絞り込み */}
            <div className="mb-4 space-y-4">
              {/* 非表示フィルター */}
              <div className="flex items-center justify-between bg-gray-700/50 rounded-lg px-3 py-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filterHiddenOnly}
                    onChange={(e) => setFilterHiddenOnly(e.target.checked)}
                    className="w-4 h-4 text-yellow-500 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500"
                  />
                  <span className="ml-2 text-sm text-gray-300 flex items-center">
                    <EyeSlashIcon className="w-4 h-4 mr-1 text-yellow-400" />
                    非表示フィルタ（ONで非表示メニューのみ表示）
                  </span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  生成メニューのカテゴリ
                </label>
                <select
                  value={selectedMenuCategory}
                  onChange={(e) => setSelectedMenuCategory(e.target.value)}
                  className="bg-gray-700 border border-gray-600 text-white text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2.5"
                >
                  <option value="">指定なし（全て表示）</option>
                  {categories?.map((category) => (
                    <option key={category.id} value={category.id.toString()}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* グッズ作成許可フィルター */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  アイテム作成許可フィルター
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filterSuzuriEnabled}
                      onChange={(e) => setFilterSuzuriEnabled(e.target.checked)}
                      className="w-4 h-4 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-300">SUZURI許可のみ</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filterPrintfulEnabled}
                      onChange={(e) => setFilterPrintfulEnabled(e.target.checked)}
                      className="w-4 h-4 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-300">Printful許可のみ</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filterArtisanEnabled}
                      onChange={(e) => setFilterArtisanEnabled(e.target.checked)}
                      className="w-4 h-4 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-300">Artisan許可のみ</span>
                  </label>
                </div>
              </div>
            </div>
            {editingGenMenu && (
              <AdminGenMenuForm
                item={editingGenMenu}
                categories={categories ?? []}
                onSave={async (newItem) => {
                  const requiredPromptVariableLength =
                    newItem.promptVariables?.length ?? 0;
                  const matches = newItem.prompt.match(/{{.*?}}/g);
                  const inputPromptVariableLength = matches
                    ? matches.length
                    : 0;
                  if (
                    inputPromptVariableLength !== requiredPromptVariableLength
                  ) {
                    showToast(
                      'error',
                      'ユーザー追加入力項目数がプロンプトと一致しません。',
                    );
                    return;
                  }
                  const onError = () =>
                    showToast('error', 'メニュー保存に失敗しました');
                  let savedItem =
                    newItem.id === 0
                      ? await createMenu(newItem, onError)
                      : await updateMenu(newItem, onError);
                  if (savedItem) {
                    showToast('success', 'メニューを保存しました');
                    refreshMenus();
                    setEditingGenMenu(null);
                  }
                }}
                onCancel={() => setEditingGenMenu(null)}
              />
            )}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={menus
                  ?.filter((menu) => {
                    // 非表示フィルター（OFF: 表示中のみ / ON: 非表示のみ）
                    const isHidden = !!(menu.isHidden ?? menu.is_hidden);
                    if (filterHiddenOnly ? !isHidden : isHidden) {
                      return false;
                    }
                    // カテゴリ絞り込み
                    if (selectedMenuCategory && menu.categoryId?.toString() !== selectedMenuCategory) {
                      return false;
                    }
                    // グッズ作成許可フィルター
                    const goodsOrder = menu.goods_order || menu.goodsOrder || [];
                    if (filterSuzuriEnabled && !goodsOrder.includes('suzuri')) {
                      return false;
                    }
                    if (filterPrintfulEnabled && !goodsOrder.includes('printful')) {
                      return false;
                    }
                    if (filterArtisanEnabled && !goodsOrder.includes('artisan')) {
                      return false;
                    }
                    return true;
                  })
                  .map((menu) => menu.id.toString()) || []}
                strategy={verticalListSortingStrategy}
              >
                <ul className="space-y-3 mt-4">
                  {menus
                    ?.filter((menu) => {
                      // 非表示フィルター（OFF: 表示中のみ / ON: 非表示のみ）
                      const isHidden = !!(menu.isHidden ?? menu.is_hidden);
                      if (filterHiddenOnly ? !isHidden : isHidden) {
                        return false;
                      }
                      // カテゴリ絞り込み
                      if (selectedMenuCategory && menu.categoryId?.toString() !== selectedMenuCategory) {
                        return false;
                      }
                      // グッズ作成許可フィルター
                      const goodsOrder = menu.goods_order || menu.goodsOrder || [];
                      if (filterSuzuriEnabled && !goodsOrder.includes('suzuri')) {
                        return false;
                      }
                      if (filterPrintfulEnabled && !goodsOrder.includes('printful')) {
                        return false;
                      }
                      return true;
                    })
                    .map((menu) => {
                    const category = categories?.find(
                      (c) => c.id === menu.categoryId,
                    );
                    return (
                      <SortableMenuItem
                        key={menu.id}
                        menu={menu}
                        category={category}
                        onEdit={() => setEditingGenMenu(menu)}
                        onDelete={() => {
                          if (window.confirm(`${menu.name}を削除しますか？`)) {
                            deleteMenu(menu.id, () => {
                              showToast('error', 'メニュー削除に失敗しました');
                            }).then((success) => {
                              if (success) {
                                showToast('success', 'メニューを削除しました');
                                refreshMenus();
                              }
                            });
                          }
                        }}
                        onCopyApiCode={() => copyApiSampleCode(menu)}
                        onToggleHidden={async () => {
                          const newHidden = !(menu.isHidden ?? menu.is_hidden);
                          const updated = await updateMenu(
                            { ...menu, isHidden: newHidden, is_hidden: newHidden },
                            () => {
                              showToast('error', '非表示設定の更新に失敗しました');
                            },
                          );
                          if (updated) {
                            showToast(
                              'success',
                              newHidden
                                ? `「${menu.name}」を非表示にしました（直接実行は可能）`
                                : `「${menu.name}」を表示に戻しました`,
                            );
                            refreshMenus();
                          }
                        }}
                        isDragging={activeId === menu.id.toString()}
                      />
                    );
                  })}
                </ul>
              </SortableContext>
            </DndContext>
          </div>
        );
      case 'goods':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-indigo-300">
                アイテム管理
              </h3>
              <div className="flex space-x-2">
                {selectedVendor === 'suzuri' && (
                  <button
                    onClick={handleSyncSuzuriItems}
                    disabled={isSyncingGoods}
                    className="bg-green-500 hover:bg-green-600 disabled:bg-gray-500 text-white font-semibold py-2 px-4 rounded-lg flex items-center"
                  >
                    {isSyncingGoods ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        同期中...
                      </>
                    ) : (
                      <>
                        <LinkIcon className="w-5 h-5 mr-2" />
                        SUZURI同期
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            {/* ベンダー切り替えタブ */}
            <div className="border-b border-gray-700 mb-4">
              <nav className="-mb-px flex space-x-8" aria-label="Vendor tabs">
                <button
                  onClick={() => setSelectedVendor('suzuri')}
                  className={`
                    py-2 px-1 border-b-2 font-medium text-sm transition-colors
                    ${selectedVendor === 'suzuri'
                      ? 'border-indigo-500 text-indigo-400'
                      : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-300'
                    }
                  `}
                >
                  SUZURI
                </button>
                <button
                  onClick={() => setSelectedVendor('printful')}
                  className={`
                    py-2 px-1 border-b-2 font-medium text-sm transition-colors
                    ${selectedVendor === 'printful'
                      ? 'border-indigo-500 text-indigo-400'
                      : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-300'
                    }
                  `}
                >
                  Printful
                </button>
                <button
                  onClick={() => setSelectedVendor('prodigi')}
                  className={`
                    py-2 px-1 border-b-2 font-medium text-sm transition-colors
                    ${selectedVendor === 'prodigi'
                      ? 'border-indigo-500 text-indigo-400'
                      : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-300'
                    }
                  `}
                >
                  Prodigi
                </button>
                <button
                  onClick={() => setSelectedVendor('artisan')}
                  className={`
                    py-2 px-1 border-b-2 font-medium text-sm transition-colors
                    ${selectedVendor === 'artisan'
                      ? 'border-indigo-500 text-indigo-400'
                      : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-300'
                    }
                  `}
                >
                  Artisan
                </button>
              </nav>
            </div>
            {showArtisanForm && (
              <ArtisanProductForm
                product={editingArtisanProduct}
                onSave={async (data) => {
                  try {
                    if (editingArtisanProduct) {
                      await updateArtisanProductApi(editingArtisanProduct.id, data as any);
                      showToast('success', 'サービス品を更新しました');
                    } else {
                      await createArtisanProduct(data as any);
                      showToast('success', 'サービス品を作成しました');
                    }
                    setShowArtisanForm(false);
                    setEditingArtisanProduct(null);
                    await loadArtisanProducts();
                  } catch (error) {
                    showToast('error', editingArtisanProduct ? '更新に失敗しました' : '作成に失敗しました');
                  }
                }}
                onCancel={() => {
                  setShowArtisanForm(false);
                  setEditingArtisanProduct(null);
                }}
              />
            )}
            {selectedVendor === 'prodigi' ? (
              <ProdigiPanel apiBase={AISHA_API_BASE} showToast={showToast} />
            ) : selectedVendor === 'artisan' ? (
              /* Artisanタブのコンテンツ */
              <div>
                <div className="mt-6 mb-4 flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-gray-300">
                    Artisan サービス品管理
                  </h4>
                  <button
                    onClick={() => {
                      setEditingArtisanProduct(null);
                      setShowArtisanForm(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
                  >
                    <PlusCircleIcon className="w-5 h-5" />
                    新規作成
                  </button>
                </div>
                {isLoadingArtisan ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
                    <p className="text-gray-400 mt-4">読み込み中...</p>
                  </div>
                ) : artisanProducts.length === 0 ? (
                  <div className="text-center py-12">
                    <CubeIcon className="w-16 h-16 mx-auto text-gray-500 mb-4" />
                    <p className="text-gray-400">Artisan サービス品はまだ登録されていません</p>
                    <p className="text-sm text-gray-500 mt-2">
                      ラッピング・ステッカーなどの自社対応商品を管理します
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {artisanProducts.map((product) => (
                      <div
                        key={product.id}
                        className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-gray-600 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                              <h5 className="text-base font-medium text-gray-200 truncate">
                                {product.product_name}
                              </h5>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  product.is_public
                                    ? 'bg-green-900/50 text-green-400 border border-green-700'
                                    : 'bg-gray-700 text-gray-400 border border-gray-600'
                                }`}
                              >
                                {product.is_public ? '公開' : '非公開'}
                              </span>
                            </div>
                            {(product.compatible_maker || product.compatible_car || product.compatible_model) && (
                              <p className="text-sm text-gray-400 mt-1">
                                {[product.compatible_maker, product.compatible_car, product.compatible_model]
                                  .filter(Boolean)
                                  .join(' / ')}
                              </p>
                            )}
                            <p className="text-sm text-indigo-400 font-medium mt-1">
                              ¥{product.product_price.toLocaleString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                            <button
                              onClick={() => {
                                setEditingArtisanProduct(product);
                                setShowArtisanForm(true);
                              }}
                              className="p-2 text-gray-400 hover:text-indigo-400 hover:bg-gray-700 rounded-lg transition-colors"
                              title="編集"
                            >
                              <PencilAltIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteArtisanProduct(product.id, product.product_name)}
                              className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg transition-colors"
                              title="削除"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : selectedVendor === 'suzuri' ? (
              <>
                {editingGoodsItem && (
                  <AdminGoodsManagementForm
                    item={editingGoodsItem}
                    onSave={(data) => handleUpdateGoodsItem(editingGoodsItem.id, data)}
                    onCancel={() => setEditingGoodsItem(null)}
                  />
                )}
                <div className="mt-4">
              <div className="flex items-center space-x-4 mb-4">
                <label className="flex items-center space-x-2 text-gray-300">
                  <input
                    type="checkbox"
                    checked={showPublicOnly}
                    onChange={(e) => {
                      setShowPublicOnly(e.target.checked);
                      // 状態更新後に少し待ってからAPI呼び出し
                      setTimeout(() => {
                        loadGoodsItems();
                      }, 0);
                    }}
                    className="rounded border-gray-600 bg-gray-700 text-indigo-500 focus:ring-indigo-500"
                  />
                  <span>公開のみ表示</span>
                </label>
                <label className="flex items-center space-x-2 text-gray-300">
                  <input
                    type="checkbox"
                    checked={showSubMaterialsOnly}
                    onChange={(e) => {
                      setShowSubMaterialsOnly(e.target.checked);
                      // 状態更新後に少し待ってからAPI呼び出し
                      setTimeout(() => {
                        loadGoodsItems();
                      }, 0);
                    }}
                    className="rounded border-gray-600 bg-gray-700 text-indigo-500 focus:ring-indigo-500"
                  />
                  <span>sub_materials必要のみ表示</span>
                </label>
              </div>
              {isLoadingGoods ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
                  <span className="ml-2 text-gray-400">アイテム一覧を読み込み中...</span>
                </div>
              ) : goodsItems.length === 0 ? (
                <div className="text-center py-8">
                  <CubeIcon className="w-16 h-16 mx-auto text-gray-500 mb-4" />
                  <p className="text-gray-400">アイテムが登録されていません</p>
                  <p className="text-sm text-gray-500 mt-2">
                    「SUZURI同期」ボタンをクリックしてアイテムを同期してください
                  </p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={async (event) => {
                    const { active, over } = event;
                    setActiveId(null);
                    setIsDragging(false);
                    if (!over || active.id === over.id) return;
                    // 公開のみ表示の場合も含め、表示中リスト順でdisplay_orderを再割り当て
                    const filtered = goodsItems.filter(item => !showPublicOnly || item.is_public);
                    const oldIndex = filtered.findIndex(item => item.id.toString() === active.id);
                    const newIndex = filtered.findIndex(item => item.id.toString() === over.id);
                    if (oldIndex !== -1 && newIndex !== -1) {
                      const newList = arrayMove(filtered, oldIndex, newIndex);
                      // display_orderを再割り当て
                      await Promise.all(newList.map((item, idx) => updateGoodsManagement(item.id, { display_order: idx })));
                      // 再取得
                      await loadGoodsItems();
                      showToast('success', '表示順序を更新しました');
                    }
                  }}
                >
                  <SortableContext
                    items={goodsItems.filter(item => !showPublicOnly || item.is_public).map(item => item.id.toString())}
                    strategy={verticalListSortingStrategy}
                  >
                    <ul className="space-y-3">
                      {goodsItems.filter(item => !showPublicOnly || item.is_public).map((item) => (
                        <SortableGoodsItem
                          key={item.id}
                          item={item}
                          onEdit={() => {
                            setEditingGoodsItem(item);
                          }}
                          isDragging={activeId === item.id.toString()}
                        />
                      ))}
                    </ul>
                  </SortableContext>
                  <DragOverlay>
                    {activeId ? (
                      <div className="bg-gray-700 p-4 rounded-md shadow flex justify-between items-start opacity-80">
                        <div className="flex items-start space-x-4">
                          {goodsItems.find(item => item.id.toString() === activeId)?.icon_url ? (
                            <img
                              src={goodsItems.find(item => item.id.toString() === activeId)?.icon_url ?? undefined}
                              alt={goodsItems.find(item => item.id.toString() === activeId)?.display_name}
                              className="w-20 h-20 object-cover rounded-md border border-gray-600 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-20 h-20 bg-gray-600 rounded-md flex items-center justify-center text-gray-500 flex-shrink-0">
                              <CubeIcon className="w-10 h-10" />
                            </div>
                          )}
                          <div className="flex-grow">
                            <div className="flex items-center space-x-2 mb-1">
                              <p className="font-semibold text-indigo-400">
                                {goodsItems.find(item => item.id.toString() === activeId)?.display_name}
                              </p>
                              <span className={`px-2 py-1 text-xs rounded-full ${goodsItems.find(item => item.id.toString() === activeId)?.is_public ? 'bg-green-600 text-green-100' : 'bg-gray-600 text-gray-300'}`}>
                                {goodsItems.find(item => item.id.toString() === activeId)?.is_public ? '公開' : '非公開'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              )}
            </div>
              </>
            ) : (
              /* Printfulタブのコンテンツ */
              <div>
                <div className="mt-6 flex justify-between items-center mb-4">
                  <div className="flex items-center space-x-4">
                    <h4 className="text-lg font-semibold text-gray-300">
                      登録済み商品 ({printfulGoods.length}件)
                    </h4>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={showPublicOnly}
                          onChange={(e) => {
                            setShowPublicOnly(e.target.checked);
                            // 状態更新後に少し待ってからAPI呼び出し
                            setTimeout(() => {
                              loadPrintfulGoods();
                            }, 0);
                          }}
                          className="form-checkbox h-4 w-4 text-indigo-500"
                        />
                        <span className="text-sm text-gray-300">公開のみ</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={showJapanShippable}
                          onChange={(e) => setShowJapanShippable(e.target.checked)}
                          className="form-checkbox h-4 w-4 text-indigo-500"
                        />
                        <span className="text-sm text-gray-300">日本発送品</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={showOverseas}
                          onChange={(e) => setShowOverseas(e.target.checked)}
                          className="form-checkbox h-4 w-4 text-indigo-500"
                        />
                        <span className="text-sm text-gray-300">海外直送品</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={showNonJapanShippable}
                          onChange={(e) => setShowNonJapanShippable(e.target.checked)}
                          className="form-checkbox h-4 w-4 text-indigo-500"
                        />
                        <span className="text-sm text-gray-300">日本発送不可</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={showDiscontinued}
                          onChange={(e) => setShowDiscontinued(e.target.checked)}
                          className="form-checkbox h-4 w-4 text-indigo-500"
                        />
                        <span className="text-sm text-gray-300">廃版品</span>
                      </label>
                    </div>
                  </div>
                  <button
                    onClick={handleSyncPrintfulProducts}
                    disabled={isSyncingPrintful}
                    className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-500 text-white font-semibold py-2 px-4 rounded-lg flex items-center"
                  >
                    {isSyncingPrintful ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        同期中...
                      </>
                    ) : (
                      <>
                        <LinkIcon className="w-5 h-5 mr-2" />
                        カタログ同期
                      </>
                    )}
                  </button>
                  {/* 日本商品チェックボタン */}
                  <button
                    onClick={handleJapanShippingCheck}
                    disabled={isCheckingJapan}
                    className="bg-green-500 hover:bg-green-600 disabled:bg-gray-500 text-white font-semibold py-2 px-4 rounded-lg flex items-center ml-2"
                  >
                    {isCheckingJapan ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        チェック中...
                      </>
                    ) : (
                      <>
                        <CheckIcon className="w-5 h-5 mr-2" />
                        日本商品チェック
                      </>
                    )}
                  </button>
                </div>
                {isLoadingPrintful ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
                    <span className="ml-2 text-gray-400">Printful商品を読み込み中...</span>
                  </div>
                ) : printfulGoods.length === 0 ? (
                  <div className="text-center py-8">
                    <CubeIcon className="w-16 h-16 mx-auto text-gray-500 mb-4" />
                    <p className="text-gray-400">日本発送可能なPrintful商品がありません</p>
                    <p className="text-sm text-gray-500 mt-2">
                      廃版でない、日本発送可能な商品のみ表示されます
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* GoodsManagementから読み込んだPrintful商品を表示 */}
                    {printfulGoods.map((goods) => (
                      <div key={goods.id} className="bg-gray-700 rounded-lg p-4">
                        <div className="flex items-start space-x-4">
                          {(productMockups[goods.id] || goods.mockup_url || goods.sample_image_url || goods.icon_url) && (
                            <img
                              src={productMockups[goods.id] || goods.mockup_url || goods.sample_image_url || goods.icon_url}
                              alt={goods.display_name}
                              className="w-20 h-20 object-cover rounded-md"
                            />
                          )}
                          <div className="flex-grow">
                            <h4 className="text-lg font-semibold text-indigo-300">
                              {goods.display_name}
                            </h4>
                            <div className="flex items-center space-x-3 mt-1">
                              <p className="text-sm text-gray-400">
                                {goods.item_type} / DB ID: {goods.id} / カタログID: {goods.vendor_item_id}
                              </p>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                goods.is_public 
                                  ? 'bg-green-600 text-white' 
                                  : 'bg-gray-600 text-gray-300'
                              }`}>
                                {goods.is_public ? '公開' : '非公開'}
                              </span>
                            </div>
                            {/* 価格情報 */}
                            <div className="mt-3 flex items-center space-x-4">
                              <div className="flex items-center space-x-2">
                                <span className="text-indigo-300 font-semibold text-lg">
                                  {goods.is_public ? (
                                    goods.final_price > 0 ? `¥${goods.final_price}～` : '－'
                                  ) : (
                                    '－'
                                  )}
                                </span>
                              </div>
                            </div>
                            {/* 公開/非公開ボタンと確認ボタン */}
                            <div className="mt-3 flex space-x-2">
                              <button
                                onClick={() => handleTogglePrintfulVisibility(goods.id, goods.is_public)}
                                className={`px-3 py-1 rounded text-sm font-medium ${
                                  goods.is_public
                                    ? 'bg-green-600 hover:bg-green-700 text-white'
                                    : 'bg-gray-600 hover:bg-gray-500 text-gray-300'
                                }`}
                              >
                                {goods.is_public ? '公開中' : '非公開'}
                              </button>
                              <button
                                onClick={() => handleOpenPrintfulEditModal(goods)}
                                className="px-3 py-1 rounded text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white"
                              >
                                確認
                              </button>
                            </div>
                          </div>
                          {/* 画像アップロードエリア（右側） */}
                          <div className="w-48 flex-shrink-0">
                            {productImages[goods.id] || goods.mockup_image_url ? (
                              // 画像がアップロード済みの場合
                              <div className="relative">
                                <img
                                  src={productImages[goods.id] || goods.mockup_image_url}
                                  alt="モックアップ画像"
                                  className="w-full h-32 object-cover rounded-lg"
                                />
                                <button
                                  onClick={() => {
                                    setProductImages(prev => {
                                      const newImages = { ...prev };
                                      delete newImages[goods.id];
                                      return newImages;
                                    });
                                  }}
                                  className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white p-1 rounded-full"
                                >
                                  <XMarkIcon className="w-4 h-4" />
                                </button>
                                <button 
                                  onClick={() => handleGenerateMockup(goods.id)}
                                  disabled={generatingMockup === goods.id}
                                  className={`mt-2 w-full py-1 px-3 rounded text-sm font-medium ${
                                    generatingMockup === goods.id
                                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                      : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                  }`}
                                >
                                  {generatingMockup === goods.id ? '生成中...' : 'モック作成'}
                                </button>
                                {productMockups[goods.id] && (
                                  <button 
                                    onClick={() => {
                                      // 購入モーダルを開く
                                      console.log('Opening purchase modal for goods:', goods);
                                      console.log('vendor_item_id:', goods.vendor_item_id);
                                      console.log('display_name:', goods.display_name);
                                      const modalData = {
                                        userId: 'admin-test',
                                        imageUrl: productImages[goods.id] || '',
                                        mockupUrl: productMockups[goods.id] || goods.mockup_url || '',
                                        catalogId: String(goods.vendor_item_id || ''),
                                        goodsName: goods.display_name || '商品'
                                      };
                                      console.log('Modal data:', modalData);
                                      console.log('Image URL being sent to Printful:', modalData.imageUrl);
                                      if (modalData.imageUrl) {
                                        console.log('Image URL validation:');
                                        console.log('- Has Firebase domain:', modalData.imageUrl.includes('firebasestorage.googleapis.com'));
                                        console.log('- Has token:', modalData.imageUrl.includes('token='));
                                        console.log('- Has alt=media:', modalData.imageUrl.includes('alt=media'));
                                      } else {
                                        console.warn('⚠️ No image URL available for this product');
                                      }
                                      setPurchaseModalData(modalData);
                                      setShowPurchaseModal(true);
                                    }}
                                    className="mt-1 w-full py-1 px-3 rounded text-sm font-medium bg-green-600 hover:bg-green-700 text-white"
                                  >
                                    サンプル商品生成
                                  </button>
                                )}
                              </div>
                            ) : (
                              // ドラッグ&ドロップエリア
                              <label 
                                className="relative block"
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  e.currentTarget.classList.add('border-indigo-400', 'bg-indigo-900/20');
                                }}
                                onDragLeave={(e) => {
                                  e.currentTarget.classList.remove('border-indigo-400', 'bg-indigo-900/20');
                                }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  e.currentTarget.classList.remove('border-indigo-400', 'bg-indigo-900/20');
                                  const file = e.dataTransfer.files[0];
                                  if (file && file.type.startsWith('image/')) {
                                    handleImageUploadForProduct(file, goods.id);
                                  }
                                }}
                              >
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      handleImageUploadForProduct(file, goods.id);
                                    }
                                  }}
                                />
                                <div className="border-2 border-dashed border-gray-500 rounded-lg p-4 text-center hover:border-indigo-400 transition-colors cursor-pointer">
                                  {uploadingImageForProduct === goods.id ? (
                                    <>
                                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400 mx-auto mb-2"></div>
                                      <p className="text-xs text-gray-400">アップロード中...</p>
                                    </>
                                  ) : (
                                    <>
                                      <PhotoIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                      <p className="text-xs text-gray-300">
                                        ドラッグ&ドロップ
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        またはクリック
                                      </p>
                                    </>
                                  )}
                                </div>
                              </label>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      case 'chargeManagement':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-indigo-300">
                チャージオプション管理
              </h3>
              <button
                onClick={() =>
                  setEditingChargeOption({
                    id: 0,
                    name: '',
                    priceYen: 0,
                    creditsAwarded: 0,
                    displayInfo: '',
                    isActive: true,
                    creditsBonus: 0,
                  })
                }
                className="bg-indigo-500 hover:bg-indigo-600 text-white font-semibold py-2 px-4 rounded-lg flex items-center"
              >
                <PlusCircleIcon className="w-5 h-5 mr-2" />{' '}
                新規チャージオプション追加
              </button>
            </div>
            {editingChargeOption && (
              <AdminChargeOptionForm
                item={editingChargeOption}
                onSave={async (newItem) => {
                  const onError = () =>
                    showToast('error', 'チャージオプション保存に失敗しました');
                  let savedItem =
                    newItem.id === 0
                      ? await createChargeOption(newItem, onError)
                      : await updateChargeOption(newItem, onError);
                  if (savedItem) {
                    showToast('success', 'チャージオプションを保存しました');
                    refreshChargeOptions();
                    setEditingChargeOption(null);
                  }
                }}
                onCancel={() => setEditingChargeOption(null)}
              />
            )}
            <ul className="space-y-3 mt-4">
              {chargeOptions?.map((option) => (
                <li
                  key={option.id}
                  className={`bg-gray-700 p-4 rounded-md shadow flex justify-between items-center ${!option.isActive ? 'opacity-60' : ''}`}
                >
                  <div>
                    <p className="font-semibold text-indigo-400">
                      {option.name}{' '}
                      {option.isActive ? (
                        <span className="text-xs text-green-400">(有効)</span>
                      ) : (
                        <span className="text-xs text-gray-500">(無効)</span>
                      )}
                    </p>
                    <p className="text-sm text-gray-300">
                      価格: {option.priceYen}円
                    </p>
                    <p className="text-sm text-gray-300">
                      付与クレジット: {option.creditsAwarded} (表示:{' '}
                      {option.displayInfo})
                    </p>
                    {option.creditsBonus && option.creditsBonus > 0 ? (
                      <p className="text-xs text-yellow-400">
                        ボーナス: +{option.creditsBonus} クレジット
                      </p>
                    ) : null}
                  </div>
                  <div className="space-x-2">
                    <button
                      onClick={() => setEditingChargeOption(option)}
                      className="p-2 text-gray-400 hover:text-indigo-300"
                      aria-label={`Edit ${option.name}`}
                    >
                      <PencilAltIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm(`${option.name}を削除しますか？`)) {
                          const success = await deleteChargeOption(
                            option.id,
                            () => {
                              showToast(
                                'error',
                                'チャージオプション削除に失敗しました',
                              );
                            },
                          );
                          if (success) {
                            showToast(
                              'success',
                              'チャージオプションを削除しました',
                            );
                            refreshChargeOptions();
                          }
                        }
                      }}
                      className="p-2 text-gray-400 hover:text-red-400"
                      aria-label={`Delete ${option.name}`}
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      case 'suzuriApi':
        return (
          <div>
            <h3 className="text-xl font-semibold text-indigo-300 mb-4">
              SUZURI API 設定
            </h3>
            <p className="text-gray-400 mb-2">
              SUZURIとの連携（グッズ作成など）には、SUZURI
              APIアクセストークンが必要です。
              このアクセストークンは、サーバー側の環境変数で設定してください。
            </p>
            <div className="p-4 bg-gray-700 rounded-md mt-4">
              <h4 className="text-lg font-medium text-indigo-400">
                アクセストークン
              </h4>
              <p className="text-sm text-gray-300 mt-1">
                必要な環境変数名:{' '}
                <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                  SUZURI_ACCESS_TOKEN
                </code>
              </p>
            </div>
            <p className="text-gray-400 mt-4">
              APIドキュメントはこちらを参照してください:{' '}
              <a
                href="https://suzuri.jp/developer/documentation/v1"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-400 hover:text-indigo-300 underline"
              >
                SUZURI API Documentation
              </a>
            </p>
            <p className="text-xs text-gray-500 mt-6">
              注意:
              APIアクセストークンはUI上では設定できません。サーバー環境で安全に管理してください。
            </p>
          </div>
        );
      case 'stripeApi':
        return (
          <div>
            <h3 className="text-xl font-semibold text-indigo-300 mb-4">
              Stripe API 設定
            </h3>
            <p className="text-gray-400 mb-2">
              Stripe決済代行サービスとの連携には、Stripe APIキーが必要です。
              これらのキーは、サーバー側の環境変数で設定してください。
            </p>
            <div className="space-y-3 mt-4">
              <div className="p-4 bg-gray-700 rounded-md">
                <h4 className="text-lg font-medium text-indigo-400">
                  公開可能キー (Publishable Key)
                </h4>
                <p className="text-sm text-gray-300 mt-1">
                  必要な環境変数名:{' '}
                  <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                    STRIPE_PUBLISHABLE_KEY
                  </code>
                </p>
              </div>
              <div className="p-4 bg-gray-700 rounded-md">
                <h4 className="text-lg font-medium text-indigo-400">
                  シークレットキー (Secret Key)
                </h4>
                <p className="text-sm text-gray-300 mt-1">
                  必要な環境変数名:{' '}
                  <code className="bg-gray-600 px-1.5 py-0.5 rounded text-indigo-300">
                    STRIPE_SECRET_KEY
                  </code>
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-6">
              注意:
              APIキーはUI上では設定できません。サーバー環境で安全に管理してください。
            </p>
          </div>
        );
      case 'users':
        return (
          <div>
            <h3 className="text-xl font-semibold text-indigo-300 mb-6">
              ユーザー管理
            </h3>
            {/* 統計情報セクション */}
            {userStatistics && (
              <div className="bg-gray-800 p-6 rounded-lg mb-6">
                <h4 className="text-lg font-medium text-white mb-4">ユーザー統計</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-indigo-400">{userStatistics.total_users}</div>
                    <div className="text-gray-400 text-sm">総ユーザー数</div>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-400">{userStatistics.mygarage_users}</div>
                    <div className="text-gray-400 text-sm">MyGarage認証</div>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-400">{userStatistics.phone_users}</div>
                    <div className="text-gray-400 text-sm">電話番号認証</div>
                  </div>
                </div>
              </div>
            )}
            {/* ユーザー検索セクション */}
            <div className="bg-gray-800 p-6 rounded-lg mb-6">
              <h4 className="text-lg font-medium text-white mb-4">ユーザー検索</h4>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    placeholder="ユーザー名またはUIDで検索"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onKeyPress={(e) => e.key === 'Enter' && handleUserSearch()}
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={userSearchLimit}
                    onChange={(e) => setUserSearchLimit(parseInt(e.target.value))}
                    className="px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value={10}>10件</option>
                    <option value={20}>20件</option>
                    <option value={50}>50件</option>
                    <option value={100}>100件</option>
                  </select>
                  <button
                    onClick={handleUserSearch}
                    disabled={isLoadingUsers}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoadingUsers ? '検索中...' : '検索'}
                  </button>
                  <button
                    onClick={handleUserSearchReset}
                    disabled={isLoadingUsers}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    リセット
                  </button>
                </div>
              </div>
            </div>
            {/* ユーザー一覧セクション */}
            <div className="bg-gray-800 p-6 rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-medium text-white">
                  ユーザー一覧 ({allUsers.length}件)
                  {userSearchQuery && <span className="text-gray-400 ml-2">- "{userSearchQuery}"で検索</span>}
                </h4>
                <button
                  onClick={() => loadAllUsers('', 20)}
                  disabled={isLoadingUsers}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingUsers ? '読み込み中...' : '最新20件'}
                </button>
              </div>
              {isLoadingUsers ? (
                <div className="text-center py-8">
                  <div className="text-gray-400">ユーザー一覧を読み込み中...</div>
                </div>
              ) : allUsers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-600">
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">名前</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">登録日</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">最終ログイン</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">生成画像数</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">ユーザータイプ</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">クレジット</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allUsers.map((user) => (
                        <tr key={user.firebase_uid} className="border-b border-gray-700 hover:bg-gray-700">
                          <td className="py-3 px-4">
                            <div className="font-medium text-white">{user.nickname}</div>
                            <div className="text-xs text-gray-400">{user.firebase_uid}</div>
                          </td>
                          <td className="py-3 px-4 text-gray-300">
                            {user.created_at ? new Date(user.created_at).toLocaleDateString('ja-JP') : '不明'}
                          </td>
                          <td className="py-3 px-4 text-gray-300">
                            {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString('ja-JP') : '記録なし'}
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-blue-400 font-medium">{user.generated_images_count}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs ${
                              user.user_type === 'MyGarageユーザー' 
                                ? 'bg-blue-900 text-blue-300' 
                                : 'bg-green-900 text-green-300'
                            }`}>
                              {user.user_type}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-green-400 font-medium">{user.unified_credits}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400">
                    {userSearchQuery ? `"${userSearchQuery}"に一致するユーザーが見つかりません` : 'ユーザーが見つかりません'}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      case 'creditManagement':
        return (
          <div>
            <h3 className="text-xl font-semibold text-indigo-300 mb-6">
              クレジット管理
            </h3>
            {/* ユーザー検索セクション */}
            <div className="bg-gray-800 p-6 rounded-lg mb-6">
              <h4 className="text-lg font-medium text-white mb-4">ユーザー検索</h4>
              <div className="flex space-x-4">
                <input
                  type="text"
                  value={searchUsername}
                  onChange={(e) => setSearchUsername(e.target.value)}
                  placeholder="ユーザー名を入力"
                  className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchUser()}
                />
                <button
                  onClick={handleSearchUser}
                  disabled={isSearching}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSearching ? '検索中...' : '検索'}
                </button>
              </div>
            </div>
            {/* 検索結果表示セクション */}
            {foundUsers.length > 0 && (
              <div className="bg-gray-800 p-6 rounded-lg mb-6">
                <h4 className="text-lg font-medium text-white mb-4">
                  検索結果 ({foundUsers.length}人)
                </h4>
                <div className="space-y-4">
                  {foundUsers.map((user) => (
                    <div
                      key={user.firebase_uid}
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedUser?.firebase_uid === user.firebase_uid
                          ? 'border-indigo-500 bg-indigo-50 bg-opacity-10'
                          : 'border-gray-600 hover:border-gray-500'
                      }`}
                      onClick={() => setSelectedUser(user)}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-400">ユーザー名:</span>
                          <span className="text-white ml-2 font-medium">{user.nickname}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">ユーザータイプ:</span>
                          <span className="text-blue-400 ml-2">{user.user_type}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">統一クレジット:</span>
                          <span className="text-green-400 ml-2 font-medium">{user.unified_credits}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">電話番号:</span>
                          <span className="text-white ml-2">{user.phone_number || 'なし'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">UID:</span>
                          <span className="text-gray-300 ml-2 text-xs">{user.firebase_uid}</span>
                        </div>
                      </div>
                      {selectedUser?.firebase_uid === user.firebase_uid && (
                        <div className="mt-2 text-xs text-indigo-400">
                          ✓ 選択済み - このユーザーにクレジットを追加できます
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* クレジット付与セクション */}
            {selectedUser && (
              <div className="bg-gray-800 p-6 rounded-lg">
                <h4 className="text-lg font-medium text-white mb-4">
                  クレジット付与 - {selectedUser.nickname}
                </h4>
                <div className="flex space-x-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      付与クレジット数
                    </label>
                    <input
                      type="number"
                      value={creditAmount}
                      onChange={(e) => setCreditAmount(parseInt(e.target.value) || 0)}
                      min="1"
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <button
                    onClick={handleAddCredits}
                    disabled={isAddingCredits}
                    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAddingCredits ? '追加中...' : 'クレジット追加'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      case 'generationHistory':
        return (
          <div>
            <h3 className="text-xl font-semibold text-indigo-300 mb-6">
              生成履歴管理
            </h3>
            {/* 統計情報セクション */}
            {generationHistoryStats && (
              <div className="bg-gray-800 p-6 rounded-lg mb-6">
                <h4 className="text-lg font-medium text-white mb-4">生成統計</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-400">{generationHistoryStats.total_generations}</div>
                    <div className="text-sm text-gray-400">生成数合計（非公開含）</div>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-400">{generationHistoryStats.library_registrations}</div>
                    <div className="text-sm text-gray-400">ライブラリ登録数</div>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-400">{generationHistoryStats.public_images}</div>
                    <div className="text-sm text-gray-400">公開数</div>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-400">{generationHistoryStats.goods_creations}</div>
                    <div className="text-sm text-gray-400">グッズ作成数</div>
                  </div>
                </div>
                {/* カテゴリ別統計 */}
                <h5 className="text-md font-medium text-white mb-3">カテゴリ別</h5>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-700 p-3 rounded-lg">
                    <div className="text-xl font-bold text-indigo-400">{generationHistoryStats.category_stats.illustration}</div>
                    <div className="text-sm text-gray-400">イラスト</div>
                  </div>
                  <div className="bg-gray-700 p-3 rounded-lg">
                    <div className="text-xl font-bold text-green-400">{generationHistoryStats.category_stats.scene_change}</div>
                    <div className="text-sm text-gray-400">シーン変更</div>
                  </div>
                  <div className="bg-gray-700 p-3 rounded-lg">
                    <div className="text-xl font-bold text-orange-400">{generationHistoryStats.category_stats.customization}</div>
                    <div className="text-sm text-gray-400">カスタマイズ</div>
                  </div>
                </div>
              </div>
            )}
            {/* 検索フォームセクション */}
            <div className="bg-gray-800 p-6 rounded-lg mb-6">
              <h4 className="text-lg font-medium text-white mb-4">生成履歴一覧 絞り込みフォーム</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">検索</label>
                  <input
                    type="text"
                    value={generationHistorySearch}
                    onChange={(e) => setGenerationHistorySearch(e.target.value)}
                    placeholder="プロンプト、メニュー名、ユーザーID"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onKeyPress={(e) => e.key === 'Enter' && handleGenerationHistorySearch()}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">カテゴリ</label>
                  <select
                    value={generationHistoryCategory}
                    onChange={(e) => setGenerationHistoryCategory(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">すべて</option>
                    <option value="illustration">イラスト</option>
                    <option value="scene_change">シーン変更</option>
                    <option value="customization">カスタマイズ</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">ユーザー</label>
                  <input
                    type="text"
                    value={generationHistoryUser}
                    onChange={(e) => setGenerationHistoryUser(e.target.value)}
                    placeholder="ユーザーID"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">評価</label>
                  <select
                    value={generationHistoryRating}
                    onChange={(e) => setGenerationHistoryRating(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">すべて</option>
                    <option value="good">Good</option>
                    <option value="bad">Bad</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleGenerationHistorySearch}
                  disabled={isLoadingGenerationHistory}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingGenerationHistory ? '検索中...' : '検索'}
                </button>
                <button
                  onClick={handleGenerationHistoryReset}
                  disabled={isLoadingGenerationHistory}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  リセット
                </button>
              </div>
            </div>
            {/* 生成履歴一覧セクション */}
            <div className="bg-gray-800 p-6 rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-medium text-white">
                  生成履歴 サムネリスト ({generationHistoryList.length}件)
                  <span className="text-sm text-gray-400 ml-2">※最大最新50件表示</span>
                </h4>
                <button
                  onClick={() => loadGenerationHistoryList()}
                  disabled={isLoadingGenerationHistory}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingGenerationHistory ? '読み込み中...' : '最新50件'}
                </button>
              </div>
              {isLoadingGenerationHistory ? (
                <div className="text-center py-8">
                  <div className="text-gray-400">生成履歴を読み込み中...</div>
                </div>
              ) : generationHistoryList.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {generationHistoryList.map((item) => (
                    <div 
                      key={item.id} 
                      className="bg-gray-700 p-4 rounded-lg hover:bg-gray-600 transition-colors cursor-pointer"
                      onClick={() => handleHistoryItemClick(item)}
                    >
                      <div className="mb-3">
                        <img
                          src={item.image_url}
                          alt={item.display_prompt}
                          className="w-full h-32 object-cover rounded-md"
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-white">{item.user_name}</span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            item.category === 'illustration' ? 'bg-indigo-900 text-indigo-300' :
                            item.category === 'scene_change' ? 'bg-green-900 text-green-300' :
                            'bg-orange-900 text-orange-300'
                          }`}>
                            {item.category === 'illustration' ? 'イラスト' :
                             item.category === 'scene_change' ? 'シーン変更' : 'カスタマイズ'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 truncate" title={item.display_prompt}>
                          {item.display_prompt}
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <span>{new Date(item.timestamp).toLocaleDateString('ja-JP')}</span>
                          <div className="flex items-center space-x-2">
                            {item.is_public && (
                              <span className="text-green-400">公開</span>
                            )}
                            {item.is_saved_to_library && (
                              <span className="text-blue-400">ライブラリ</span>
                            )}
                            {item.goods_creation_count > 0 && (
                              <span className="text-purple-400">グッズ{item.goods_creation_count}</span>
                            )}
                            {item.rating && (
                              <span className={`${item.rating === 'good' ? 'text-green-400' : 'text-red-400'}`}>
                                {item.rating === 'good' ? '👍' : '👎'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400">
                    {generationHistorySearch || generationHistoryCategory || generationHistoryUser || generationHistoryRating
                      ? '検索条件に一致する生成履歴が見つかりません'
                      : '生成履歴が見つかりません'}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <div className="flex flex-col md:flex-row gap-6 md:gap-8 h-full">
      <nav className="md:w-1/4 lg:w-1/5 bg-gray-800 p-4 rounded-lg shadow-lg space-y-2 flex-shrink-0">
        {renderAdminNavItem(
          'genMenuCategories',
          'メニューカテゴリ管理',
          <FolderOpenIcon className="w-5 h-5" />,
        )}
        {renderAdminNavItem(
          'genMenus',
          '生成メニュー管理',
          <ViewGridIcon className="w-5 h-5" />,
        )}
        {renderAdminNavItem(
          'engines',
          '利用エンジン設定',
          <CogIcon className="w-5 h-5" />,
        )}
        {renderAdminNavItem(
          'chargeManagement',
          'チャージ管理',
          <CreditCardIcon className="w-5 h-5" />,
        )}
        {renderAdminNavItem(
          'sales',
          '売上管理',
          <ChartBarIcon className="w-5 h-5" />,
        )}
        {renderAdminNavItem(
          'orderManagement',
          '注文管理',
          <ClipboardDocumentListIcon className="w-5 h-5" />,
        )}
        {renderAdminNavItem(
          'information',
          'お知らせ管理',
          <InformationCircleIcon className="w-5 h-5" />,
        )}
        {renderAdminNavItem(
          'goods',
          'アイテム管理',
          <CubeIcon className="w-5 h-5" />,
        )}
        {renderAdminNavItem(
          'users',
          'ユーザー管理',
          <UsersIcon className="w-5 h-5" />,
        )}
        {renderAdminNavItem(
          'creditManagement',
          'クレジット管理',
          <CreditCardIcon className="w-5 h-5" />,
        )}
        {renderAdminNavItem(
          'generationHistory',
          '生成履歴',
          <ClipboardDocumentListIcon className="w-5 h-5" />,
        )}
        {renderAdminNavItem(
          'suzuriApi',
          'SUZURI API設定',
          <LinkIcon className="w-5 h-5" />,
        )}
        {renderAdminNavItem(
          'stripeApi',
          'Stripe API設定',
          <KeyIcon className="w-5 h-5" />,
        )}
      </nav>
      <div className="md:w-3/4 lg:w-4/5 bg-gray-800 p-6 rounded-lg shadow-lg overflow-y-auto custom-scrollbar">
        {renderSectionContent()}
      </div>
      {/* 生成履歴詳細モーダル */}
      {isHistoryDetailModalOpen && selectedHistoryItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* ヘッダー */}
            <div className="flex justify-between items-center p-6 border-b border-gray-700">
              <h2 className="text-xl font-semibold text-white">生成履歴詳細</h2>
              <button
                onClick={handleCloseHistoryDetailModal}
                className="text-gray-400 hover:text-white text-2xl font-bold"
              >
                ×
              </button>
            </div>
            {/* コンテンツ */}
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 画像セクション */}
                <div>
                  <h3 className="text-lg font-medium text-white mb-4">生成画像</h3>
                  <div className="relative">
                    <img
                      src={selectedHistoryItem.image_url}
                      alt="生成画像"
                      className="w-full h-auto rounded-lg shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => handleImageExpand(selectedHistoryItem.image_url)}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/placeholder-image.png';
                      }}
                    />
                  </div>
                </div>
                {/* 詳細情報セクション */}
                <div className="space-y-6">
                  {/* 基本情報 */}
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">基本情報</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-400">ユーザー:</span>
                        <span className="text-white font-medium">{selectedHistoryItem.user_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">ユーザーID:</span>
                        <span className="text-white font-mono text-sm">{selectedHistoryItem.user_id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">カテゴリ:</span>
                        <span className={`px-2 py-1 rounded text-white text-sm ${
                          selectedHistoryItem.category === 'illustration' ? 'bg-purple-500' :
                          selectedHistoryItem.category === 'scene_change' ? 'bg-blue-500' :
                          'bg-orange-500'
                        }`}>
                          {selectedHistoryItem.category === 'illustration' ? 'イラスト' :
                           selectedHistoryItem.category === 'scene_change' ? 'シーン変更' : 'カスタマイズ'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">メニュー名:</span>
                        <span className="text-white">{selectedHistoryItem.menu_name || '未設定'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">生成日時:</span>
                        <span className="text-white">
                          {new Date(selectedHistoryItem.timestamp).toLocaleString('ja-JP', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* ステータス情報 */}
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">ステータス</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-400">公開設定:</span>
                        <span className={`px-2 py-1 rounded text-white text-sm ${selectedHistoryItem.is_public ? 'bg-green-500' : 'bg-red-500'}`}>
                          {selectedHistoryItem.is_public ? '公開' : '非公開'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">ライブラリ保存:</span>
                        <span className={`px-2 py-1 rounded text-white text-sm ${selectedHistoryItem.is_saved_to_library ? 'bg-blue-500' : 'bg-gray-500'}`}>
                          {selectedHistoryItem.is_saved_to_library ? '保存済み' : '未保存'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">グッズ作成数:</span>
                        <span className="text-white font-medium">{selectedHistoryItem.goods_creation_count}件</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">評価:</span>
                        <span className={`px-2 py-1 rounded text-white text-sm ${
                          selectedHistoryItem.rating === 'good' ? 'bg-green-500' : 
                          selectedHistoryItem.rating === 'bad' ? 'bg-red-500' : 'bg-gray-500'
                        }`}>
                          {selectedHistoryItem.rating === 'good' ? 'Good' : 
                           selectedHistoryItem.rating === 'bad' ? 'Bad' : '未評価'}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* プロンプト */}
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">プロンプト</h3>
                    <div className="bg-gray-700 p-4 rounded-lg">
                      <p className="text-white text-sm whitespace-pre-wrap leading-relaxed">
                        {selectedHistoryItem.display_prompt}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* フッター */}
            <div className="flex justify-end p-6 border-t border-gray-700">
              <button
                onClick={handleCloseHistoryDetailModal}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
      {/* 画像拡大モーダル */}
      {isImageExpandModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* ヘッダー */}
            <div className="flex justify-between items-center p-6 border-b border-gray-700">
              <h2 className="text-xl font-semibold text-white">生成画像</h2>
              <button
                onClick={handleCloseImageExpandModal}
                className="text-gray-400 hover:text-white text-2xl font-bold"
              >
                ×
              </button>
            </div>
            {/* コンテンツ */}
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 画像セクション */}
                <div>
                  <h3 className="text-lg font-medium text-white mb-4">生成画像</h3>
                  <div className="relative">
                    <img
                      src={expandedImageUrl}
                      alt="生成画像"
                      className="w-full h-auto rounded-lg shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/placeholder-image.png';
                      }}
                    />
                  </div>
                </div>
                {/* 詳細情報セクション */}
                <div className="space-y-6">
                  {/* 基本情報 */}
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">基本情報</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-400">URL:</span>
                        <span className="text-white font-medium">{expandedImageUrl}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* フッター */}
            <div className="flex justify-end p-6 border-t border-gray-700">
              <button
                onClick={handleCloseImageExpandModal}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
      {/* バリエーション選択モーダル */}
      {showVariantModal !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-indigo-300 mb-4">
              商品バリエーションを選択
            </h3>
            {loadingVariants ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
              </div>
            ) : productVariants ? (
              <div className="space-y-4">
                <div className="mb-4">
                  <p className="text-gray-300 mb-2">商品名: {productVariants.product_name}</p>
                  <p className="text-gray-400 text-sm">合計 {productVariants.total_variants} バリエーション</p>
                </div>
                {/* サイズ選択 */}
                {productVariants.sizes && productVariants.sizes.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      1. サイズを選択
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {productVariants.sizes.map((size: string) => {
                        // @ts-ignore: kept for potential future use
                        const _variantsWithSize = productVariants.variants.filter((v: any) => v.size === size);
                        return (
                          <button
                            key={size}
                            onClick={() => {
                              setSelectedSize(size);
                              // サイズを変更したら、その サイズ＋現在の色でバリアントを探す
                              if (selectedColor) {
                                const variant = productVariants.variants.find(
                                  (v: any) => v.size === size && v.color === selectedColor
                                );
                                if (variant) {
                                  setSelectedVariant(variant.id);
                                } else {
                                  // その組み合わせがない場合は色をリセット
                                  setSelectedColor(null);
                                  setSelectedVariant(null);
                                }
                              } else {
                                // 色が選択されていない場合はバリアントをクリア
                                setSelectedVariant(null);
                              }
                            }}
                            className={`py-2 px-4 rounded border ${
                              selectedSize === size
                                ? 'bg-indigo-600 border-indigo-500 text-white'
                                : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            {size}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* 色選択（サイズが選択されている場合のみ表示） */}
                {selectedSize && productVariants.colors && productVariants.colors.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      2. 色を選択
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {productVariants.colors.map((color: any) => {
                        // 選択されたサイズでこの色が利用可能かチェック
                        const variant = productVariants.variants.find(
                          (v: any) => v.size === selectedSize && v.color === color.name
                        );
                        const isAvailable = !!variant;
                        return (
                          <button
                            key={color.name}
                            onClick={() => {
                              if (variant) {
                                setSelectedColor(color.name);
                                setSelectedVariant(variant.id);
                                console.log('Selected variant:', variant);
                              }
                            }}
                            disabled={!isAvailable}
                            className={`py-2 px-4 rounded border flex items-center space-x-2 ${
                              selectedColor === color.name
                                ? 'bg-indigo-600 border-indigo-500 text-white'
                                : isAvailable
                                ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                                : 'bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed opacity-50'
                            }`}
                          >
                            <span
                              className="w-4 h-4 rounded border border-gray-600"
                              style={{ backgroundColor: color.code }}
                            />
                            <span>{color.name}</span>
                            {!isAvailable && <span className="text-xs ml-1">(利用不可)</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* 選択されたバリエーション情報 */}
                {selectedVariant && (
                  <div className="mt-4 p-4 bg-gray-700 rounded">
                    <p className="text-sm text-gray-300">
                      選択されたバリエーション: 
                      {(() => {
                        const variant = productVariants.variants.find((v: any) => v.id === selectedVariant);
                        return variant ? `${variant.color || ''} ${variant.size || ''} (ID: ${variant.id})` : '';
                      })()}
                    </p>
                  </div>
                )}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowVariantModal(null);
                      setProductVariants(null);
                      setSelectedVariant(null);
                      setSelectedSize(null);
                      setSelectedColor(null);
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    キャンセル
                  </button>
                  <button
                    onClick={() => handleCreateSampleProduct(showVariantModal)}
                    disabled={!selectedVariant || creatingProduct === showVariantModal}
                    className={`px-4 py-2 rounded font-medium ${
                      !selectedVariant || creatingProduct === showVariantModal
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {creatingProduct === showVariantModal ? '作成中...' : '商品を作成'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-400">
                バリエーション情報を取得できませんでした
              </div>
            )}
          </div>
        </div>
      )}
      {/* Printful編集モーダル */}
      {editingPrintfulProduct && (
        <PrintfulProductEditModal
          product={editingPrintfulProduct}
          isOpen={isPrintfulEditModalOpen}
          onClose={() => {
            setIsPrintfulEditModalOpen(false);
            setEditingPrintfulProduct(null);
          }}
          onSave={handleSavePrintfulEdit}
        />
      )}
      {/* バリアント選択モーダル */}
      {showVariantSelectModal && selectedGoodsForMockup && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-4xl space-y-4 overflow-y-auto max-h-[90vh] custom-scrollbar">
            <h4 className="text-lg font-semibold text-indigo-300">
              モックアップ用のサイズ・色を選択
            </h4>
            {loadingCatalogVariants ? (
              <div className="text-center py-8">
                <div className="text-gray-400">バリアント情報を読み込み中...</div>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <p className="text-gray-400 text-sm">
                    商品: {selectedGoodsForMockup.display_name}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {catalogVariants.map((variant: any) => (
                    <button
                      key={variant.variant_id}
                      onClick={() => {
                        setSelectedVariantForMockup(variant);
                        // テンプレート画像URLを更新
                        if (templatesData?.variant_mapping) {
                          const variantId = parseInt(variant.variant_id);
                          const variantTemplate = templatesData.variant_mapping.find((v: any) => 
                            parseInt(v.variant_id) === variantId
                          );
                          console.log('Setting template for variant:', variantId, 'variantTemplate:', variantTemplate);
                          if (variantTemplate?.templates?.[0]) {
                            // templatesリストから実際のテンプレートを取得
                            const fullTemplate = templatesData.templates?.find((t: any) => 
                              t.template_id === variantTemplate.templates[0].template_id
                            );
                            console.log('Full template found:', fullTemplate);
                            if (fullTemplate?.image_url) {
                              console.log('Setting template URL:', fullTemplate.image_url);
                              setSelectedTemplateUrl(fullTemplate.image_url);
                              setFullTemplateData(fullTemplate);
                              // デフォルトパラメータを設定
                              const defaultParams = calculateDefaultParams(productImages[selectedGoodsForMockup.id], fullTemplate);
                              if (defaultParams) {
                                setMockupPositionParams(defaultParams);
                                setPreviewParams(defaultParams);
                              }
                            }
                          }
                        }
                      }}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        selectedVariantForMockup?.variant_id === variant.variant_id
                          ? 'border-indigo-500 bg-indigo-900/30'
                          : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                      } ${!variant.in_stock || !variant.is_japan_available ? 'opacity-50' : ''}`}
                      disabled={!variant.in_stock || !variant.is_japan_available}
                    >
                      <div className="text-left">
                        <div className="font-medium text-white">
                          {variant.size}
                          {variant.color && ` - ${variant.color}`}
                        </div>
                        <div className="text-sm text-gray-400 mt-1">
                          {variant.price && `¥${variant.price}`}
                          {!variant.is_japan_available && ' (日本配送不可)'}
                          {!variant.in_stock && ' (在庫なし)'}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                {/* モックアップテンプレート画像 */}
                {(() => {
                  console.log('selectedTemplateUrl:', selectedTemplateUrl);
                  if (selectedTemplateUrl) {
                    return (
                      <div className="mt-6">
                        <h5 className="text-md font-semibold text-indigo-300 mb-3">モックアップテンプレート</h5>
                        <div className="bg-gray-700 p-4 rounded-lg flex justify-center">
                          <img 
                            src={selectedTemplateUrl} 
                            alt="Mockup Template" 
                            className="max-w-full h-auto rounded"
                            style={{ maxHeight: '300px', objectFit: 'contain' }}
                          />
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                {/* Printfiles情報 */}
                {printfilesData && selectedVariantForMockup && (
                  <div className="mt-6">
                    <h5 className="text-md font-semibold text-indigo-300 mb-3">Printfiles情報</h5>
                    <div className="bg-gray-700 p-4 rounded-lg">
                      {(() => {
                        console.log('Looking for variant:', selectedVariantForMockup.variant_id);
                        console.log('Available printfiles:', printfilesData);
                        console.log('variant_printfiles:', printfilesData.variant_printfiles);
                        // バリアントIDを数値として比較
                        const variantId = parseInt(selectedVariantForMockup.variant_id);
                        const variantPrintfile = printfilesData.variant_printfiles?.find((vp: any) => {
                          console.log('Comparing:', vp.variant_id, 'with', variantId);
                          return parseInt(vp.variant_id) === variantId;
                        });
                        console.log('Found variantPrintfile:', variantPrintfile);
                        // placements内のprintfileを探す
                        console.log('variantPrintfile.placements:', variantPrintfile?.placements);
                        const placementId = variantPrintfile?.placements?.['embroidery_front_large'] ||
                                          variantPrintfile?.placements?.['front'] ||
                                          variantPrintfile?.placements?.['default'];
                        // printfilesリストから対応するprintfileを取得
                        console.log('printfilesData.printfiles:', printfilesData.printfiles);
                        console.log('placementId:', placementId);
                        const printfile = placementId ? printfilesData.printfiles?.find((pf: any) => {
                          console.log('Comparing printfile_id:', pf.printfile_id, 'with placementId:', placementId);
                          return pf.printfile_id === placementId;
                        }) : null;
                        console.log('Found placementId:', placementId);
                        console.log('Found printfile:', printfile);
                        if (printfile) {
                          return (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div>
                                <span className="text-gray-400">幅:</span>
                                <span className="ml-2 text-white">{printfile.width}px</span>
                              </div>
                              <div>
                                <span className="text-gray-400">高さ:</span>
                                <span className="ml-2 text-white">{printfile.height}px</span>
                              </div>
                              <div>
                                <span className="text-gray-400">DPI:</span>
                                <span className="ml-2 text-white">{printfile.dpi}</span>
                              </div>
                              <div>
                                <span className="text-gray-400">Fill Mode:</span>
                                <span className="ml-2 text-white">{printfile.fill_mode}</span>
                              </div>
                            </div>
                          );
                        }
                        return <div className="text-gray-400">Printfile情報が見つかりません</div>;
                      })()}
                    </div>
                  </div>
                )}
                {/* Templates印刷情報 */}
                {templatesData && selectedVariantForMockup && (
                  <div className="mt-6">
                    <h5 className="text-md font-semibold text-indigo-300 mb-3">テンプレート印刷情報</h5>
                    <div className="bg-gray-700 p-4 rounded-lg">
                      {(() => {
                        console.log('Looking for variant template:', selectedVariantForMockup.variant_id);
                        console.log('Available templates:', templatesData);
                        console.log('variant_mapping:', templatesData.variant_mapping);
                        // バリアントIDを数値として比較
                        const variantId = parseInt(selectedVariantForMockup.variant_id);
                        const variantTemplate = templatesData.variant_mapping?.find((vm: any) => {
                          console.log('Template comparing:', vm.variant_id, 'with', variantId);
                          return parseInt(vm.variant_id) === variantId;
                        });
                        console.log('Found variantTemplate:', variantTemplate);
                        const template = variantTemplate?.templates?.[0];
                        console.log('Found template:', template);
                        // templatesリストから実際のテンプレート詳細を取得
                        const fullTemplate = template ? templatesData.templates?.find((t: any) => 
                          t.template_id === template.template_id
                        ) : null;
                        console.log('Found full template:', fullTemplate);
                        if (fullTemplate) {
                          return (
                            <div>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm mb-3">
                                <div>
                                  <span className="text-gray-400">テンプレート幅:</span>
                                  <span className="ml-2 text-white">{fullTemplate.template_width}px</span>
                                </div>
                                <div>
                                  <span className="text-gray-400">テンプレート高さ:</span>
                                  <span className="ml-2 text-white">{fullTemplate.template_height}px</span>
                                </div>
                                <div>
                                  <span className="text-gray-400">配置:</span>
                                  <span className="ml-2 text-white">{fullTemplate.placement || template.placement}</span>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                <div>
                                  <span className="text-gray-400">印刷エリア幅:</span>
                                  <span className="ml-2 text-white">{fullTemplate.print_area_width}px</span>
                                </div>
                                <div>
                                  <span className="text-gray-400">印刷エリア高さ:</span>
                                  <span className="ml-2 text-white">{fullTemplate.print_area_height}px</span>
                                </div>
                                <div>
                                  <span className="text-gray-400">上端位置:</span>
                                  <span className="ml-2 text-white">{fullTemplate.print_area_top}px</span>
                                </div>
                                <div>
                                  <span className="text-gray-400">左端位置:</span>
                                  <span className="ml-2 text-white">{fullTemplate.print_area_left}px</span>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return <div className="text-gray-400">テンプレート情報が見つかりません</div>;
                      })()}
                    </div>
                  </div>
                )}
                {/* モックアッププレビュー */}
                {selectedTemplateUrl && fullTemplateData && selectedGoodsForMockup && (
                  <div className="mt-6">
                    <h5 className="text-md font-semibold text-indigo-300 mb-3">モックアッププレビュー</h5>
                    <div className="bg-gray-700 p-4 rounded-lg flex justify-center">
                      <div style={{ 
                        position: 'relative', 
                        width: '100%',
                        maxWidth: '500px',
                        aspectRatio: `${fullTemplateData.template_width} / ${fullTemplateData.template_height}`
                      }}>
                        {/* テンプレート画像（ベース） */}
                        <img
                          src={selectedTemplateUrl}
                          alt="Product template"
                          style={{ 
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            zIndex: 1
                          }}
                        />
                        {/* 印刷エリアとユーザー画像（テンプレートの上） */}
                        <div style={{ 
                          position: 'absolute', 
                          width: `${(fullTemplateData.print_area_width / fullTemplateData.template_width) * 100}%`,
                          height: `${(fullTemplateData.print_area_height / fullTemplateData.template_height) * 100}%`,
                          top: `${(fullTemplateData.print_area_top / fullTemplateData.template_height) * 100}%`,
                          left: `${(fullTemplateData.print_area_left / fullTemplateData.template_width) * 100}%`,
                          background: 'rgba(255,233,230,0.33)',
                          overflow: 'hidden',
                          zIndex: 2
                        }}>
                          {/* ユーザーの画像 */}
                          {productImages[selectedGoodsForMockup.id] && (
                            <img 
                              alt="User design" 
                              src={productImages[selectedGoodsForMockup.id]}
                              style={{ 
                                position: 'absolute',
                                width: `${(previewParams.width / fullTemplateData.print_area_width) * 100}%`,
                                height: `${(previewParams.height / fullTemplateData.print_area_height) * 100}%`,
                                top: `${(previewParams.top / fullTemplateData.print_area_height) * 100}%`,
                                left: `${(previewParams.left / fullTemplateData.print_area_width) * 100}%`,
                                objectFit: 'contain'
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* 位置調整パラメータ */}
                {selectedVariantForMockup && (
                  <div className="mt-6 p-4 bg-gray-700 rounded-lg">
                    <h5 className="text-md font-semibold text-indigo-300 mb-3">画像位置調整（オプション）</h5>
                    <p className="text-xs text-gray-400 mb-3">※印刷エリア内での画像位置を調整します（単位：px）</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">
                          画像幅
                          <input
                            type="checkbox"
                            checked={maintainAspectRatio}
                            onChange={(e) => setMaintainAspectRatio(e.target.checked)}
                            className="ml-2 mr-1"
                          />
                          <span className="text-xs">比率維持</span>
                        </label>
                        <input
                          type="number"
                          value={mockupPositionParams.width}
                          onChange={(e) => {
                            const newWidth = parseInt(e.target.value) || 0;
                            if (maintainAspectRatio) {
                              // 比率を維持して高さを自動計算
                              const newHeight = Math.round(newWidth / imageAspectRatio);
                              setMockupPositionParams(prev => ({ ...prev, width: newWidth, height: newHeight }));
                            } else {
                              setMockupPositionParams(prev => ({ ...prev, width: newWidth }));
                            }
                          }}
                          className="w-full px-3 py-1 bg-gray-600 text-white rounded border border-gray-500 focus:border-indigo-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">画像高さ</label>
                        <input
                          type="number"
                          value={mockupPositionParams.height}
                          onChange={(e) => {
                            const newHeight = parseInt(e.target.value) || 0;
                            if (maintainAspectRatio) {
                              // 比率を維持して幅を自動計算
                              const newWidth = Math.round(newHeight * imageAspectRatio);
                              setMockupPositionParams(prev => ({ ...prev, width: newWidth, height: newHeight }));
                            } else {
                              setMockupPositionParams(prev => ({ ...prev, height: newHeight }));
                            }
                          }}
                          className="w-full px-3 py-1 bg-gray-600 text-white rounded border border-gray-500 focus:border-indigo-400"
                          disabled={maintainAspectRatio && false} // 比率維持時も入力可能にする
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">上からの位置</label>
                        <input
                          type="number"
                          value={mockupPositionParams.top}
                          onChange={(e) => setMockupPositionParams(prev => ({ ...prev, top: parseInt(e.target.value) || 0 }))}
                          className="w-full px-3 py-1 bg-gray-600 text-white rounded border border-gray-500 focus:border-indigo-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">左からの位置</label>
                        <input
                          type="number"
                          value={mockupPositionParams.left}
                          onChange={(e) => setMockupPositionParams(prev => ({ ...prev, left: parseInt(e.target.value) || 0 }))}
                          className="w-full px-3 py-1 bg-gray-600 text-white rounded border border-gray-500 focus:border-indigo-400"
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <button
                        onClick={() => {
                          // デフォルト値にリセット
                          const defaultParams = calculateDefaultParams(productImages[selectedGoodsForMockup.id], fullTemplateData);
                          if (defaultParams) {
                            setMockupPositionParams(defaultParams);
                          }
                        }}
                        className="text-sm text-indigo-400 hover:text-indigo-300"
                      >
                        推奨値にリセット
                      </button>
                      <button
                        onClick={() => {
                          // プレビューに反映
                          setPreviewParams({...mockupPositionParams});
                        }}
                        className="bg-indigo-500 hover:bg-indigo-600 px-4 py-2 rounded text-white font-medium text-sm"
                      >
                        プレビューに反映
                      </button>
                      <button
                        onClick={() => handleCreatePrintfulMockup()}
                        disabled={!selectedVariantForMockup || !fullTemplateData}
                        className="bg-green-500 hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed px-4 py-2 rounded text-white font-medium text-sm ml-2"
                      >
                        モックアップ作成
                      </button>
                    </div>
                  </div>
                )}
                {/* 生成されたモックアップの表示 */}
                {(creatingMockup || generatedMockupUrl || mockupError || selectedGoodsForMockup?.mockup_url) && (
                  <div className="mt-6 p-4 bg-gray-700 rounded-lg">
                    <h5 className="text-md font-semibold text-indigo-300 mb-3">生成されたモックアップ</h5>
                    {creatingMockup && (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400 mx-auto mb-2"></div>
                        <p className="text-gray-400">モックアップを生成中...</p>
                      </div>
                    )}
                    {mockupError && (
                      <div className="bg-red-900/30 border border-red-500 rounded p-3 text-red-300">
                        エラー: {mockupError}
                      </div>
                    )}
                    {/* 新しく生成されたモックアップがある場合はそれを、なければ既存のmockup_urlを表示 */}
                    {(generatedMockupUrl || selectedGoodsForMockup?.mockup_url) && !creatingMockup && (
                      <div>
                        <div className="flex justify-center">
                          <img 
                            src={generatedMockupUrl || selectedGoodsForMockup.mockup_url} 
                            alt="Mockup" 
                            className="max-w-full h-auto rounded"
                            style={{ maxWidth: '500px' }}
                          />
                        </div>
                        <p className="text-xs text-gray-400 text-center mt-2">
                          {generatedMockupUrl ? '新しく生成されたモックアップ' : '現在保存されているモックアップ'}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setShowVariantSelectModal(false);
                      setSelectedVariantForMockup(null);
                      setPrintfilesData(null);
                      setTemplatesData(null);
                      setSelectedTemplateUrl(null);
                      setFullTemplateData(null);
                      setMockupPositionParams({width: 1600, height: 1600, top: 0, left: 0});
                      setPreviewParams({width: 1600, height: 1600, top: 0, left: 0});
                      setGeneratedMockupUrl(null);
                      setMockupError(null);
                      setMockupTaskKey(null);
                      setMaintainAspectRatio(true);  // 比率維持もリセット
                    }}
                    className="bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded text-gray-200 font-medium"
                  >
                    キャンセル
                  </button>
                  <button
                    onClick={() => {
                      if (selectedVariantForMockup) {
                        handleGenerateMockup(selectedGoodsForMockup.id, selectedVariantForMockup);
                      }
                    }}
                    disabled={!selectedVariantForMockup}
                    className={`px-4 py-2 rounded font-medium ${
                      selectedVariantForMockup
                        ? 'bg-indigo-500 hover:bg-indigo-600 text-white'
                        : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    モックアップに反映
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* バリエーション選択モーダル（商品作成用） */}
      {showVariantModal && productVariants && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-indigo-300 mb-4">
                バリエーション選択
              </h3>
              {loadingVariants ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400 mx-auto"></div>
                </div>
              ) : (
                <>
                  {/* サイズ選択 */}
                  <div className="mb-6">
                    <h4 className="text-md font-semibold text-gray-300 mb-3">サイズを選択</h4>
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                      {productVariants.sizes?.map((size: string) => (
                        <button
                          key={size}
                          onClick={() => {
                            setSelectedSize(size);
                            setSelectedColor(null);
                            setSelectedVariant(null);
                          }}
                          className={`p-2 rounded border ${
                            selectedSize === size
                              ? 'border-indigo-400 bg-indigo-900 text-white'
                              : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* 色選択（サイズ選択後） */}
                  {selectedSize && productVariants.variants_by_size?.[selectedSize] && (
                    <div className="mb-6">
                      <h4 className="text-md font-semibold text-gray-300 mb-3">色を選択</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {productVariants.variants_by_size[selectedSize].map((variant: any) => (
                          <button
                            key={variant.id}
                            onClick={() => {
                              setSelectedColor(variant.color);
                              setSelectedVariant(variant.id);
                            }}
                            disabled={!variant.is_available}
                            className={`p-3 rounded border ${
                              selectedVariant === variant.id
                                ? 'border-indigo-400 bg-indigo-900'
                                : variant.is_available
                                ? 'border-gray-600 bg-gray-700 hover:border-gray-500'
                                : 'border-gray-700 bg-gray-800 opacity-50 cursor-not-allowed'
                            }`}
                          >
                            <div className="flex items-center space-x-2">
                              {variant.color_code && (
                                <div
                                  className="w-6 h-6 rounded-full border border-gray-600"
                                  style={{ backgroundColor: variant.color_code }}
                                />
                              )}
                              <div className="text-left">
                                <div className="text-sm text-white">{variant.color || 'デフォルト'}</div>
                                <div className="text-xs text-gray-400">
                                  {variant.is_available ? '利用可能' : '利用不可'}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-700">
                    <button
                      onClick={() => {
                        setShowVariantModal(null);
                        setProductVariants(null);
                        setSelectedVariant(null);
                        setSelectedSize(null);
                        setSelectedColor(null);
                      }}
                      className="bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded text-gray-200 font-medium"
                    >
                      キャンセル
                    </button>
                    <button
                      onClick={() => handleCreateSampleProduct(showVariantModal)}
                      disabled={!selectedVariant || creatingProduct === showVariantModal}
                      className={`px-4 py-2 rounded font-medium ${
                        selectedVariant && creatingProduct !== showVariantModal
                          ? 'bg-green-600 hover:bg-green-700 text-white'
                          : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {creatingProduct === showVariantModal ? '作成中...' : 'サンプル商品を作成'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* 購入モーダル */}
      {showPurchaseModal && purchaseModalData && (
        <PrintfulPurchaseModal
          isOpen={showPurchaseModal}
          onClose={() => {
            setShowPurchaseModal(false);
            setPurchaseModalData(null);
          }}
          userId={purchaseModalData.userId}
          imageUrl={purchaseModalData.imageUrl}
          mockupUrl={purchaseModalData.mockupUrl}
          catalogId={purchaseModalData.catalogId}
          goodsName={purchaseModalData.goodsName}
        />
      )}
    </div>
  );
};
// SortableCategoryItemコンポーネント
const SortableCategoryItem: React.FC<{
  category: AdminGenerationMenuCategoryItem;
  onEdit: () => void;
  onDelete: () => void;
  isDragging: boolean;
}> = ({ category, onEdit, onDelete, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: category.id.toString() });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`bg-gray-700 p-4 rounded-md shadow flex justify-between items-start transition-opacity ${
        isSortableDragging ? 'opacity-50' : 'opacity-100'
      } ${
        isDragging && !isSortableDragging ? 'pointer-events-none' : ''
      }`}
    >
      <div className="flex items-center space-x-3 flex-grow">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-indigo-300 transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 5v.01M12 12v.01M12 19v.01M12 5a2 2 0 110-4 2 2 0 010 4zM12 12a2 2 0 110-4 2 2 0 010 4zM12 19a2 2 0 110-4 2 2 0 010 4z"
            />
          </svg>
        </div>
        <div>
          <p className="font-semibold text-indigo-400">{category.name}</p>
          <p className="text-sm text-gray-300 mt-1">
            {category.description || '説明なし'}
          </p>
        </div>
      </div>
      <div className="space-x-2 flex-shrink-0">
        <button
          onClick={onEdit}
          className="p-2 text-gray-400 hover:text-indigo-300 transition-colors"
          aria-label={`Edit ${category.name}`}
        >
          <PencilAltIcon className="w-5 h-5" />
        </button>
        <button
          onClick={onDelete}
          className="p-2 text-gray-400 hover:text-red-400 transition-colors"
          aria-label={`Delete ${category.name}`}
        >
          <TrashIcon className="w-5 h-5" />
        </button>
      </div>
    </li>
  );
};
// SortableGoodsItemコンポーネント
const SortableGoodsItem: React.FC<{
  item: GoodsManagementItem;
  onEdit: () => void;
  isDragging: boolean;
}> = ({ item, onEdit, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: item.id.toString() });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`bg-gray-700 p-4 rounded-md shadow flex justify-between items-start transition-opacity cursor-grab ${
        isSortableDragging ? 'opacity-50' : 'opacity-100'
      } ${isDragging && !isSortableDragging ? 'pointer-events-none' : ''}`}
      {...attributes}
      {...listeners}
    >
            <div className="flex items-start space-x-4">
        {item.icon_url ? (
          <img
            src={item.icon_url}
            alt={item.display_name}
            className="w-20 h-20 object-cover rounded-md border border-gray-600 flex-shrink-0"
          />
        ) : (
          <div className="w-20 h-20 bg-gray-600 rounded-md flex items-center justify-center text-gray-500 flex-shrink-0">
            <CubeIcon className="w-10 h-10" />
          </div>
        )}
        <div className="flex-grow">
          <div className="flex items-center space-x-2 mb-1">
            <p className="font-semibold text-indigo-400">{item.display_name}</p>
            <span className={`px-2 py-1 text-xs rounded-full ${item.is_public ? 'bg-green-600 text-green-100' : 'bg-gray-600 text-gray-300'}`}>{item.is_public ? '公開' : '非公開'}</span>
          </div>
          <p className="text-sm text-gray-300">SUZURI ID: {item.suzuri_item_id}</p>
          <p className="text-sm text-gray-300">基本価格: ¥{item.base_price.toLocaleString()}</p>
          <p className="text-sm text-gray-300">最終価格: ¥{item.final_price.toLocaleString()}</p>
          {item.descriptions && item.descriptions.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-medium text-gray-400">説明:</p>
              {item.descriptions.slice(0, 2).map((desc, index) => (
                <p key={index} className="text-xs text-gray-500 ml-2">{desc}</p>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="space-x-2 flex-shrink-0" data-no-dnd>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          className="p-2 text-gray-400 hover:text-indigo-300 pointer-events-auto"
          aria-label={`Edit ${item.display_name}`}
        >
          <PencilAltIcon className="w-5 h-5" />
        </button>
      </div>
    </li>
  );
};
// ... (rest of the AdminView forms: AdminGenMenuCategoryForm, AdminGenMenuForm, AdminGoodsForm, AdminChargeOptionForm remain the same)
// The forms are not included here for brevity but are assumed to be present in the actual file.
const AdminGenMenuCategoryForm: React.FC<{
  item: AdminGenerationMenuCategoryItem;
  onSave: (item: AdminGenerationMenuCategoryItem) => void;
  onCancel: () => void;
}> = ({ item, onSave, onCancel }) => {
  const [currentItem, setCurrentItem] =
    useState<AdminGenerationMenuCategoryItem>(item);
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setCurrentItem((prev) => ({ ...prev, [name]: value }));
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-lg space-y-4">
        <h4 className="text-lg font-semibold text-indigo-300">
          {item.id === 0 ? '新規カテゴリ追加' : `編集: ${item.name}`}
        </h4>
        <div>
          <label
            htmlFor="categoryName"
            className="text-sm text-gray-400 block mb-1"
          >
            カテゴリ名
          </label>
          <input
            id="categoryName"
            name="name"
            value={currentItem.name || ''}
            onChange={handleChange}
            className="w-full bg-gray-700 p-2 rounded text-gray-200"
            required
          />
        </div>
        <div>
          <label
            htmlFor="categoryDescription"
            className="text-sm text-gray-400 block mb-1"
          >
            補足説明 (任意)
          </label>
          <textarea
            id="categoryDescription"
            name="description"
            value={currentItem.description || ''}
            onChange={handleChange}
            rows={3}
            className="w-full bg-gray-700 p-2 rounded text-gray-200"
          />
        </div>
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded text-gray-200 font-medium"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={() => onSave(currentItem)}
            className="bg-indigo-500 hover:bg-indigo-600 px-4 py-2 rounded text-white font-medium"
            disabled={!currentItem.name.trim()}
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
};
const AdminGenMenuForm: React.FC<{
  item: AdminGenerationMenuItem;
  categories: AdminGenerationMenuCategoryItem[];
  onSave: (item: AdminGenerationMenuItem) => void;
  onCancel: () => void;
}> = ({ item, categories, onSave, onCancel }) => {
  const { staffRefs, carModelRefs, carSpotRefs } = useMasterReferences();
  // デバッグ: 初期値の確認
  console.log('AdminGenMenuForm item:', item);
  console.log('goodsOrder:', item.goodsOrder);
  console.log('goods_order:', item.goods_order);
  const [currentItem, setCurrentItem] = useState<AdminGenerationMenuItem>({
    ...item,
    goodsOrder: item.goodsOrder || [],
    goods_order: item.goodsOrder || [] // バックエンド用にも設定
  });
  const [isUploadingInput, setIsUploadingInput] = useState(false);
  const [isUploadingResult, setIsUploadingResult] = useState(false);
  const [isUploadingGuide, setIsUploadingGuide] = useState(false);
  const [showAssistantModal, setShowAssistantModal] = useState(false);

  // FLUX.2用の状態変数
  const [useEditImage, setUseEditImage] = useState(item.useEditImage || item.use_edit_image || false);
  const [refImageLabels, setRefImageLabels] = useState<string[]>(item.refImageLabels || item.ref_image_labels || []);
  const [newRefLabel, setNewRefLabel] = useState('');

  // メニュー固定参照画像
  const [menuReferenceImages, setMenuReferenceImages] = useState<{ label: string; url: string }[]>(
    item.menuReferenceImages || item.menu_reference_images || []
  );
  const [newMenuRefLabel, setNewMenuRefLabel] = useState('');
  const [isUploadingMenuRef, setIsUploadingMenuRef] = useState(false);

  // 動画エンジン用: 人物参照ラベル（アセット登録対象）
  const [videoPersonRefLabels, setVideoPersonRefLabels] = useState<string[]>(
    item.videoPersonRefLabels || item.video_person_ref_labels || []
  );
  const [newPersonRefLabel, setNewPersonRefLabel] = useState('');
  const [isUploadingPersonRef, setIsUploadingPersonRef] = useState(false);

  // 動画エンジン用: Renoise登録済みアセットの直接参照
  const [videoPersonAssets, setVideoPersonAssets] = useState<{ asset_id: number; name: string; thumbnail_url?: string }[]>(
    item.videoPersonAssets || item.video_person_assets || []
  );
  const [renoiseAssets, setRenoiseAssets] = useState<RenoiseAsset[]>([]);
  const [isLoadingRenoiseAssets, setIsLoadingRenoiseAssets] = useState(false);

  // デフォルトのアスペクト比
  const [defaultAspectRatio, setDefaultAspectRatio] = useState<string | null>(
    item.defaultAspectRatio || item.default_aspect_ratio || null
  );

  // 動画エンジン用: 動画の秒数（4〜15）
  const [videoDurationSeconds, setVideoDurationSeconds] = useState<number>(
    item.videoDurationSeconds || item.video_duration_seconds || 5
  );

  // 動画i2v用: フレーム指定方法と固定画像
  const [videoStartFrameMode, setVideoStartFrameMode] = useState<'user' | 'fixed'>(
    (item.videoStartFrameMode || item.video_start_frame_mode || 'user') as 'user' | 'fixed'
  );
  const [videoStartFrameUrl, setVideoStartFrameUrl] = useState<string>(
    item.videoStartFrameUrl || item.video_start_frame_url || ''
  );
  const [videoEndFrameMode, setVideoEndFrameMode] = useState<'none' | 'user' | 'fixed'>(
    (item.videoEndFrameMode || item.video_end_frame_mode || (item.videoUseEndFrame || item.video_use_end_frame ? 'user' : 'none')) as 'none' | 'user' | 'fixed'
  );
  const [videoEndFrameUrl, setVideoEndFrameUrl] = useState<string>(
    item.videoEndFrameUrl || item.video_end_frame_url || ''
  );
  const [isUploadingFrame, setIsUploadingFrame] = useState(false);
  // TopView参照動画モード: メニュー固定の参照動画
  const [videoReferenceVideoUrl, setVideoReferenceVideoUrl] = useState<string>(
    item.videoReferenceVideoUrl || item.video_reference_video_url || ''
  );
  const [isUploadingReferenceVideo, setIsUploadingReferenceVideo] = useState(false);

  // 高度編集エンジンかどうかを判定（編集画像・参照画像対応）
  const isAdvancedEditEngine = ['black_forest_labs2_pro', 'black_forest_labs2_flex', 'nanobanana', 'nanobanana_pro', 'nanobanana2', 'grok_imagen', 'seedream4', 'seedream45', 'seedream5', 'gpt_image', 'gpt_image2', 'gpt_image25_sunburst', 'gpt_image25_flare', 'hotgen_image_v4_pro', 'hotgen_image_v51_pro', 'flux3_image', 'grok_imagine2', 'nanobanana2_lite'].includes(currentItem.engine);
  // 動画生成エンジンかどうかを判定（系統別）
  const isRenoiseVideoEngine = ['seedance2', 'seedance2_fast', 'seedance25', 'kling3_omni'].includes(currentItem.engine);
  // TopView参照動画モード: 動き・カメラワークの参照動画（メニュー固定）＋参照画像（人物は画像参照）
  const isTopviewOmniEngine = ['topview_t1_omni', 'seedance25_topview_omni', 'seedance2_topview_omni'].includes(currentItem.engine);
  const isTopviewI2vEngine = ['topview_t1_i2v', 'seedance25_topview_i2v', 'seedance2_topview_i2v'].includes(currentItem.engine);
  const isSousakuRefsEngine = ['hotgen_video_v5', 'seedance2_sousaku_refs', 'seedance25_sousaku_refs'].includes(currentItem.engine) || isTopviewOmniEngine;
  // start画像が任意の系統（画像なしならtext-to-video）・人物参照なし: fal / Runway Gen-4.5 / Gemini Omni Flash
  const isFalVideoEngine = ['minimax_h3_max_turbo', 'runway_gen45', 'gemini_omni_flash'].includes(currentItem.engine);
  // エンドフレーム非対応（start画像1枚まで）
  const isNoEndFrameVideoEngine = ['runway_gen45', 'gemini_omni_flash'].includes(currentItem.engine);
  const isI2vVideoEngine = ['hotgen_video_v4_pro', 'seedance2_sousaku_i2v', 'seedance25_sousaku_i2v'].includes(currentItem.engine) || isFalVideoEngine || isTopviewI2vEngine;
  const isSousakuVideoEngine = isSousakuRefsEngine || (isI2vVideoEngine && !isFalVideoEngine);
  const isVideoEngine = isRenoiseVideoEngine || isSousakuVideoEngine || isFalVideoEngine;
  const isHeyGenEngine = currentItem.engine === 'heygen';
  const isHeyGenText = isHeyGenEngine && (currentItem.heygenMode || 'text') === 'text';
  const isVideoEngineOrHeygen = isVideoEngine || isHeyGenEngine;
  // 動画の秒数範囲（2960が受け付ける値）
  const minVideoDurationSeconds = currentItem.engine === 'runway_gen45' ? 2 : currentItem.engine === 'gemini_omni_flash' ? 3 : currentItem.engine === 'minimax_h3_max_turbo' ? 5 : 4;
  const maxVideoDurationSeconds = isNoEndFrameVideoEngine ? 10 : ['topview_t1_omni', 'seedance25_topview_omni', 'topview_t1_i2v', 'seedance25_topview_i2v'].includes(currentItem.engine) ? 30 : 15;

  // 動画エンジン選択時: アスペクト比を16:9/9:16の二択に強制、秒数を必ず設定
  React.useEffect(() => {
    if (isVideoEngine && !isI2vVideoEngine) {
      // 動画(参照型): 「ユーザーが参照画像を渡す」をデフォルトON + {userimage} タグ挿入
      setUseEditImage(true);
      setCurrentItem((prev) => {
        let newPrompt = prev.prompt || '';
        if (!newPrompt.includes('{userimage}')) {
          newPrompt = '{userimage}\n' + newPrompt;
        }
        return { ...prev, prompt: newPrompt, use_edit_image: true };
      });
    }
    // 系統が変わったら、使わない方の人物参照データをクリア（混在防止）
    if (isI2vVideoEngine) {
      // i2v型は人物参照を使わない（スタート/エンドフレームのみ）→ 両方クリア
      if (videoPersonRefLabels.length > 0) {
        setVideoPersonRefLabels([]);
        setCurrentItem((prev) => ({ ...prev, videoPersonRefLabels: [], video_person_ref_labels: [] }));
      }
      if (videoPersonAssets.length > 0) {
        setVideoPersonAssets([]);
        setCurrentItem((prev) => ({ ...prev, videoPersonAssets: [], video_person_assets: [] }));
      }
    } else if (isRenoiseVideoEngine && videoPersonRefLabels.length > 0) {
      // Renoise系はアセットを使う → ラベルをクリア
      setVideoPersonRefLabels([]);
      setCurrentItem((prev) => ({ ...prev, videoPersonRefLabels: [], video_person_ref_labels: [] }));
    } else if (isSousakuRefsEngine && videoPersonAssets.length > 0) {
      // Sousaku refs型はラベルを使う → アセットをクリア
      setVideoPersonAssets([]);
      setCurrentItem((prev) => ({ ...prev, videoPersonAssets: [], video_person_assets: [] }));
    }
    if (isVideoEngine) {
      if (defaultAspectRatio !== '16:9' && defaultAspectRatio !== '9:16') {
        setDefaultAspectRatio('16:9');
        setCurrentItem((prev) => ({ ...prev, defaultAspectRatio: '16:9', default_aspect_ratio: '16:9' }));
      }
      const d = videoDurationSeconds && videoDurationSeconds >= minVideoDurationSeconds && videoDurationSeconds <= maxVideoDurationSeconds ? videoDurationSeconds : 5;
      setVideoDurationSeconds(d);
      setCurrentItem((prev) => ({ ...prev, videoDurationSeconds: d, video_duration_seconds: d }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideoEngine]);

  // 動画エンジンを切り替えて秒数が範囲外になったら5秒に戻す（Runway/Geminiは最大10秒など）
  React.useEffect(() => {
    if (!isVideoEngine || !videoDurationSeconds) return;
    if (videoDurationSeconds < minVideoDurationSeconds || videoDurationSeconds > maxVideoDurationSeconds) {
      setVideoDurationSeconds(5);
      setCurrentItem((prev) => ({ ...prev, videoDurationSeconds: 5, video_duration_seconds: 5 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minVideoDurationSeconds, maxVideoDurationSeconds]);

  // HeyGen選択時: アスペクト比16:9デフォルト
  React.useEffect(() => {
    if (isHeyGenEngine && defaultAspectRatio !== '16:9' && defaultAspectRatio !== '9:16') {
      setDefaultAspectRatio('16:9');
      setCurrentItem((prev) => ({ ...prev, defaultAspectRatio: '16:9', default_aspect_ratio: '16:9' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHeyGenEngine]);

  // Renoise系動画エンジン選択時にRenoise登録済みアセット一覧を取得
  React.useEffect(() => {
    if (isRenoiseVideoEngine && renoiseAssets.length === 0 && !isLoadingRenoiseAssets) {
      setIsLoadingRenoiseAssets(true);
      fetchRenoiseAssets().then((assets) => {
        setRenoiseAssets(assets);
        setIsLoadingRenoiseAssets(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRenoiseVideoEngine]);
  const maxRefImages = ['topview_t1_omni', 'seedance25_topview_omni'].includes(currentItem.engine) ? 30 : currentItem.engine === 'black_forest_labs2_pro' ? 7 : ['nanobanana', 'nanobanana_pro', 'nanobanana2'].includes(currentItem.engine) ? 14 : currentItem.engine === 'gpt_image' ? 16 : ['grok_imagen', 'seedream4'].includes(currentItem.engine) ? 10 : 9;
  // itemが変更されたときにcurrentItemを更新
  React.useEffect(() => {
    console.log('useEffect: item changed', item);
    console.log('useEffect: goodsOrder', item.goodsOrder);
    setCurrentItem({
      ...item,
      goodsOrder: item.goodsOrder || [],
      goods_order: item.goodsOrder || [] // バックエンド用にも設定
    });
    // FLUX.2用の状態も更新
    setUseEditImage(item.useEditImage || item.use_edit_image || false);
    setRefImageLabels(item.refImageLabels || item.ref_image_labels || []);
    // メニュー固定参照画像も更新
    setMenuReferenceImages(item.menuReferenceImages || item.menu_reference_images || []);
    // 動画エンジン用: 人物参照ラベルも更新
    setVideoPersonRefLabels(item.videoPersonRefLabels || item.video_person_ref_labels || []);
    setVideoPersonAssets(item.videoPersonAssets || item.video_person_assets || []);
    // デフォルトのアスペクト比も更新
    setDefaultAspectRatio(item.defaultAspectRatio || item.default_aspect_ratio || null);
    setVideoDurationSeconds(item.videoDurationSeconds || item.video_duration_seconds || 5);
    setVideoStartFrameMode((item.videoStartFrameMode || item.video_start_frame_mode || 'user') as 'user' | 'fixed');
    setVideoStartFrameUrl(item.videoStartFrameUrl || item.video_start_frame_url || '');
    setVideoEndFrameMode((item.videoEndFrameMode || item.video_end_frame_mode || (item.videoUseEndFrame || item.video_use_end_frame ? 'user' : 'none')) as 'none' | 'user' | 'fixed');
    setVideoEndFrameUrl(item.videoEndFrameUrl || item.video_end_frame_url || '');
    setVideoReferenceVideoUrl(item.videoReferenceVideoUrl || item.video_reference_video_url || '');
  }, [item]);
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    if (name === 'engine') {
      setCurrentItem((prev) => ({
        ...prev,
        engine: value as SupportedGenerationEngine,
      }));
    } else if (name === 'categoryId') {
      setCurrentItem((prev) => ({
        ...prev,
        categoryId: value === '' ? null : parseInt(value, 10),
      }));
    } else {
      setCurrentItem((prev) => ({
        ...prev,
        [name]: name === 'credit' ? parseInt(value, 10) || 0 : value,
      }));
    }
  };
  // サンプル元画像アップロード処理
  const handleSampleInputImageUpload = async (file: File | null) => {
    if (!file) return;
    setIsUploadingInput(true);
    try {
      const response = await uploadMenuImage(file, 'sample_input');
      setCurrentItem((prev) => ({
        ...prev,
        sampleInputImageUrl: response.public_url,
        sample_input_image_url: response.public_url, // バックエンド用フィールドも設定
      }));
    } catch (error) {
      console.error('サンプル元画像アップロードエラー:', error);
      alert('サンプル元画像のアップロードに失敗しました');
    } finally {
      setIsUploadingInput(false);
    }
  };
  // サンプル生成後画像アップロード処理
  const handleSampleResultImageUpload = async (file: File | null) => {
    if (!file) return;
    setIsUploadingResult(true);
    try {
      const response = await uploadMenuImage(file, 'sample_result');
      setCurrentItem((prev) => ({
        ...prev,
        sampleResultImageUrl: response.public_url,
        sample_result_image_url: response.public_url, // バックエンド用フィールドも設定
      }));
    } catch (error) {
      console.error('サンプル生成後画像アップロードエラー:', error);
      alert('サンプル生成後画像のアップロードに失敗しました');
    } finally {
      setIsUploadingResult(false);
    }
  };
  // 使い方説明画像アップロード処理
  const handleUsageGuideImageUpload = async (file: File | null) => {
    if (!file) return;
    setIsUploadingGuide(true);
    try {
      const response = await uploadMenuImage(file, 'usage_guide');
      setCurrentItem((prev) => ({
        ...prev,
        usageGuideImageUrl: response.public_url,
        usage_guide_image_url: response.public_url, // バックエンド用フィールドも設定
      }));
    } catch (error) {
      console.error('使い方説明画像アップロードエラー:', error);
      alert('使い方説明画像のアップロードに失敗しました');
    } finally {
      setIsUploadingGuide(false);
    }
  };

  // メニュー固定参照画像アップロード処理
  const handleMenuRefImageUpload = async (file: File | null) => {
    if (!file || !newMenuRefLabel.trim()) {
      alert('ラベルを入力してから画像を選択してください');
      return;
    }
    if (refImageLabels.length + menuReferenceImages.length >= maxRefImages) {
      alert(`参照画像は最大${maxRefImages}枚までです`);
      return;
    }
    setIsUploadingMenuRef(true);
    try {
      const response = await uploadMenuImage(file, 'menu_reference');
      const newRef = { label: newMenuRefLabel.trim(), url: response.public_url };
      const updatedMenuRefs = [...menuReferenceImages, newRef];
      setMenuReferenceImages(updatedMenuRefs);

      // refImageLabelsにも追加（menu_ref_N 形式で）
      const menuRefKey = `menu_ref_${updatedMenuRefs.length}`;
      const newLabels = [...refImageLabels, menuRefKey];
      setRefImageLabels(newLabels);

      // プロンプトにタグを追加
      const newIndex = newLabels.length;
      setCurrentItem((prev) => {
        let newPrompt = prev.prompt || '';
        const newTag = `{refimage${newIndex}}`;
        if (!newPrompt.includes(newTag)) {
          if (newPrompt.includes('{editimage}')) {
            const lastRefMatch = newPrompt.match(/\{refimage\d+\}/g);
            if (lastRefMatch) {
              const lastTag = lastRefMatch[lastRefMatch.length - 1];
              newPrompt = newPrompt.replace(lastTag, `${lastTag}\n${newTag}`);
            } else {
              newPrompt = newPrompt.replace('{editimage}', `{editimage}\n${newTag}`);
            }
          } else {
            newPrompt = newTag + '\n' + newPrompt;
          }
        }
        return {
          ...prev,
          prompt: newPrompt,
          ref_image_labels: newLabels,
          menuReferenceImages: updatedMenuRefs,
          menu_reference_images: updatedMenuRefs,
        };
      });

      setNewMenuRefLabel('');
    } catch (error) {
      console.error('メニュー固定参照画像アップロードエラー:', error);
      alert('メニュー固定参照画像のアップロードに失敗しました');
    } finally {
      setIsUploadingMenuRef(false);
    }
  };

  // メニュー固定参照画像削除処理
  const handleDeleteMenuRefImage = (index: number) => {
    const updatedMenuRefs = menuReferenceImages.filter((_, i) => i !== index);
    setMenuReferenceImages(updatedMenuRefs);

    // refImageLabelsからmenu_ref_を削除して振り直す
    const menuRefKey = `menu_ref_${index + 1}`;
    const labelIndex = refImageLabels.findIndex(label => label === menuRefKey);
    if (labelIndex !== -1) {
      const newLabels = refImageLabels.filter((_, i) => i !== labelIndex);
      // menu_ref_の番号を振り直す
      const renamedLabels = newLabels.map(label => {
        if (label.startsWith('menu_ref_')) {
          const oldNum = parseInt(label.replace('menu_ref_', ''));
          if (oldNum > index + 1) {
            return `menu_ref_${oldNum - 1}`;
          }
        }
        return label;
      });
      setRefImageLabels(renamedLabels);

      setCurrentItem((prev) => {
        let newPrompt = prev.prompt || '';
        // 全ての参照画像タグを削除
        for (let i = 1; i <= refImageLabels.length; i++) {
          newPrompt = newPrompt.replace(new RegExp(`\\{refimage${i}\\}\\n?`, 'g'), '');
        }
        // 新しいラベルに基づいてタグを再追加
        if (renamedLabels.length > 0) {
          const refTags = renamedLabels.map((_, i) => `{refimage${i + 1}}`).join('\n');
          if (newPrompt.includes('{editimage}')) {
            newPrompt = newPrompt.replace('{editimage}', `{editimage}\n${refTags}`);
          } else {
            newPrompt = refTags + '\n' + newPrompt;
          }
        }
        return {
          ...prev,
          prompt: newPrompt,
          ref_image_labels: renamedLabels,
          menuReferenceImages: updatedMenuRefs,
          menu_reference_images: updatedMenuRefs,
        };
      });
    } else {
      setCurrentItem((prev) => ({
        ...prev,
        menuReferenceImages: updatedMenuRefs,
        menu_reference_images: updatedMenuRefs,
      }));
    }
  };
  const handleUserInputChange = (
    index: number,
    field: 'label' | 'key' | 'multiline',
    value: string | boolean,
  ) => {
    const updatedFields = [...(currentItem.promptVariables || [])];
    updatedFields[index] = { ...updatedFields[index], [field]: value };
    setCurrentItem((prev) => ({ ...prev, promptVariables: updatedFields }));
  };
  const addUserInputField = () => {
    setCurrentItem((prev) => ({
      ...prev,
      promptVariables: [
        ...(prev.promptVariables || []),
        { key: '', label: '', multiline: false },
      ],
    }));
  };
  const removeUserInputField = (index: number) => {
    setCurrentItem((prev) => ({
      ...prev,
      promptVariables: prev.promptVariables?.filter((_, i) => i !== index),
    }));
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-xl space-y-4 overflow-y-auto max-h-[90vh] custom-scrollbar">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-lg font-semibold text-indigo-300">
            {item.id === 0 ? '新規生成メニュー追加' : `編集: ${item.name}`}
          </h4>
          <button
            type="button"
            onClick={() => setShowAssistantModal(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-md text-sm flex items-center"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            アシスタント
          </button>
        </div>
        <div>
          <label htmlFor="name" className="text-sm text-gray-400 block mb-1">
            メニュー名
          </label>
          <input
            id="name"
            name="name"
            value={currentItem.name || ''}
            onChange={handleChange}
            className="w-full bg-gray-700 p-2 rounded text-gray-200"
            required
          />
        </div>
        <div>
          <label
            htmlFor="categoryId"
            className="text-sm text-gray-400 block mb-1"
          >
            カテゴリ (任意)
          </label>
          <select
            id="categoryId"
            name="categoryId"
            value={currentItem.categoryId ?? ''}
            onChange={handleChange}
            className="w-full bg-gray-700 p-2 rounded text-gray-200"
          >
            <option value="">カテゴリなし</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor="description"
            className="text-sm text-gray-400 block mb-1"
          >
            簡易説明 (任意)
          </label>
          <textarea
            id="description"
            name="description"
            value={currentItem.description || ''}
            onChange={handleChange}
            rows={2}
            className="w-full bg-gray-700 p-2 rounded text-gray-200"
            placeholder="メニューの簡単な説明"
          />
        </div>
        <div>
          <label htmlFor="engine" className="text-sm text-gray-400 block mb-1">
            生成エンジン
          </label>
          <select
            id="engine"
            name="engine"
            value={currentItem.engine || 'nanobanana2'}
            onChange={handleChange}
            className="w-full bg-gray-700 p-2 rounded text-gray-200"
          >
            <option value="nanobanana_pro">Nanobanana Pro (Gemini 3 Pro) - 高品質（編集1枚+参照14枚）</option>
            <option value="nanobanana2">Nanobanana 2 (Gemini 3.1 Flash) - Pro品質+Flash速度（編集1枚+参照14枚）</option>
            <option value="nanobanana2_lite">Nanobanana 2 Lite - 1K専用・参照画像1枚</option>
            <option value="flux3_image">FLUX 3 Image (BFL) - 生成・編集（入力画像合計10枚・ネガティブ非対応）</option>
            <option value="black_forest_labs2_pro">FLUX.2 Pro - 高品質編集（編集1枚+参照7枚）</option>
            <option value="black_forest_labs2_flex">FLUX.2 Flex - 高速編集（編集1枚+参照9枚）</option>
            <option value="grok_imagine2">Grok Imagine Image 2.0 (xAI) - 生成・編集（入力画像合計5枚）</option>
            <option value="gpt_image2">GPT Image 2 (OpenAI) - 編集画像or参照画像対応</option>
            <option value="gpt_image25_sunburst">GPT Image 2.5 Sunburst (OpenAI) - 精度優先・編集画像or参照画像対応</option>
            <option value="gpt_image25_flare">GPT Image 2.5 Flare (OpenAI) - 速度優先・編集画像or参照画像対応</option>
            <option value="hotgen_image_v4_pro">HotGen Image v4 Pro - 高度編集対応（編集元1枚+参照画像）</option>
            <option value="hotgen_image_v51_pro">HotGen Image V5.1 Pro - 編集専用（編集元+参照 合計3枚まで）</option>
            <option value="seedance2">【動画】Seedance 2 (Renoise) - 人物はRenoiseアセット</option>
            <option value="seedance2_fast">【動画】Seedance 2 Fast (Renoise) - 高速版・人物はRenoiseアセット</option>
            <option value="seedance25">【動画】Seedance 2.5 (Renoise) - 人物はRenoiseアセット</option>
            <option value="kling3_omni">【動画】Kling 3 Omni (Renoise) - 人物はRenoiseアセット</option>
            <option value="seedance2_sousaku_refs">【動画】Seedance 2 refs (Sousaku AI) - 参照画像最大9枚</option>
            <option value="seedance2_sousaku_i2v">【動画】Seedance 2 i2v (Sousaku AI) - start画像必須</option>
            <option value="seedance25_sousaku_refs">【動画】Seedance 2.5 refs (Sousaku AI) - 参照画像最大9枚</option>
            <option value="seedance25_sousaku_i2v">【動画】Seedance 2.5 i2v (Sousaku AI) - start画像必須</option>
            <option value="hotgen_video_v5">【動画】HotGen Video v5 (Sousaku AI) - 参照画像最大9枚</option>
            <option value="hotgen_video_v4_pro">【動画】HotGen Video v4 Pro (Sousaku AI) - start画像必須</option>
            <option value="minimax_h3_max_turbo">【動画】MiniMax H3 Max Turbo (fal) - start/end画像は任意・5〜15秒</option>
            <option value="runway_gen45">【動画】Runway Gen-4.5 - start画像は任意（end画像不可）・2〜10秒・720p</option>
            <option value="gemini_omni_flash">【動画】Gemini Omni Flash (Google・preview) - start画像は任意（end画像不可）・3〜10秒・16:9/9:16</option>
            <option value="topview_t1_omni">【動画】TopView T1 参照動画 (TopView) - 参照動画1本＋参照画像最大30枚・4〜30秒</option>
            <option value="seedance25_topview_omni">【動画】Seedance 2.5 参照動画 (TopView) - 参照動画必須＋参照画像最大30枚・4〜30秒</option>
            <option value="seedance2_topview_omni">【動画】Seedance 2.0 参照動画 (TopView) - 参照動画必須＋参照画像最大9枚・4〜15秒</option>
            <option value="topview_t1_i2v">【動画】TopView T1 i2v (TopView) - start画像必須・4〜30秒</option>
            <option value="seedance25_topview_i2v">【動画】Seedance 2.5 i2v (TopView) - start画像必須・4〜30秒</option>
            <option value="seedance2_topview_i2v">【動画】Seedance 2.0 i2v (TopView) - start画像必須・4〜15秒</option>
            <option value="heygen">【動画】HeyGen (トーキングヘッド・喋る動画)</option>
            {/* 2960側で停止中のエンジンは新規選択肢に出さない（既存メニューが使っている場合のみ表示） */}
            {STOPPED_ENGINES.includes(currentItem.engine) && (
              <option value={currentItem.engine}>【停止中】{currentItem.engine}（2960で停止・別エンジンへ切替が必要）</option>
            )}
          </select>
        </div>
        {/* 高度な画像編集エンジン用の設定UI */}
        {(isRenoiseVideoEngine || isSousakuRefsEngine) && (
          <div className="bg-gray-700 p-4 rounded-md border border-pink-500">
            <h5 className="text-md font-semibold text-pink-400 mb-3">
              動画参照設定 — 人物参照
            </h5>
            <p className="text-xs text-gray-400 mb-3">
              {isRenoiseVideoEngine
                ? 'Renoise系エンジンの人物参照は、Renoise登録済みアセットからの選択のみです。選択するとプロンプトに {person1} などのタグが自動挿入されます（位置は編集可能）。'
                : 'Sousaku AI系エンジンの人物参照は、参照設定・マスタ参照・固定参照画像から追加します（画像として生成時に渡されます）。ユーザーによる任意アップロードはできません。'}
            </p>
            {/* Renoise登録済みアセット（直接参照・Renoise系のみ） */}
            {isRenoiseVideoEngine && videoPersonAssets.length > 0 && (
              <ul className="space-y-1 mb-3">
                {videoPersonAssets.map((rawAsset, assetIndex) => {
                  const asset = { asset_id: (rawAsset as any).asset_id ?? (rawAsset as any).assetId, name: rawAsset.name, thumbnail_url: (rawAsset as any).thumbnail_url ?? (rawAsset as any).thumbnailUrl };
                  return (
                  <li key={asset.asset_id} className="flex items-center justify-between bg-gray-600 px-3 py-1.5 rounded">
                    <span className="text-sm text-gray-200 flex items-center gap-2">
                      {asset.thumbnail_url ? (
                        <img src={asset.thumbnail_url} alt={asset.name} className="w-8 h-8 object-cover rounded" />
                      ) : (
                        <span>🎭</span>
                      )}
                      <code className="bg-pink-900/60 px-1 py-0.5 rounded text-pink-200">{`{person${assetIndex + 1}}`}</code>
                      <input
                        type="text"
                        value={(rawAsset as any).display_name ?? (rawAsset as any).displayName ?? ''}
                        onChange={(e) => {
                          const dn = e.target.value;
                          const newAssets = videoPersonAssets.map((a, i) =>
                            i === assetIndex ? { ...a, display_name: dn } : a,
                          );
                          setVideoPersonAssets(newAssets);
                          setCurrentItem((prev) => ({
                            ...prev,
                            videoPersonAssets: newAssets,
                            video_person_assets: newAssets,
                          }));
                        }}
                        placeholder="フロント表示名（例: 豊田愛）"
                        className="bg-gray-700 px-2 py-1 rounded text-gray-200 text-sm w-44 border border-pink-500/50"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="text-xs text-gray-400">{asset.name} (asset:{asset.asset_id})</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const removedIndex = assetIndex;
                        const newAssets = videoPersonAssets.filter((a) => ((a as any).asset_id ?? (a as any).assetId) !== asset.asset_id);
                        setVideoPersonAssets(newAssets);
                        setCurrentItem((prev) => {
                          // 削除したタグをプロンプトから除去し、後続のタグ番号を詰める
                          let newPrompt = prev.prompt || '';
                          newPrompt = newPrompt.replace(new RegExp(`\\{person${removedIndex + 1}\\}\\n?`, 'g'), '');
                          for (let k = removedIndex + 2; k <= videoPersonAssets.length; k++) {
                            newPrompt = newPrompt.replace(new RegExp(`\\{person${k}\\}`, 'g'), `{person${k - 1}}`);
                          }
                          return {
                            ...prev,
                            prompt: newPrompt,
                            videoPersonAssets: newAssets,
                            video_person_assets: newAssets,
                          };
                        });
                      }}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      削除
                    </button>
                  </li>
                );
                })}
              </ul>
            )}
            {isRenoiseVideoEngine && (
            <select
              className="w-full bg-gray-600 p-2 rounded text-gray-200 mb-2 border border-pink-500"
              defaultValue=""
              onChange={(e) => {
                const assetId = parseInt(e.target.value, 10);
                e.target.value = '';
                if (!assetId) return;
                if (videoPersonAssets.some((a) => ((a as any).asset_id ?? (a as any).assetId) === assetId)) return;
                const asset = renoiseAssets.find((a) => a.id === assetId);
                if (!asset) return;
                const newAssets = [...videoPersonAssets, { asset_id: asset.id, name: asset.name, thumbnail_url: asset.thumbnail_url }];
                setVideoPersonAssets(newAssets);
                setCurrentItem((prev) => {
                  // プロンプトに {personN} タグを挿入（N=アセットの並び順）
                  const tag = `{person${newAssets.length}}`;
                  let newPrompt = prev.prompt || '';
                  if (!newPrompt.includes(tag)) {
                    newPrompt = tag + '\n' + newPrompt;
                  }
                  return {
                    ...prev,
                    prompt: newPrompt,
                    videoPersonAssets: newAssets,
                    video_person_assets: newAssets,
                  };
                });
              }}
            >
              <option value="">
                {isLoadingRenoiseAssets ? '-- Renoiseアセット読み込み中... --' : `-- Renoise登録済みアセットから選択 (${renoiseAssets.length}件) --`}
              </option>
              {renoiseAssets.map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {asset.name} (ID: {asset.id})
                </option>
              ))}
            </select>
            )}
            {/* 追加済み人物参照リスト（Sousaku AI系のみ） */}
            {isSousakuRefsEngine && videoPersonRefLabels.length > 0 && (
              <ul className="space-y-1 mb-3">
                {videoPersonRefLabels.map((label) => {
                  // menu_ref_N は固定参照画像からサムネ・人物名を取得
                  let personName = '';
                  let personImageUrl = '';
                  const menuRefMatch = label.match(/^menu_ref_(\d+)$/);
                  if (menuRefMatch) {
                    const ref = menuReferenceImages[parseInt(menuRefMatch[1], 10) - 1];
                    if (ref) {
                      personName = ref.label;
                      personImageUrl = ref.url;
                    }
                  } else {
                    const staffRef = staffRefs.find((r) => r.reference_key === label);
                    if (staffRef) personName = staffRef.staff_name;
                  }
                  return (
                  <li key={label} className="flex items-center justify-between bg-gray-600 px-3 py-1.5 rounded">
                    <span className="text-sm text-gray-200 flex items-center gap-2">
                      {personImageUrl ? (
                        <img src={personImageUrl} alt={personName} className="w-8 h-8 object-cover rounded" />
                      ) : (
                        <span>👤</span>
                      )}
                      <code className="bg-pink-900/60 px-1 py-0.5 rounded text-pink-200">{label}</code>
                      {personName && <span>{personName}</span>}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const newLabels = videoPersonRefLabels.filter((l) => l !== label);
                        setVideoPersonRefLabels(newLabels);
                        setCurrentItem((prev) => ({
                          ...prev,
                          videoPersonRefLabels: newLabels,
                          video_person_ref_labels: newLabels,
                        }));
                      }}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      削除
                    </button>
                  </li>
                );
                })}
              </ul>
            )}
            {/* 参照設定・マスタ参照から人物追加（Sousaku AI refs型のみ） */}
            {isSousakuRefsEngine && (
            <select
              className="w-full bg-gray-600 p-2 rounded text-gray-200 mb-2"
              defaultValue=""
              onChange={(e) => {
                const key = e.target.value;
                if (!key || videoPersonRefLabels.includes(key)) {
                  e.target.value = '';
                  return;
                }
                const newLabels = [...videoPersonRefLabels, key];
                setVideoPersonRefLabels(newLabels);
                setCurrentItem((prev) => ({
                  ...prev,
                  videoPersonRefLabels: newLabels,
                  video_person_ref_labels: newLabels,
                }));
                e.target.value = '';
              }}
            >
              <option value="">-- 人物参照を追加 --</option>
              <optgroup label="参照設定（ユーザー個人）">
                <option value="ref_self1">ref_self1 - 自分（正面）</option>
                <option value="ref_self2">ref_self2 - 自分（笑顔）</option>
                <option value="ref_self3">ref_self3 - 自分（斜め）</option>
                <option value="ref_family1">ref_family1 - ファミリー1</option>
                <option value="ref_family2">ref_family2 - ファミリー2</option>
                <option value="ref_family3">ref_family3 - ファミリー3</option>
                <option value="ref_family4">ref_family4 - ファミリー4</option>
                <option value="ref_family5">ref_family5 - ファミリー5</option>
              </optgroup>
              <optgroup label="マスタ参照（カータレント）">
                {staffRefs.map((ref) => (
                  <option key={ref.reference_key} value={ref.reference_key}>
                    {ref.reference_key} - {ref.staff_name}
                  </option>
                ))}
              </optgroup>
            </select>
            )}
            {/* 固定参照画像から人物追加（アップロード・Sousaku AI refs型のみ） */}
            {isSousakuRefsEngine && (
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={newPersonRefLabel}
                onChange={(e) => setNewPersonRefLabel(e.target.value)}
                placeholder="人物名（固定参照画像として登録）"
                className="flex-1 bg-gray-600 p-2 rounded text-gray-200"
              />
              <label className={`px-4 py-2 rounded cursor-pointer text-white ${isUploadingPersonRef ? 'bg-gray-500' : 'bg-pink-600 hover:bg-pink-700'}`}>
                {isUploadingPersonRef ? 'アップロード中...' : '画像を選択'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={isUploadingPersonRef}
                  onChange={async (e) => {
                    const file = e.target.files?.[0] ?? null;
                    e.target.value = '';
                    if (!file) return;
                    if (!newPersonRefLabel.trim()) {
                      alert('人物名を入力してから画像を選択してください');
                      return;
                    }
                    setIsUploadingPersonRef(true);
                    try {
                      const response = await uploadMenuImage(file, 'menu_reference');
                      const newRef = { label: newPersonRefLabel.trim(), url: response.public_url };
                      const updatedMenuRefs = [...menuReferenceImages, newRef];
                      setMenuReferenceImages(updatedMenuRefs);
                      const menuRefKey = `menu_ref_${updatedMenuRefs.length}`;
                      const newLabels = [...videoPersonRefLabels, menuRefKey];
                      setVideoPersonRefLabels(newLabels);
                      setCurrentItem((prev) => ({
                        ...prev,
                        videoPersonRefLabels: newLabels,
                        video_person_ref_labels: newLabels,
                        menuReferenceImages: updatedMenuRefs,
                        menu_reference_images: updatedMenuRefs,
                      }));
                      setNewPersonRefLabel('');
                    } catch (error) {
                      console.error('人物固定参照画像アップロードエラー:', error);
                      alert('人物固定参照画像のアップロードに失敗しました');
                    } finally {
                      setIsUploadingPersonRef(false);
                    }
                  }}
                />
              </label>
            </div>
            )}
          </div>
        )}
        {isTopviewOmniEngine && (
          <div className="bg-gray-700 p-4 rounded-md border border-cyan-500">
            <h5 className="text-md font-semibold text-cyan-400 mb-3">
              参照動画（動き・カメラワーク）
            </h5>
            <p className="text-xs text-gray-400 mb-3">
              {currentItem.engine === 'topview_t1_omni'
                ? '参照動画の動き・カメラワークを元に動画を生成します（T1は参照動画なし・参照画像だけでも可）。'
                : '参照動画の動き・カメラワークを元に動画を生成します（Seedanceは参照動画が必須）。'}
              車両などはプロンプトと参照画像で指定します。MP4/WebM/MOV・30MBまで。参照動画の長さも課金対象になる場合があります。
            </p>
            <div className="flex items-center gap-3">
              {videoReferenceVideoUrl && (
                <video src={videoReferenceVideoUrl} className="w-32 h-20 object-cover rounded border border-cyan-500" muted controls />
              )}
              <label className={`px-3 py-1.5 rounded cursor-pointer text-white text-sm ${isUploadingReferenceVideo ? 'bg-gray-500' : 'bg-cyan-600 hover:bg-cyan-700'}`}>
                {isUploadingReferenceVideo ? 'アップロード中...' : (videoReferenceVideoUrl ? '動画を変更' : '動画を選択')}
                <input
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime"
                  className="hidden"
                  disabled={isUploadingReferenceVideo}
                  onChange={async (e) => {
                    const file = e.target.files?.[0] ?? null;
                    e.target.value = '';
                    if (!file) return;
                    if (file.size > 30 * 1024 * 1024) {
                      alert('参照動画は30MB以下にしてください');
                      return;
                    }
                    setIsUploadingReferenceVideo(true);
                    try {
                      const response = await uploadMenuImage(file, 'menu_reference_video');
                      setVideoReferenceVideoUrl(response.public_url);
                      setCurrentItem((prev) => ({
                        ...prev,
                        videoReferenceVideoUrl: response.public_url,
                        video_reference_video_url: response.public_url,
                      }));
                    } catch (error) {
                      alert(`参照動画のアップロードに失敗しました: ${error instanceof Error ? error.message : error}`);
                    } finally {
                      setIsUploadingReferenceVideo(false);
                    }
                  }}
                />
              </label>
              {videoReferenceVideoUrl && (
                <button
                  type="button"
                  className="px-3 py-1.5 rounded text-white text-sm bg-gray-600 hover:bg-gray-500"
                  onClick={() => {
                    setVideoReferenceVideoUrl('');
                    setCurrentItem((prev) => ({ ...prev, videoReferenceVideoUrl: '', video_reference_video_url: '' }));
                  }}
                >
                  外す
                </button>
              )}
            </div>
          </div>
        )}
        {isI2vVideoEngine && (
          <div className="bg-gray-700 p-4 rounded-md border border-amber-500">
            <h5 className="text-md font-semibold text-amber-400 mb-3">
              動画フレーム設定（i2v）
            </h5>
            <p className="text-xs text-gray-400 mb-3">
              {isFalVideoEngine
                ? 'スタートフレーム（開始画像）は任意です。指定なしならプロンプトだけのtext-to-video、指定ありならimage-to-videoになります。各フレームは「ユーザーが実行時にアップロード」か「メニューで固定」を選べます。'
                : 'スタートフレーム（開始画像・必須）から動画を生成します。各フレームは「ユーザーが実行時にアップロード」か「メニューで固定」を選べます。'}
            </p>
            {/* スタートフレーム */}
            <div className="mb-4">
              <label className="text-sm text-amber-300 block mb-1">スタートフレーム（{isFalVideoEngine ? '任意' : '必須'}）</label>
              <select
                value={videoStartFrameMode}
                onChange={(e) => {
                  const mode = e.target.value as 'user' | 'fixed';
                  setVideoStartFrameMode(mode);
                  setCurrentItem((prev) => ({
                    ...prev,
                    videoStartFrameMode: mode,
                    video_start_frame_mode: mode,
                  }));
                }}
                className="w-full bg-gray-600 p-2 rounded text-gray-200 mb-2"
              >
                <option value="user">ユーザーが実行時にアップロード</option>
                <option value="fixed">メニューで固定（画像をセット）</option>
              </select>
              {videoStartFrameMode === 'fixed' && (
                <div className="flex items-center gap-3">
                  {videoStartFrameUrl && (
                    <img src={videoStartFrameUrl} alt="start frame" className="w-16 h-10 object-cover rounded border border-amber-500" />
                  )}
                  <label className={`px-3 py-1.5 rounded cursor-pointer text-white text-sm ${isUploadingFrame ? 'bg-gray-500' : 'bg-amber-600 hover:bg-amber-700'}`}>
                    {isUploadingFrame ? 'アップロード中...' : (videoStartFrameUrl ? '画像を変更' : '画像を選択')}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={isUploadingFrame}
                      onChange={async (e) => {
                        const file = e.target.files?.[0] ?? null;
                        e.target.value = '';
                        if (!file) return;
                        setIsUploadingFrame(true);
                        try {
                          const response = await uploadMenuImage(file, 'menu_reference');
                          setVideoStartFrameUrl(response.public_url);
                          setCurrentItem((prev) => ({
                            ...prev,
                            videoStartFrameUrl: response.public_url,
                            video_start_frame_url: response.public_url,
                          }));
                        } catch (error) {
                          alert('スタートフレーム画像のアップロードに失敗しました');
                        } finally {
                          setIsUploadingFrame(false);
                        }
                      }}
                    />
                  </label>
                </div>
              )}
            </div>
            {/* エンドフレーム（Runway Gen-4.5 / Gemini Omni Flash は非対応） */}
            {!isNoEndFrameVideoEngine && (
            <div>
              <label className="text-sm text-amber-300 block mb-1">エンドフレーム（任意）</label>
              <select
                value={videoEndFrameMode}
                onChange={(e) => {
                  const mode = e.target.value as 'none' | 'user' | 'fixed';
                  setVideoEndFrameMode(mode);
                  setCurrentItem((prev) => ({
                    ...prev,
                    videoEndFrameMode: mode,
                    video_end_frame_mode: mode,
                    videoUseEndFrame: mode === 'user',
                    video_use_end_frame: mode === 'user',
                  }));
                }}
                className="w-full bg-gray-600 p-2 rounded text-gray-200 mb-2"
              >
                <option value="none">使用しない</option>
                <option value="user">ユーザーが実行時にアップロード</option>
                <option value="fixed">メニューで固定（画像をセット）</option>
              </select>
              {videoEndFrameMode === 'fixed' && (
                <div className="flex items-center gap-3">
                  {videoEndFrameUrl && (
                    <img src={videoEndFrameUrl} alt="end frame" className="w-16 h-10 object-cover rounded border border-amber-500" />
                  )}
                  <label className={`px-3 py-1.5 rounded cursor-pointer text-white text-sm ${isUploadingFrame ? 'bg-gray-500' : 'bg-amber-600 hover:bg-amber-700'}`}>
                    {isUploadingFrame ? 'アップロード中...' : (videoEndFrameUrl ? '画像を変更' : '画像を選択')}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={isUploadingFrame}
                      onChange={async (e) => {
                        const file = e.target.files?.[0] ?? null;
                        e.target.value = '';
                        if (!file) return;
                        setIsUploadingFrame(true);
                        try {
                          const response = await uploadMenuImage(file, 'menu_reference');
                          setVideoEndFrameUrl(response.public_url);
                          setCurrentItem((prev) => ({
                            ...prev,
                            videoEndFrameUrl: response.public_url,
                            video_end_frame_url: response.public_url,
                          }));
                        } catch (error) {
                          alert('エンドフレーム画像のアップロードに失敗しました');
                        } finally {
                          setIsUploadingFrame(false);
                        }
                      }}
                    />
                  </label>
                </div>
              )}
            </div>
            )}
          </div>
        )}
        {isHeyGenEngine && (
          <div className="bg-gray-700 p-4 rounded-md border border-teal-500 space-y-3">
            <h5 className="text-md font-semibold text-teal-400">
              HeyGen設定（トーキングヘッド）
            </h5>
            {/* モード選択 */}
            <div>
              <label className="text-sm text-gray-400 block mb-1">モード</label>
              <select
                value={currentItem.heygenMode || 'text'}
                onChange={(e) => setCurrentItem((prev) => ({ ...prev, heygenMode: e.target.value }))}
                className="w-full bg-gray-600 p-2 rounded text-gray-200 text-sm"
              >
                <option value="text">テキストをセット（テキストを喋らせる）</option>
                <option value="audio">音源をセット（音声ファイルで喋らせる）</option>
              </select>
            </div>
            <p className="text-xs text-gray-400">
              人物画像はユーザーが実行時にアップロードします。{isHeyGenText ? '' : '音声ファイルもユーザーが実行時にアップロードします。'}
            </p>
            {isHeyGenText && (
              <>
                {/* 音声: 男性/女性/voice_id */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">音声（voice）</label>
                    <select
                      value={currentItem.heygenVoiceGender || 'male'}
                      onChange={(e) => setCurrentItem((prev) => ({ ...prev, heygenVoiceGender: e.target.value }))}
                      className="w-full bg-gray-600 p-2 rounded text-gray-200 text-sm"
                    >
                      <option value="male">男性</option>
                      <option value="female">女性</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">voice_id（任意・優先）</label>
                    <input
                      type="text"
                      value={currentItem.heygenVoiceId || ''}
                      onChange={(e) => setCurrentItem((prev) => ({ ...prev, heygenVoiceId: e.target.value }))}
                      placeholder="HeyGen voice_id"
                      className="w-full bg-gray-600 p-2 rounded text-gray-200 text-sm"
                    />
                  </div>
                </div>
                {/* 喋らせるテキスト（固定台本） */}
                <div>
                  <label className="text-sm text-gray-400 block mb-1">喋らせるテキスト（固定台本）</label>
                  <textarea
                    value={currentItem.heygenDefaultScript || ''}
                    onChange={(e) => setCurrentItem((prev) => ({ ...prev, heygenDefaultScript: e.target.value }))}
                    rows={4}
                    placeholder={'こんにちは。\n\n今日は新しい車を紹介します。'}
                    className="w-full bg-gray-600 p-2 rounded text-gray-200 text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">短い間は「、」「。」、長めの間は空行を使います</p>
                </div>
                {/* ユーザーに追加テキストを入れさせる */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="heygenAllowUserScript"
                    checked={!!currentItem.heygenAllowUserScript}
                    onChange={(e) => setCurrentItem((prev) => ({ ...prev, heygenAllowUserScript: e.target.checked }))}
                    className="w-4 h-4 text-teal-600 bg-gray-600 border-gray-500 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="heygenAllowUserScript" className="ml-2 text-gray-200 text-sm">ユーザーに追加テキストを入れさせる</label>
                </div>
                {currentItem.heygenAllowUserScript && (
                  <div className="ml-6">
                    <label className="text-sm text-gray-400 block mb-1">最大文字数</label>
                    <input
                      type="number"
                      min={1}
                      value={currentItem.heygenUserScriptMaxLength ?? 200}
                      onChange={(e) => setCurrentItem((prev) => ({ ...prev, heygenUserScriptMaxLength: parseInt(e.target.value, 10) || 200 }))}
                      className="w-32 bg-gray-600 p-2 rounded text-gray-200 text-sm"
                    />
                    <p className="text-xs text-gray-400 mt-1">ユーザーの追加テキストは固定台本の後に連結されます</p>
                  </div>
                )}
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-400 block mb-1">表現の強さ</label>
                <select
                  value={currentItem.heygenExpressiveness || 'medium'}
                  onChange={(e) => setCurrentItem((prev) => ({ ...prev, heygenExpressiveness: e.target.value }))}
                  className="w-full bg-gray-600 p-2 rounded text-gray-200 text-sm"
                >
                  <option value="low">控えめ</option>
                  <option value="medium">標準</option>
                  <option value="high">大きめ</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">解像度</label>
                <select
                  value={currentItem.heygenResolution || '720p'}
                  onChange={(e) => setCurrentItem((prev) => ({ ...prev, heygenResolution: e.target.value }))}
                  className="w-full bg-gray-600 p-2 rounded text-gray-200 text-sm"
                >
                  <option value="720p">720p</option>
                  <option value="1080p">1080p</option>
                  <option value="4k">4k</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">動きの指示（motion_prompt・任意）</label>
              <input
                type="text"
                value={currentItem.heygenMotionPrompt || ''}
                onChange={(e) => setCurrentItem((prev) => ({ ...prev, heygenMotionPrompt: e.target.value }))}
                placeholder="例: natural upper-body movement, friendly eye contact"
                className="w-full bg-gray-600 p-2 rounded text-gray-200 text-sm"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="heygenRemoveBg"
                checked={!!currentItem.heygenRemoveBackground}
                onChange={(e) => setCurrentItem((prev) => ({ ...prev, heygenRemoveBackground: e.target.checked }))}
                className="w-4 h-4 text-teal-600 bg-gray-600 border-gray-500 rounded focus:ring-teal-500"
              />
              <label htmlFor="heygenRemoveBg" className="ml-2 text-gray-200 text-sm">背景を除去する</label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="heygenCharacterAvatar"
                checked={!!currentItem.heygenCharacterAvatar}
                onChange={(e) => setCurrentItem((prev) => ({ ...prev, heygenCharacterAvatar: e.target.checked }))}
                className="w-4 h-4 text-teal-600 bg-gray-600 border-gray-500 rounded focus:ring-teal-500"
              />
              <label htmlFor="heygenCharacterAvatar" className="ml-2 text-gray-200 text-sm">キャラクターアバター（人物以外の画像を喋らせる場合にON）</label>
            </div>
          </div>
        )}
        {(isAdvancedEditEngine || isRenoiseVideoEngine || isSousakuRefsEngine) && (
          <div className="bg-gray-700 p-4 rounded-md border border-indigo-500">
            <h5 className="text-md font-semibold text-indigo-400 mb-3">
              {isVideoEngine ? '動画参照設定 — 参照画像（人物以外）' : '画像編集設定'}
            </h5>
            <div className="space-y-4">
              {/* 編集画像チェックボックス（動画時はユーザー参照画像） */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="useEditImage"
                  checked={useEditImage}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setUseEditImage(checked);
                    if (isVideoEngine) {
                      // 動画: {userimage} タグを挿入/除去（ユーザー画像は参照画像として渡される）
                      setCurrentItem((prev) => {
                        let newPrompt = prev.prompt || '';
                        if (checked && !newPrompt.includes('{userimage}')) {
                          newPrompt = '{userimage}\n' + newPrompt;
                        } else if (!checked) {
                          newPrompt = newPrompt.replace(/\{userimage\}\n?/g, '');
                        }
                        return { ...prev, prompt: newPrompt, use_edit_image: checked };
                      });
                      return;
                    }
                    // 画像編集: プロンプトにタグを追加/削除
                    setCurrentItem((prev) => {
                      let newPrompt = prev.prompt || '';
                      if (checked && !newPrompt.includes('{editimage}')) {
                        newPrompt = '{editimage}\n' + newPrompt;
                      } else if (!checked) {
                        newPrompt = newPrompt.replace(/\{editimage\}\n?/g, '');
                      }
                      return { ...prev, prompt: newPrompt, use_edit_image: checked };
                    });
                  }}
                  className="w-4 h-4 text-indigo-600 bg-gray-600 border-gray-500 rounded focus:ring-indigo-500"
                />
                <label htmlFor="useEditImage" className="ml-2 text-gray-200">
                  {isVideoEngine ? 'ユーザーが参照画像を渡す（実行時にアップロード）' : '編集画像を利用する'}
                </label>
              </div>


              {/* 参照画像の追加 */}
              <div>
                <label className="text-sm text-gray-400 block mb-2">
                  参照画像 (最大{maxRefImages}枚)
                </label>

                {/* フリー入力（元の機能） */}
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newRefLabel}
                    onChange={(e) => setNewRefLabel(e.target.value)}
                    placeholder="参照画像のラベル（例: 人物の画像）"
                    className="flex-1 bg-gray-600 p-2 rounded text-gray-200"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!newRefLabel.trim()) return;
                      if (refImageLabels.length >= maxRefImages) {
                        alert(`参照画像は最大${maxRefImages}枚までです`);
                        return;
                      }
                      const newLabels = [...refImageLabels, newRefLabel.trim()];
                      setRefImageLabels(newLabels);
                      setNewRefLabel('');
                      // プロンプトにタグを追加
                      const newIndex = newLabels.length;
                      setCurrentItem((prev) => {
                        let newPrompt = prev.prompt || '';
                        const newTag = `{refimage${newIndex}}`;
                        if (!newPrompt.includes(newTag)) {
                          if (newPrompt.includes('{editimage}')) {
                            const lastRefMatch = newPrompt.match(/\{refimage\d+\}/g);
                            if (lastRefMatch) {
                              const lastTag = lastRefMatch[lastRefMatch.length - 1];
                              newPrompt = newPrompt.replace(lastTag, `${lastTag}\n${newTag}`);
                            } else {
                              newPrompt = newPrompt.replace('{editimage}', `{editimage}\n${newTag}`);
                            }
                          } else {
                            newPrompt = newTag + '\n' + newPrompt;
                          }
                        }
                        return { ...prev, prompt: newPrompt, ref_image_labels: newLabels };
                      });
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  >
                    追加
                  </button>
                </div>

                {/* 参照設定から追加 */}
                <div className="flex flex-col gap-2 mb-3">
                  <select
                    className="w-full bg-gray-600 p-2 rounded text-gray-200"
                    defaultValue=""
                    onChange={(e) => {
                      const selectedKey = e.target.value;
                      if (!selectedKey) return;

                      // テキスト参照かどうかを判定
                      const isTextRef = selectedKey === 'car_numbertext' || selectedKey === 'car_nickname';

                      if (isTextRef) {
                        // テキスト参照の場合: プロンプトに直接タグを追加（ref_image_labelsには追加しない）
                        setCurrentItem((prev) => {
                          let newPrompt = prev.prompt || '';
                          const newTag = `{${selectedKey}}`;
                          if (!newPrompt.includes(newTag)) {
                            if (newPrompt.includes('{editimage}')) {
                              const lastRefMatch = newPrompt.match(/\{refimage\d+\}/g);
                              if (lastRefMatch) {
                                const lastTag = lastRefMatch[lastRefMatch.length - 1];
                                newPrompt = newPrompt.replace(lastTag, `${lastTag}\n${newTag}`);
                              } else {
                                newPrompt = newPrompt.replace('{editimage}', `{editimage}\n${newTag}`);
                              }
                            } else {
                              newPrompt = newTag + '\n' + newPrompt;
                            }
                          }
                          return { ...prev, prompt: newPrompt };
                        });
                        e.target.value = '';
                        return;
                      }

                      // 画像参照の場合: 既存のロジック
                      if (refImageLabels.length >= maxRefImages) {
                        alert(`参照画像は最大${maxRefImages}枚までです`);
                        e.target.value = '';
                        return;
                      }
                      const newLabels = [...refImageLabels, selectedKey];
                      setRefImageLabels(newLabels);
                      // プロンプトにタグを追加
                      const newIndex = newLabels.length;
                      setCurrentItem((prev) => {
                        let newPrompt = prev.prompt || '';
                        const newTag = `{refimage${newIndex}}`;
                        if (!newPrompt.includes(newTag)) {
                          if (newPrompt.includes('{editimage}')) {
                            const lastRefMatch = newPrompt.match(/\{refimage\d+\}/g);
                            if (lastRefMatch) {
                              const lastTag = lastRefMatch[lastRefMatch.length - 1];
                              newPrompt = newPrompt.replace(lastTag, `${lastTag}\n${newTag}`);
                            } else {
                              newPrompt = newPrompt.replace('{editimage}', `{editimage}\n${newTag}`);
                            }
                          } else {
                            newPrompt = newTag + '\n' + newPrompt;
                          }
                        }
                        return { ...prev, prompt: newPrompt, ref_image_labels: newLabels };
                      });
                      e.target.value = '';
                    }}
                  >
                    <option value="">-- 参照設定から追加（個別） --</option>
                    <optgroup label="自分リファレンス">
                      <option value="ref_self1">ref_self1 - 自分（正面）</option>
                      <option value="ref_self2">ref_self2 - 自分（笑顔）</option>
                      <option value="ref_self3">ref_self3 - 自分（斜め）</option>
                    </optgroup>
                    <optgroup label="愛車リファレンス">
                      <option value="car_numbertext">car_numbertext - 愛車（ナンバーテキスト）</option>
                      <option value="car_number">car_number - 愛車（ナンバー画像）</option>
                      <option value="car_nickname">car_nickname - 愛車（ニックネーム）</option>
                      <option value="car_front">car_front - 愛車（フロント正面）</option>
                      <option value="car_side">car_side - 愛車（真横）</option>
                      <option value="car_rear">car_rear - 愛車（リア正面）</option>
                      <option value="car_front_angled_7_3">car_front_angled_7_3 - 愛車（斜め前7:3）</option>
                      <option value="car_rear_angled_7_3">car_rear_angled_7_3 - 愛車（斜め後ろ7:3）</option>
                    </optgroup>
                    <optgroup label="ファミリーリファレンス">
                      <option value="ref_family1">ref_family1 - ファミリー1</option>
                      <option value="ref_family2">ref_family2 - ファミリー2</option>
                      <option value="ref_family3">ref_family3 - ファミリー3</option>
                      <option value="ref_family4">ref_family4 - ファミリー4</option>
                      <option value="ref_family5">ref_family5 - ファミリー5</option>
                    </optgroup>
                    <optgroup label="お気に入りリファレンス">
                      <option value="ref_other1">ref_other1 - お気に入り1</option>
                      <option value="ref_other2">ref_other2 - お気に入り2</option>
                      <option value="ref_other3">ref_other3 - お気に入り3</option>
                      <option value="ref_other4">ref_other4 - お気に入り4</option>
                      <option value="ref_other5">ref_other5 - お気に入り5</option>
                    </optgroup>
                    <optgroup label="ガレージリファレンス">
                      <option value="ref_garage1">ref_garage1 - ガレージ1</option>
                      <option value="ref_garage2">ref_garage2 - ガレージ2</option>
                      <option value="ref_garage3">ref_garage3 - ガレージ3</option>
                      <option value="ref_garage4">ref_garage4 - ガレージ4</option>
                      <option value="ref_garage5">ref_garage5 - ガレージ5</option>
                    </optgroup>
                  </select>
                  {/* マスタ参照から追加（共通） */}
                  <select
                    className="w-full bg-purple-900 p-2 rounded text-purple-100 border border-purple-600"
                    defaultValue=""
                    onChange={(e) => {
                      const selectedKey = e.target.value;
                      if (!selectedKey) return;
                      if (refImageLabels.length >= maxRefImages) {
                        alert(`参照画像は最大${maxRefImages}枚までです`);
                        e.target.value = '';
                        return;
                      }
                      const newLabels = [...refImageLabels, selectedKey];
                      setRefImageLabels(newLabels);
                      const newIndex = newLabels.length;
                      setCurrentItem((prev) => {
                        let newPrompt = prev.prompt || '';
                        const newTag = `{refimage${newIndex}}`;
                        if (!newPrompt.includes(newTag)) {
                          if (newPrompt.includes('{editimage}')) {
                            const lastRefMatch = newPrompt.match(/\{refimage\d+\}/g);
                            if (lastRefMatch) {
                              const lastTag = lastRefMatch[lastRefMatch.length - 1];
                              newPrompt = newPrompt.replace(lastTag, `${lastTag}\n${newTag}`);
                            } else {
                              newPrompt = newPrompt.replace('{editimage}', `{editimage}\n${newTag}`);
                            }
                          } else {
                            newPrompt = newTag + '\n' + newPrompt;
                          }
                        }
                        return { ...prev, prompt: newPrompt, ref_image_labels: newLabels };
                      });
                      e.target.value = '';
                    }}
                  >
                    <option value="">-- マスタ参照から追加（共通） --</option>
                    <optgroup label="カータレント">
                      {staffRefs.map((ref) => (
                        <option key={ref.reference_key} value={ref.reference_key}>
                          {ref.reference_key} - {ref.staff_name}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="カーモデル">
                      {carModelRefs.map((ref) => (
                        <option key={ref.reference_key} value={ref.reference_key}>
                          {ref.reference_key} - {ref.model_name}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="カースポット">
                      {carSpotRefs.map((ref) => (
                        <option key={ref.reference_key} value={ref.reference_key}>
                          {ref.reference_key} - {ref.spot_name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>

                {/* メニュー固定参照画像セクション */}
                <div className="mt-4 p-3 bg-orange-900/30 border border-orange-600/50 rounded-lg">
                  <label className="text-sm text-orange-300 block mb-2 font-semibold">
                    メニュー固定参照画像を追加（S3にアップロード）
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newMenuRefLabel}
                      onChange={(e) => setNewMenuRefLabel(e.target.value)}
                      placeholder="参照画像のラベル（例: 背景画像）"
                      className="flex-1 bg-gray-700 p-2 rounded text-gray-200"
                    />
                    <label className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 cursor-pointer flex items-center">
                      {isUploadingMenuRef ? (
                        <span>アップロード中...</span>
                      ) : (
                        <span>画像を選択</span>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={isUploadingMenuRef || !newMenuRefLabel.trim()}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleMenuRefImageUpload(file);
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-400">
                    ラベルを入力してから画像を選択してください。この画像はメニューに固定され、生成時に自動で使用されます。
                  </p>

                  {/* 登録済みメニュー固定参照画像リスト */}
                  {menuReferenceImages.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs text-orange-300">登録済みメニュー固定参照画像:</p>
                      {menuReferenceImages.map((ref, index) => (
                        // 人物参照として使用中のものはこのリストに出さない（上の人物参照セクションで管理）
                        videoPersonRefLabels.includes(`menu_ref_${index + 1}`) ? null : (
                        <div key={index} className="flex items-center justify-between bg-gray-700 p-2 rounded">
                          <div className="flex items-center gap-2">
                            <img
                              src={ref.url}
                              alt={ref.label}
                              className="w-12 h-12 object-cover rounded"
                            />
                            <span className="text-gray-200">
                              <code className="bg-orange-800 px-1 py-0.5 rounded text-orange-200 mr-2">
                                menu_ref_{index + 1}
                              </code>
                              {ref.label}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteMenuRefImage(index)}
                            className="px-2 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                          >
                            削除
                          </button>
                        </div>
                        )
                      ))}
                    </div>
                  )}
                </div>

                {/* 追加済み参照画像リスト */}
                {refImageLabels.length > 0 && (
                  <div className="space-y-2">
                    {refImageLabels.map((label, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-600 p-2 rounded">
                        <span className="text-gray-200">
                          <code className="bg-gray-700 px-1 py-0.5 rounded text-indigo-300 mr-2">
                            {`{refimage${index + 1}}`}
                          </code>
                          {label.startsWith('menu_ref_') ? (
                            <span className="text-orange-400">{label}</span>
                          ) : label.startsWith('ref_') || label.startsWith('car_') ? (
                            <span className="text-green-400">{label}</span>
                          ) : (
                            label
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            const newLabels = refImageLabels.filter((_, i) => i !== index);
                            setRefImageLabels(newLabels);
                            // プロンプトからタグを削除し、残りのタグ番号を振り直す
                            setCurrentItem((prev) => {
                              let newPrompt = prev.prompt || '';
                              // 全ての参照画像タグを削除
                              for (let i = 1; i <= refImageLabels.length; i++) {
                                newPrompt = newPrompt.replace(new RegExp(`\\{refimage${i}\\}\\n?`, 'g'), '');
                              }
                              // 新しいラベルに基づいてタグを再追加
                              if (newLabels.length > 0) {
                                const refTags = newLabels.map((_, i) => `{refimage${i + 1}}`).join('\n');
                                if (newPrompt.includes('{editimage}')) {
                                  newPrompt = newPrompt.replace('{editimage}', `{editimage}\n${refTags}`);
                                } else {
                                  newPrompt = refTags + '\n' + newPrompt;
                                }
                              }
                              return { ...prev, prompt: newPrompt, ref_image_labels: newLabels };
                            });
                          }}
                          className="px-2 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                        >
                          削除
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              チェック・追加すると、プロンプトに自動で <code className="bg-gray-600 px-1 py-0.5 rounded text-indigo-300">{'{editimage}'}</code> や <code className="bg-gray-600 px-1 py-0.5 rounded text-indigo-300">{'{refimage1}'}</code> などのタグが挿入されます。
              <br />
              <span className="text-green-400">緑色</span>のラベルはユーザーの参照設定から自動取得されます。
              <span className="text-orange-400">オレンジ色</span>のラベルはメニュー固定参照画像（事前登録済み）です。
              それ以外はユーザーが画像をアップロードします。
            </p>
          </div>
        )}
        <div>
          <label htmlFor="prompt" className="text-sm text-gray-400 block mb-1">
            生成プロンプト
          </label>
          <textarea
            id="prompt"
            name="prompt"
            value={currentItem.prompt || ''}
            onChange={handleChange}
            rows={4}
            className="w-full bg-gray-700 p-2 rounded text-gray-200"
            placeholder="例: A futuristic sports car, {{user_color}}, driving on Mars."
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            ユーザー入力は{' '}
            <code className="bg-gray-600 px-1 py-0.5 rounded text-indigo-300">{`{{placeholder_name}}`}</code>{' '}
            形式で挿入できます。
          </p>
        </div>
        <div>
          <label
            htmlFor="negativePrompt"
            className="text-sm text-gray-400 block mb-1"
          >
            ネガティブプロンプト (任意)
          </label>
          <textarea
            id="negativePrompt"
            name="negativePrompt"
            value={currentItem.negativePrompt || ''}
            onChange={handleChange}
            rows={2}
            className="w-full bg-gray-700 p-2 rounded text-gray-200"
            placeholder="例: blurry, watermark, text"
          />
        </div>
        <div className="border-t border-gray-700 pt-3 mt-3">
          <h5 className="text-md font-semibold text-indigo-400 mb-2">
            サンプル画像 (任意)
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 block mb-1">
                サンプル元画像
              </label>
              <ImageUpload
                onImageSelect={handleSampleInputImageUpload}
                label="サンプル元画像をアップロード"
                initialPreviewUrl={currentItem.sample_input_image_url || currentItem.sampleInputImageUrl}
                showDeleteButton={true}
              />
              {isUploadingInput && (
                <p className="text-xs text-yellow-400 mt-1">アップロード中...</p>
              )}
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">
                サンプル生成後画像
              </label>
              <ImageUpload
                onImageSelect={handleSampleResultImageUpload}
                label="サンプル生成後画像をアップロード"
                initialPreviewUrl={currentItem.sample_result_image_url || currentItem.sampleResultImageUrl}
                showDeleteButton={true}
              />
              {isUploadingResult && (
                <p className="text-xs text-yellow-400 mt-1">アップロード中...</p>
              )}
            </div>
          </div>
        </div>
        <div className="border-t border-gray-700 pt-3 mt-3">
          <h5 className="text-md font-semibold text-indigo-400 mb-2">
            使い方説明画像 (任意)
          </h5>
          <div className="max-w-md">
            <ImageUpload
              onImageSelect={handleUsageGuideImageUpload}
              label="使い方説明画像をアップロード"
              initialPreviewUrl={currentItem.usage_guide_image_url || currentItem.usageGuideImageUrl}
              showDeleteButton={true}
            />
            {isUploadingGuide && (
              <p className="text-xs text-yellow-400 mt-1">アップロード中...</p>
            )}
          </div>
        </div>
        <div className="border-t border-gray-700 pt-3 mt-3">
          <h5 className="text-md font-semibold text-indigo-400 mb-2">
            ユーザー追加入力項目設定 (任意)
          </h5>
          {currentItem.promptVariables &&
            currentItem.promptVariables.map((field, index) => (
              <div
                key={index}
                className="mb-3 p-3 border border-gray-600 rounded-md space-y-2 relative"
              >
                <p className="text-xs text-gray-400">入力項目 #{index + 1}</p>
                <div>
                  <label
                    htmlFor={`label-${index}`}
                    className="text-sm text-gray-400 block mb-1"
                  >
                    {field.label.startsWith('追加入力: ')
                      ? field.label.substring('追加入力: '.length)
                      : field.label}
                  </label>
                  <input
                    id={`label-${index}`}
                    value={field.label}
                    onChange={(e) =>
                      handleUserInputChange(index, 'label', e.target.value)
                    }
                    className="w-full bg-gray-700 p-2 rounded text-gray-200"
                    placeholder="例: 車の色, 背景テーマ"
                  />
                </div>
                <div>
                  <label
                    htmlFor={`key-${index}`}
                    className="text-sm text-gray-400 block mb-1"
                  >
                    プロンプト内プレースホルダー名
                  </label>
                  <input
                    id={`key-${index}`}
                    value={field.key}
                    onChange={(e) =>
                      handleUserInputChange(index, 'key', e.target.value)
                    }
                    className="w-full bg-gray-700 p-2 rounded text-gray-200"
                    placeholder="例: user_color (英数字とアンダースコア)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    プロンプト内で{' '}
                    <code className="bg-gray-600 px-1 py-0.5 rounded text-indigo-300">{`{{${field.key || 'placeholder_name'}}}`}</code>{' '}
                    として使用
                  </p>
                </div>
                <div className="mt-2">
                  <label className="flex items-center space-x-2 text-sm text-gray-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={field.multiline || false}
                      onChange={(e) =>
                        handleUserInputChange(index, 'multiline', e.target.checked)
                      }
                      className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-indigo-500 focus:ring-indigo-500"
                    />
                    <span>複数行対応</span>
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => removeUserInputField(index)}
                  className="absolute top-2 right-2 p-1 text-red-400 hover:text-red-300"
                  aria-label="この入力項目を削除"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          <button
            type="button"
            onClick={addUserInputField}
            className="mt-1 text-sm bg-gray-600 hover:bg-gray-500 text-gray-300 px-3 py-1.5 rounded flex items-center"
          >
            <PlusCircleIcon className="w-4 h-4 mr-1.5" /> テキスト入力項目を追加
          </button>
        </div>
        <div>
          <label
            htmlFor="credit"
            className="text-sm text-gray-400 block mb-1 mt-3"
          >
            消費クレジット
          </label>
          <input
            id="credit"
            type="number"
            name="credit"
            value={currentItem.credit || 0}
            onChange={handleChange}
            className="w-full bg-gray-700 p-2 rounded text-gray-200"
          />
        </div>
        <div>
          <label className="text-sm text-gray-400 block mb-1 mt-3">
            アイテム作成
          </label>
          <div className="flex items-center space-x-6">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="goods_order_suzuri"
                checked={currentItem.goodsOrder?.includes('suzuri') || false}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setCurrentItem(prev => {
                    const newGoodsOrder = isChecked 
                      ? [...(prev.goodsOrder || []).filter(s => s !== 'suzuri'), 'suzuri']
                      : (prev.goodsOrder || []).filter(s => s !== 'suzuri');
                    return {
                      ...prev,
                      goodsOrder: newGoodsOrder,
                      goods_order: newGoodsOrder // バックエンド用にも設定
                    };
                  });
                }}
                className="rounded bg-gray-700 text-indigo-500 focus:ring-indigo-500"
              />
              <span className="text-gray-200">SUZURI</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="goods_order_printful"
                checked={currentItem.goodsOrder?.includes('printful') || false}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setCurrentItem(prev => {
                    const newGoodsOrder = isChecked 
                      ? [...(prev.goodsOrder || []).filter(s => s !== 'printful'), 'printful']
                      : (prev.goodsOrder || []).filter(s => s !== 'printful');
                    return {
                      ...prev,
                      goodsOrder: newGoodsOrder,
                      goods_order: newGoodsOrder // バックエンド用にも設定
                    };
                  });
                }}
                className="rounded bg-gray-700 text-indigo-500 focus:ring-indigo-500"
              />
              <span className="text-gray-200">Printful</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="goods_order_artisan"
                checked={currentItem.goodsOrder?.includes('artisan') || false}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setCurrentItem(prev => {
                    const newGoodsOrder = isChecked
                      ? [...(prev.goodsOrder || []).filter(s => s !== 'artisan'), 'artisan']
                      : (prev.goodsOrder || []).filter(s => s !== 'artisan');
                    return {
                      ...prev,
                      goodsOrder: newGoodsOrder,
                      goods_order: newGoodsOrder // バックエンド用にも設定
                    };
                  });
                }}
                className="rounded bg-gray-700 text-indigo-500 focus:ring-indigo-500"
              />
              <span className="text-gray-200">Artisan</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="goods_order_prodigi"
                checked={currentItem.goodsOrder?.includes('prodigi') || false}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setCurrentItem(prev => {
                    const newGoodsOrder = isChecked
                      ? [...(prev.goodsOrder || []).filter(s => s !== 'prodigi'), 'prodigi']
                      : (prev.goodsOrder || []).filter(s => s !== 'prodigi');
                    return {
                      ...prev,
                      goodsOrder: newGoodsOrder,
                      goods_order: newGoodsOrder // バックエンド用にも設定
                    };
                  });
                }}
                className="rounded bg-gray-700 text-indigo-500 focus:ring-indigo-500"
              />
              <span className="text-gray-200">Prodigi</span>
            </label>
          </div>
        </div>
        {/* デフォルトのアスペクト比 */}
        <div>
          <label className="text-sm text-gray-400 block mb-1">
            {isVideoEngineOrHeygen ? 'アスペクト比（動画: 16:9 / 9:16 の二択）' : 'デフォルトのアスペクト比（オプション）'}
          </label>
          <select
            value={isVideoEngineOrHeygen ? (defaultAspectRatio === '9:16' ? '9:16' : '16:9') : (defaultAspectRatio || '')}
            onChange={(e) => {
              const newValue = e.target.value || null;
              setDefaultAspectRatio(newValue);
              setCurrentItem(prev => ({
                ...prev,
                defaultAspectRatio: newValue,
                default_aspect_ratio: newValue
              }));
            }}
            className="w-full bg-gray-700 p-2 rounded text-gray-200"
          >
            {isVideoEngineOrHeygen ? (
              <>
                <option value="16:9">16:9（横長ワイド）</option>
                <option value="9:16">9:16（縦長ワイド）</option>
              </>
            ) : (
              <>
                <option value="">指定なし（ユーザー選択）</option>
                <option value="1:1">1:1（正方形）</option>
                <option value="16:9">16:9（横長ワイド）</option>
                <option value="9:16">9:16（縦長ワイド）</option>
                <option value="4:3">4:3（横長）</option>
                <option value="3:4">3:4（縦長）</option>
              </>
            )}
          </select>
          <p className="text-xs text-gray-400 mt-1">
            {isVideoEngineOrHeygen ? '動画メニューは16:9か9:16のどちらかを選択します' : '指定すると、このメニューを選択したときにデフォルトで設定されます'}
          </p>
        </div>
        {/* 動画の秒数（動画エンジンのみ） */}
        {isVideoEngine && (
          <div>
            <label className="text-sm text-gray-400 block mb-1">
              動画の秒数（{minVideoDurationSeconds}〜{maxVideoDurationSeconds}秒）
            </label>
            <select
              value={videoDurationSeconds}
              onChange={(e) => {
                const sec = parseInt(e.target.value, 10);
                setVideoDurationSeconds(sec);
                setCurrentItem((prev) => ({
                  ...prev,
                  videoDurationSeconds: sec,
                  video_duration_seconds: sec,
                }));
              }}
              className="w-full bg-gray-700 p-2 rounded text-gray-200"
            >
              {Array.from({ length: maxVideoDurationSeconds + 1 - minVideoDurationSeconds }, (_, i) => i + minVideoDurationSeconds).map((sec) => (
                <option key={sec} value={sec}>
                  {sec}秒
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              生成される動画の長さです（長いほど生成時間・コストが増えます）
            </p>
          </div>
        )}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded text-gray-200 font-medium"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={() => {
              onSave(currentItem);
            }}
            className="bg-indigo-500 hover:bg-indigo-600 px-4 py-2 rounded text-white font-medium"
            disabled={!currentItem.name.trim() || !currentItem.engine.trim()}
          >
            保存
          </button>
        </div>
      </div>
      {showAssistantModal && (
        <GenerationMenuAssistantModal
          isOpen={showAssistantModal}
          onClose={() => setShowAssistantModal(false)}
          categoryId={currentItem.categoryId ?? null}
          categories={categories}
          onApply={(data) => {
            setCurrentItem((prev) => ({
              ...prev,
              description: data.description,
              prompt: data.prompt,
              promptVariables: data.promptVariables
            }));
            setShowAssistantModal(false);
          }}
        />
      )}
    </div>
  );
};
const AdminChargeOptionForm: React.FC<{
  item: AdminChargeOptionItem;
  onSave: (item: AdminChargeOptionItem) => void;
  onCancel: () => void;
}> = ({ item, onSave, onCancel }) => {
  const [currentItem, setCurrentItem] = useState<AdminChargeOptionItem>(item);
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setCurrentItem((prev) => ({ ...prev, [name]: checked }));
    } else {
      setCurrentItem((prev) => ({
        ...prev,
        [name]:
          name === 'priceYen' ||
          name === 'creditsAwarded' ||
          name === 'creditsBonus'
            ? parseInt(value, 10) || 0
            : value,
      }));
    }
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-lg space-y-4 overflow-y-auto max-h-[90vh] custom-scrollbar">
        <h4 className="text-lg font-semibold text-indigo-300">
          {item.id === 0 ? '新規チャージオプション追加' : `編集: ${item.name}`}
        </h4>
        <div>
          <label
            htmlFor="chargeName"
            className="text-sm text-gray-400 block mb-1"
          >
            オプション名
          </label>
          <input
            id="chargeName"
            name="name"
            value={currentItem.name || ''}
            onChange={handleChange}
            className="w-full bg-gray-700 p-2 rounded text-gray-200"
            placeholder="例: 500円お得プラン"
            required
          />
        </div>
        <div>
          <label
            htmlFor="priceYen"
            className="text-sm text-gray-400 block mb-1"
          >
            価格 (円)
          </label>
          <input
            id="priceYen"
            name="priceYen"
            type="number"
            value={currentItem.priceYen}
            onChange={handleChange}
            className="w-full bg-gray-700 p-2 rounded text-gray-200"
          />
        </div>
        <div>
          <label
            htmlFor="creditsAwarded"
            className="text-sm text-gray-400 block mb-1"
          >
            付与クレジット数
          </label>
          <input
            id="creditsAwarded"
            name="creditsAwarded"
            type="number"
            value={currentItem.creditsAwarded}
            onChange={handleChange}
            className="w-full bg-gray-700 p-2 rounded text-gray-200"
          />
        </div>
        <div>
          <label
            htmlFor="creditsBonus"
            className="text-sm text-gray-400 block mb-1"
          >
            ボーナスクレジット (表示用、任意)
          </label>
          <input
            id="creditsBonus"
            name="creditsBonus"
            type="number"
            value={currentItem.creditsBonus || 0}
            onChange={handleChange}
            className="w-full bg-gray-700 p-2 rounded text-gray-200"
          />
        </div>
        <div>
          <label
            htmlFor="displayInfo"
            className="text-sm text-gray-400 block mb-1"
          >
            表示情報テキスト
          </label>
          <input
            id="displayInfo"
            name="displayInfo"
            value={currentItem.displayInfo || ''}
            onChange={handleChange}
            className="w-full bg-gray-700 p-2 rounded text-gray-200"
            placeholder="例: 550 クレジット (+50お得)"
            required
          />
        </div>
        <div className="flex items-center mt-2">
          <input
            id="isActive"
            name="isActive"
            type="checkbox"
            checked={currentItem.isActive}
            onChange={handleChange}
            className="h-4 w-4 text-indigo-600 border-gray-600 rounded focus:ring-indigo-500 bg-gray-700"
          />
          <label htmlFor="isActive" className="ml-2 text-sm text-gray-300">
            有効にする
          </label>
        </div>
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded text-gray-200 font-medium"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={() => onSave(currentItem)}
            className="bg-indigo-500 hover:bg-indigo-600 px-4 py-2 rounded text-white font-medium"
            disabled={
              !currentItem.name.trim() || !currentItem.displayInfo.trim()
            }
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
};
const SortableMenuItem: React.FC<{
  menu: AdminGenerationMenuItem;
  category: AdminGenerationMenuCategoryItem | undefined;
  onEdit: () => void;
  onDelete: () => void;
  onCopyApiCode: () => void;
  onToggleHidden: () => void;
  isDragging: boolean;
}> = ({ menu, category, onEdit, onDelete, onCopyApiCode, onToggleHidden, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: menu.id.toString() });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`bg-gray-700 p-4 rounded-md shadow flex justify-between items-start ${
        isDragging ? 'opacity-50' : ''
      }`}
      {...attributes}
      {...listeners}
    >
      <div className="flex items-center space-x-3 flex-1">
        <div className="cursor-move text-gray-400 hover:text-indigo-300">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16"></path>
          </svg>
        </div>
        <div className="flex-1">
          <p className="font-semibold text-indigo-400">
            {menu.name}{' '}
            <span className="text-xs text-gray-400">
              ({menu.engine})
            </span>
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            {(menu.isHidden ?? menu.is_hidden) && (
              <p className="text-xs text-yellow-400 bg-yellow-900/50 px-1.5 py-0.5 rounded-full inline-block my-1">
                非表示
              </p>
            )}
            {category && (
              <p className="text-xs text-cyan-400 bg-cyan-900/50 px-1.5 py-0.5 rounded-full inline-block my-1">
                カテゴリ: {category.name}
              </p>
            )}
            {/* グッズ作成許可バッジ */}
            {(menu.goods_order || menu.goodsOrder || []).includes('suzuri') && (
              <span className="text-xs text-green-400 bg-green-900/50 px-1.5 py-0.5 rounded-full inline-block my-1">
                SUZURI
              </span>
            )}
            {(menu.goods_order || menu.goodsOrder || []).includes('printful') && (
              <span className="text-xs text-blue-400 bg-blue-900/50 px-1.5 py-0.5 rounded-full inline-block my-1">
                Printful
              </span>
            )}
            {(menu.goods_order || menu.goodsOrder || []).includes('artisan') && (
              <span className="text-xs text-orange-400 bg-orange-900/50 px-1.5 py-0.5 rounded-full inline-block my-1">
                Artisan
              </span>
            )}
            {(menu.goods_order || menu.goodsOrder || []).includes('prodigi') && (
              <span className="text-xs text-purple-400 bg-purple-900/50 px-1.5 py-0.5 rounded-full inline-block my-1">
                Prodigi
              </span>
            )}
          </div>
          <p
            className="text-sm text-gray-300 mt-1"
            title={menu.description}
          >
            簡易説明: {menu.description || '未設定'}
          </p>
          <p
            className="text-sm text-gray-300 mt-1 truncate max-w-md"
            title={menu.engine}
          >
            プロンプト: {menu.engine}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            コスト: {menu.credit} クレジット
          </p>
          {(menu.sampleInputImageUrl ||
            menu.sampleResultImageUrl) && (
            <div className="mt-2 flex space-x-2">
              {menu.sampleInputImageUrl && (
                <img
                  src={menu.sampleInputImageUrl}
                  alt="Sample Source"
                  className="w-16 h-10 object-cover rounded border border-gray-600"
                />
              )}
              {menu.sampleResultImageUrl && (
                <img
                  src={menu.sampleResultImageUrl}
                  alt="Sample Generated"
                  className="w-16 h-10 object-cover rounded border border-gray-600"
                />
              )}
            </div>
          )}
        </div>
      </div>
      <div className="space-x-2 flex-shrink-0">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleHidden();
          }}
          className={`p-2 ${(menu.isHidden ?? menu.is_hidden) ? 'text-yellow-400 hover:text-yellow-300' : 'text-gray-400 hover:text-yellow-400'}`}
          aria-label={`Toggle hidden for ${menu.name}`}
          title={(menu.isHidden ?? menu.is_hidden) ? '非表示中（クリックで表示に戻す）' : 'フロントのメニューから非表示にする（直接実行は可能）'}
        >
          {(menu.isHidden ?? menu.is_hidden) ? (
            <EyeSlashIcon className="w-5 h-5" />
          ) : (
            <EyeIcon className="w-5 h-5" />
          )}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onCopyApiCode();
          }}
          className="p-2 text-gray-400 hover:text-green-400"
          aria-label={`Copy API code for ${menu.name}`}
          title="APIサンプルコードをコピー"
        >
          <CodeBracketIcon className="w-5 h-5" />
        </button>
        <button
          onClick={onEdit}
          className="p-2 text-gray-400 hover:text-indigo-300"
          aria-label={`Edit ${menu.name}`}
        >
          <PencilAltIcon className="w-5 h-5" />
        </button>
        <button
          onClick={onDelete}
          className="p-2 text-gray-400 hover:text-red-400"
          aria-label={`Delete ${menu.name}`}
        >
          <TrashIcon className="w-5 h-5" />
        </button>
      </div>
    </li>
  );
};
// ── Prodigiタブ（Printfulと同レイヤー: 全商品を登録→公開/価格を管理） ──
interface ProdigiGood {
  id: number; item_name: string; display_name: string; category: string; product_type: string;
  variant_count: number; is_public: boolean; profit_margin: number; shipping_surcharge: number;
  image_url?: string; cat_image?: string; subcategory?: string; subcategory_image?: string;
}
interface ProdigiVariant { sku: string; attrs: { [k: string]: string }; price: string; currency: string; desc?: string; }
interface ProdigiLive {
  description?: string; dimensions?: { width: number; height: number; units: string } | null;
  axes?: { [axis: string]: string[] } | null; image_url?: string;
  cost?: { item?: { amount: string; currency: string } | null; shipping?: { amount: string; currency: string } | null } | null;
  variant_count?: number; rep_sku?: string; variants?: ProdigiVariant[];
  fx?: { [cur: string]: number }; fx_date?: string;
}
// CSVの細かいカテゴリ → Prodigiダッシュボードの大分類 + カテゴリ画像slug(固有)
const PRODIGI_CAT: { [k: string]: { name: string; img: string } } = {
  'Wall art': { name: 'Wall art', img: 'wall-art' },
  'Gallery Boards': { name: 'Wall art', img: 'wall-art' },
  'Wood': { name: 'Wall art', img: 'wall-art' },
  'Prints & posters': { name: 'Prints & posters', img: 'prints-and-posters' },
  'Stickers': { name: 'Stickers', img: 'stickers' },
  'Tattoos': { name: 'Stickers', img: 'stickers' },
  'Technology': { name: 'Technology', img: 'technology' },
  'Device Cases': { name: 'Technology', img: 'technology' },
  'Books & Magazines': { name: 'Books & magazines', img: 'books-and-magazines' },
  'Home & Living': { name: 'Home & living', img: 'home-and-living' },
  'Drinkware': { name: 'Home & living', img: 'home-and-living' },
  'Magnets': { name: 'Home & living', img: 'home-and-living' },
  'Pets': { name: 'Home & living', img: 'home-and-living' },
  'Stationery': { name: 'Cards & stationery', img: 'cards-and-stationery' },
  'Packaging & Boxes': { name: 'Cards & stationery', img: 'cards-and-stationery' },
  'Patches': { name: 'Accessories', img: 'accessories' },
  'Accessories': { name: 'Accessories', img: 'accessories' },
  'Sports & games': { name: 'Sport & games', img: 'sport-and-games' },
};
// サブカテゴリ → 親(大分類)。大分類はサブカテゴリで決まる(CSVカテゴリより優先)
const SUBCAT_PARENT: { [s: string]: string } = {
  'Drinkware': 'Home & living', 'Kitchen & dining': 'Home & living', 'Cushions & pillows': 'Home & living',
  'Bathroom': 'Home & living', 'Bedding': 'Home & living', 'Rugs & blankets': 'Home & living',
  'Home decor': 'Home & living', 'Tabletops': 'Home & living', 'Pets': 'Home & living',
  'Framed prints': 'Wall art', 'Canvas': 'Wall art', 'Acrylic prints': 'Wall art', 'Metal prints': 'Wall art',
  'Display prints': 'Wall art', 'Photo tiles': 'Wall art',
  'Art prints': 'Prints & posters', 'Photo prints': 'Prints & posters', 'Posters': 'Prints & posters', 'Mounted prints': 'Prints & posters',
  'Postcards': 'Cards & stationery', 'Cards': 'Cards & stationery', 'Greeting cards': 'Cards & stationery', 'Wrapping paper': 'Cards & stationery',
  'Stickers': 'Stickers', 'Tattoos': 'Stickers', 'Magazines': 'Books & magazines',
  'Badges': 'Accessories', 'Bags': 'Accessories', 'Keyrings': 'Accessories', 'Patches': 'Accessories',
  'Desk mats': 'Technology', 'Tech accessories': 'Technology', 'Jigsaws': 'Sport & games', 'Packaging': 'Packaging',
};
const prodigiCatName = (c: string) => (PRODIGI_CAT[c]?.name) || c;
const prodigiBroad = (g: { subcategory?: string; category: string }) => (g.subcategory && SUBCAT_PARENT[g.subcategory]) || prodigiCatName(g.category);
const prodigiCatImg = (c: string) => `https://www.prodigi.com/img/products/micro/category-${PRODIGI_CAT[c]?.img || 'accessories'}.webp`;
const prodigiHero = (g: { image_url?: string }) => (g.image_url && g.image_url.includes('/hero/')) ? g.image_url : '';

const ProdigiPanel: React.FC<{ apiBase: string; showToast: (t: 'success' | 'error', m: string) => void }> = ({ apiBase, showToast }) => {
  const [goods, setGoods] = useState<ProdigiGood[]>([]);
  const [loading, setLoading] = useState(true);
  const [publicOnly, setPublicOnly] = useState(false);
  const [sel, setSel] = useState<ProdigiGood | null>(null);
  const [live, setLive] = useState<ProdigiLive | null>(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [margin, setMargin] = useState(0);
  const [surcharge, setSurcharge] = useState(0);
  const [catView, setCatView] = useState<string | null>(null); // 選択カテゴリ(大分類)
  const [subView, setSubView] = useState<string | null>(null); // 選択サブカテゴリ
  const [search, setSearch] = useState('');
  const [vpub, setVpub] = useState<Set<string>>(new Set()); // 公開中variant SKU
  const [vsearch, setVsearch] = useState('');
  const [vfilter, setVfilter] = useState<{ [axis: string]: string }>({}); // 軸ごとの絞り込み選択

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/prodigi/goods/`);
      const data = await res.json();
      if (data.success) setGoods(data.goods);
    } catch { showToast('error', 'Prodigi商品の取得に失敗しました'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (g: ProdigiGood) => {
    setSel(g); setLive(null); setLiveLoading(true); setVsearch(''); setVfilter({});
    setMargin(g.profit_margin || 0); setSurcharge(g.shipping_surcharge || 0);
    try {
      const res = await fetch(`${apiBase}/prodigi/goods/${g.id}/live/`);
      const d = await res.json();
      if (d.success) { setLive(d); setVpub(new Set(d.published_skus || [])); }
      else showToast('error', d.error || '詳細取得に失敗しました');
    } catch { showToast('error', '詳細取得に失敗しました'); }
    finally { setLiveLoading(false); }
  };

  const toggleVariant = async (sku: string) => {
    if (!sel) return;
    const willPublish = !vpub.has(sku);
    const next = new Set(vpub); willPublish ? next.add(sku) : next.delete(sku);
    setVpub(next);
    try {
      const res = await fetch(`${apiBase}/prodigi/goods/${sel.id}/variant-visibility/`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sku, is_public: willPublish }),
      });
      const d = await res.json();
      if (d.success) setGoods(gs => gs.map(x => x.id === sel.id ? { ...x, is_public: d.is_public } : x));
      else { setVpub(vpub); showToast('error', '公開切替に失敗しました'); }
    } catch { setVpub(vpub); showToast('error', '公開切替に失敗しました'); }
  };

  // フィルタで絞った全SKUを一括で公開/非公開（表示上限300件を超えても全件に適用）
  const bulkVariant = async (skus: string[], makePublic: boolean) => {
    if (!sel || skus.length === 0) return;
    const prev = vpub;
    const next = new Set(vpub);
    skus.forEach(s => { makePublic ? next.add(s) : next.delete(s); });
    setVpub(next);
    try {
      const res = await fetch(`${apiBase}/prodigi/goods/${sel.id}/variant-visibility/`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skus, is_public: makePublic }),
      });
      const d = await res.json();
      if (d.success) {
        setGoods(gs => gs.map(x => x.id === sel.id ? { ...x, is_public: d.is_public } : x));
        showToast('success', `${skus.length}件を${makePublic ? '公開' : '非公開'}にしました`);
      } else { setVpub(prev); showToast('error', '一括公開切替に失敗しました'); }
    } catch { setVpub(prev); showToast('error', '一括公開切替に失敗しました'); }
  };

  const toggleVisibility = async (g: ProdigiGood) => {
    try {
      const res = await fetch(`${apiBase}/prodigi/goods/${g.id}/visibility/`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_public: !g.is_public }),
      });
      const d = await res.json();
      if (d.success) {
        setGoods(gs => gs.map(x => x.id === g.id ? { ...x, is_public: d.is_public } : x));
        setSel(s => (s && s.id === g.id) ? { ...s, is_public: d.is_public } : s); // モーダルのボタンも即反映
        showToast('success', d.is_public ? '公開にしました' : '非公開にしました');
      } else showToast('error', '公開設定に失敗しました');
    } catch { showToast('error', '公開設定に失敗しました'); }
  };

  const savePrice = async () => {
    if (!sel) return;
    try {
      const res = await fetch(`${apiBase}/prodigi/goods/${sel.id}/price/`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profit_margin: margin, shipping_surcharge: surcharge }),
      });
      const d = await res.json();
      if (d.success) {
        setGoods(gs => gs.map(x => x.id === sel.id ? { ...x, profit_margin: margin, shipping_surcharge: surcharge } : x));
        showToast('success', `${sel.product_type} の価格を保存しました`);
      } else showToast('error', '価格保存に失敗しました');
    } catch { showToast('error', '価格保存に失敗しました'); }
  };

  const publicCount = goods.filter(g => g.is_public).length;
  const q = search.trim().toLowerCase();
  const base = publicOnly ? goods.filter(g => g.is_public) : goods;
  // 大分類でグループ(ダッシュボードと同じ)
  const byCat: { [c: string]: ProdigiGood[] } = {};
  base.forEach(g => { const c = prodigiBroad(g); (byCat[c] = byCat[c] || []).push(g); });
  const cats = Object.keys(byCat).sort();
  // 検索中は商品を横断表示
  const searchHits = q ? goods.filter(g => g.product_type.toLowerCase().includes(q) || g.category.toLowerCase().includes(q)) : [];

  const ProductCard = (g: ProdigiGood) => (
    <button key={g.id} onClick={() => openDetail(g)}
      className={`text-left rounded-lg border overflow-hidden transition-colors ${g.is_public ? 'border-green-600 bg-green-900/10 hover:bg-green-900/20' : 'border-gray-700 bg-gray-800/40 hover:bg-gray-800/70'}`}>
      <div className="h-32 bg-white flex items-center justify-center overflow-hidden">
        {g.image_url ? <img src={g.image_url} alt={g.product_type} className="max-h-full max-w-full object-contain" /> : <span className="text-gray-400 text-xs">画像なし</span>}
      </div>
      <div className="p-2">
        <div className="font-medium text-indigo-300 text-sm truncate">{g.product_type}</div>
        <div className="text-xs text-gray-500">{g.variant_count}バリエーション</div>
        <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${g.is_public ? 'bg-green-600 text-green-50' : 'bg-gray-600 text-gray-200'}`}>{g.is_public ? '公開中' : '非公開'}</span>
      </div>
    </button>
  );

  return (
    <div className="mt-4">
      {/* ヘッダ: 検索 + 公開のみ */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {(catView || subView) && (
            <div className="text-sm text-gray-400 flex items-center gap-1">
              <button onClick={() => { setCatView(null); setSubView(null); }} className="text-gray-300 hover:text-white">カテゴリ一覧</button>
              {catView && <><span>›</span><button onClick={() => setSubView(null)} className="text-gray-300 hover:text-white">{catView}</button></>}
              {subView && <><span>›</span><span className="text-gray-200">{subView}</span></>}
            </div>
          )}
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="商品を検索…"
            className="bg-gray-900 border border-gray-600 rounded-lg px-3 py-1.5 text-sm text-gray-100 w-56" />
        </div>
        <div className="text-sm text-gray-400">
          {goods.length}アイテム / 公開<span className="text-green-400 font-semibold"> {publicCount}</span>
          <label className="ml-3 text-gray-300"><input type="checkbox" className="mr-1" checked={publicOnly} onChange={e => setPublicOnly(e.target.checked)} />公開のみ</label>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-500 py-8 text-center">読み込み中…</div>
      ) : q ? (
        /* 検索結果 */
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">{searchHits.map(ProductCard)}</div>
      ) : subView && catView ? (
        /* 第3階層: サブカテゴリ内の商品カード(個別画像) */
        <div>
          <div className="text-lg font-semibold text-gray-200 mb-3">{subView}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {(byCat[catView] || []).filter(g => (g.subcategory || g.product_type) === subView).map(ProductCard)}
          </div>
        </div>
      ) : catView ? (
        /* 第2階層: サブカテゴリ カード(画像=サブカテゴリ画像・商品はpill) */
        (() => {
          const subs: { [s: string]: ProdigiGood[] } = {};
          (byCat[catView] || []).forEach(g => { const s = g.subcategory || g.product_type; (subs[s] = subs[s] || []).push(g); });
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.keys(subs).sort().map(sub => {
                const list = subs[sub];
                const pub = list.filter(g => g.is_public).length;
                const img = list.find(g => g.subcategory_image)?.subcategory_image || list[0]?.image_url;
                return (
                  <button key={sub} onClick={() => setSubView(sub)}
                    className="text-left rounded-xl border border-gray-700 bg-gray-800/40 hover:bg-gray-800/70 overflow-hidden transition-colors">
                    <div className="h-36 bg-white flex items-center justify-center overflow-hidden">
                      {img ? <img src={img} alt={sub} className="max-h-full max-w-full object-contain" /> : <span className="text-gray-400 text-xs">画像なし</span>}
                    </div>
                    <div className="p-3">
                      <div className="font-semibold text-gray-100">{sub}</div>
                      <div className="text-xs text-gray-500 mb-2">{list.length}商品{pub > 0 && <span className="text-green-400"> ・公開{pub}</span>}</div>
                      <div className="flex flex-wrap gap-1">
                        {list.slice(0, 4).map(g => <span key={g.id} className="px-2 py-0.5 text-xs rounded bg-gray-700 text-indigo-200">{g.product_type}</span>)}
                        {list.length > 4 && <span className="px-2 py-0.5 text-xs text-gray-400">+{list.length - 4}</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })()
      ) : (
        /* 第1階層: カテゴリ一覧(ダッシュボード風カード) */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cats.map(cat => {
            const list = byCat[cat];
            const pub = list.filter(g => g.is_public).length;
            const catImg = `https://www.prodigi.com/img/products/micro/category-${cat.toLowerCase().replace(/ & /g, '-and-').replace(/\s+/g, '-')}.webp`;
            return (
              <button key={cat} onClick={() => { setCatView(cat); setSubView(null); }}
                className="text-left rounded-xl border border-gray-700 bg-gray-800/40 hover:bg-gray-800/70 overflow-hidden transition-colors">
                <div className="h-36 bg-white flex items-center justify-center overflow-hidden">
                  {catImg ? <img src={catImg} alt={cat} className="max-h-full max-w-full object-contain" /> : null}
                </div>
                <div className="p-3">
                  <div className="font-semibold text-gray-100">{cat}</div>
                  <div className="text-xs text-gray-500 mb-2">{list.length}アイテム{pub > 0 && <span className="text-green-400"> ・公開{pub}</span>}</div>
                  <div className="flex flex-wrap gap-1">
                    {list.slice(0, 3).map(g => <span key={g.id} className="px-2 py-0.5 text-xs rounded bg-gray-700 text-indigo-200">{g.product_type}</span>)}
                    {list.length > 3 && <span className="px-2 py-0.5 text-xs text-gray-400">+{list.length - 3} more…</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* 詳細モーダル(ライブAPI) */}
      {sel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setSel(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-lg font-semibold text-indigo-300">{sel.product_type}</div>
                <div className="text-xs text-gray-500">{sel.category} / {sel.variant_count}バリエーション</div>
              </div>
              <button onClick={() => setSel(null)} className="text-gray-400 hover:text-gray-200 text-xl leading-none">×</button>
            </div>

            {liveLoading ? (
              <div className="text-gray-500 py-8 text-center">Prodigi APIから取得中…</div>
            ) : live ? (
              <div className="space-y-4">
                {(live.image_url || sel.image_url) &&
                  <div className="h-40 bg-white rounded-lg flex items-center justify-center overflow-hidden">
                    <img src={live.image_url || sel.image_url} alt={sel.product_type} className="max-h-full max-w-full object-contain" />
                  </div>}
                {live.description && <div className="text-sm text-gray-300">{live.description}</div>}
                {live.dimensions && <div className="text-xs text-gray-400">寸法: {live.dimensions.width}×{live.dimensions.height} {live.dimensions.units}</div>}

                {/* 原価(quote実額) + 日本円併記 + 通貨の説明 */}
                {(() => {
                  const jpy = (a?: string, cur?: string) => (a && cur && live.fx && live.fx[cur]) ? `（≈¥${Math.round(Number(a) * live.fx![cur]).toLocaleString()}）` : '';
                  return (
                    <div className="text-sm text-gray-300 bg-gray-800/60 rounded p-2">
                      <div>
                        Prodigi原価(製造費・代表SKU):
                        {live.cost?.item ? ` 商品 ${live.cost.item.amount} ${live.cost.item.currency}${jpy(live.cost.item.amount, live.cost.item.currency)}` : ' –'}
                        <span className="text-xs text-gray-500 ml-1">(rep: {live.rep_sku})</span>
                      </div>
                      {live.cost?.shipping && (
                        <div className="mt-1 flex items-center gap-1.5 text-sm">
                          <span>🚚</span>
                          <span className="text-gray-400">日本までの送料原価:</span>
                          <span className="font-bold text-white">{live.cost.shipping.amount} {live.cost.shipping.currency}{jpy(live.cost.shipping.amount, live.cost.shipping.currency)}</span>
                          <span className="text-[11px] text-gray-500">※全バリエーション共通（商品タイプで決まりサイズでは変わりません）</span>
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        ※原価=Prodigiへの支払い額(製造国通貨: GBP英/USD米/AUD豪)。<b className="text-gray-300">¥は実レート換算の参考値</b>{live.fx_date ? `（${live.fx_date}時点）` : ''}。日本での<b className="text-gray-300">売価はJPY</b>で下の「上乗せ価格」で決めます。
                      </div>
                    </div>
                  );
                })()}

                {/* バリエーション表(SKUごとに価格・公開。ダッシュボードと同じ) */}
                {(() => {
                  const axes = live.axes ? Object.keys(live.axes) : [];
                  const vars = live.variants || [];
                  const vq = vsearch.trim().toLowerCase();
                  const filtered = vars.filter(v => {
                    if (vq && !(v.sku.toLowerCase().includes(vq) || (v.desc || '').toLowerCase().includes(vq) || Object.values(v.attrs || {}).some(x => String(x).toLowerCase().includes(vq)))) return false;
                    for (const a of axes) { if (vfilter[a] && v.attrs?.[a] !== vfilter[a]) return false; }
                    return true;
                  });
                  const activeFilters = Object.values(vfilter).filter(Boolean).length;
                  const cap = 300;
                  return (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-semibold text-gray-300">バリエーション（{filtered.length}/{vars.length}件・SKUごとに価格/公開）</div>
                        <input value={vsearch} onChange={e => setVsearch(e.target.value)} placeholder="サイズ/SKU検索…"
                          className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-gray-100 w-40" />
                      </div>
                      {/* 列(軸)ごとのフィルタ */}
                      <div className="flex flex-wrap gap-2 mb-2">
                        {axes.map(a => (
                          <label key={a} className="text-xs text-gray-400">
                            {a}
                            <select value={vfilter[a] || ''} onChange={e => setVfilter(f => ({ ...f, [a]: e.target.value }))}
                              className="ml-1 bg-gray-900 border border-gray-600 rounded px-1.5 py-1 text-gray-200">
                              <option value="">すべて</option>
                              {(live.axes?.[a] || []).map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                          </label>
                        ))}
                        {activeFilters > 0 && <button onClick={() => setVfilter({})} className="text-xs text-indigo-300 underline self-end">フィルタ解除</button>}
                      </div>
                      {/* 一括公開/非公開（絞り込み後の全件に適用） */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-gray-400">{activeFilters > 0 || vq ? `絞り込み${filtered.length}件を` : `全${filtered.length}件を`}一括:</span>
                        <button onClick={() => bulkVariant(filtered.map(v => v.sku), true)}
                          className="px-2 py-0.5 rounded text-white bg-green-600 hover:bg-green-500 text-xs">全部を公開</button>
                        <button onClick={() => bulkVariant(filtered.map(v => v.sku), false)}
                          className="px-2 py-0.5 rounded text-white bg-gray-600 hover:bg-gray-500 text-xs">全部を非公開</button>
                      </div>
                      <div className="max-h-72 overflow-auto border border-gray-700 rounded">
                        <table className="w-full text-xs">
                          <thead className="sticky top-0 bg-gray-800 text-gray-400">
                            <tr>
                              {axes.map(a => <th key={a} className="px-2 py-1 text-left font-medium">{a}</th>)}
                              {axes.length === 0 && <th className="px-2 py-1 text-left font-medium">仕様</th>}
                              <th className="px-2 py-1 text-left">SKU</th>
                              <th className="px-2 py-1 text-right">原価(製造費)</th>
                              <th className="px-2 py-1 text-center">公開</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filtered.slice(0, cap).map(v => {
                              const on = vpub.has(v.sku);
                              return (
                                <tr key={v.sku} className={`border-t border-gray-700/60 ${on ? 'bg-green-900/10' : ''}`}>
                                  {axes.map(a => <td key={a} className="px-2 py-1 text-gray-300 whitespace-nowrap">{v.attrs?.[a] || '–'}</td>)}
                                  {axes.length === 0 && <td className="px-2 py-1 text-gray-300">{v.desc || '–'}</td>}
                                  <td className="px-2 py-1 text-gray-500 whitespace-nowrap">{v.sku}</td>
                                  <td className="px-2 py-1 text-right text-gray-300 whitespace-nowrap">{v.price} {v.currency}
                                    {live.fx && live.fx[v.currency] ? <span className="text-gray-500"> ≈¥{Math.round(Number(v.price) * live.fx[v.currency]).toLocaleString()}</span> : ''}
                                  </td>
                                  <td className="px-2 py-1 text-center">
                                    <button onClick={() => toggleVariant(v.sku)}
                                      className={`px-2 py-0.5 rounded text-white ${on ? 'bg-green-600 hover:bg-green-500' : 'bg-gray-600 hover:bg-gray-500'}`}>
                                      {on ? '公開中' : '非公開'}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {filtered.length > cap && <div className="text-xs text-gray-500 mt-1">先頭{cap}件表示（絞り込み検索を使ってください）</div>}
                      <div className="text-xs text-green-400 mt-1">公開中: {vpub.size}件</div>
                    </div>
                  );
                })()}

                {/* 価格設定 */}
                <div className="bg-gray-800/60 rounded p-3 space-y-2">
                  <div className="text-sm font-semibold text-gray-300">販売設定</div>
                  <div className="flex items-center gap-3 flex-wrap text-sm">
                    <label className="text-gray-400">上乗せ価格 ¥
                      <input type="number" value={margin} onChange={e => setMargin(Number(e.target.value))}
                        className="ml-1 w-24 bg-gray-900 border border-gray-600 rounded px-2 py-1 text-gray-100" />
                    </label>
                    <label className="text-gray-400">上乗せ送料 ¥
                      <input type="number" value={surcharge} onChange={e => setSurcharge(Number(e.target.value))}
                        className="ml-1 w-24 bg-gray-900 border border-gray-600 rounded px-2 py-1 text-gray-100" />
                    </label>
                    <button onClick={savePrice} className="px-3 py-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded">保存</button>
                    <button onClick={() => toggleVisibility(sel)}
                      className={`px-3 py-1 rounded text-white ${sel.is_public ? 'bg-green-600 hover:bg-green-500' : 'bg-gray-600 hover:bg-gray-500'}`}>
                      {sel.is_public ? '公開中→非公開に' : '非公開→公開に'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-red-400 py-6 text-center">詳細を取得できませんでした</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminView;
